import React, { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useApp } from '../context/AppContext';
import { Gauge, RefreshCw, TrendingUp, Info, FileText, Save, Send, Pencil, Zap, AlertTriangle } from 'lucide-react';
import DriverEarningsReport from '../components/DriverEarningsReport';
import DriverAvatar, { fetchDriverPhoto } from '../components/DriverPhoto';
import { buildOfferPdfBytes, offerBytesToBase64 } from '../utils/offerPdf';
import { activationHistory, contractBillingGaps } from '../utils/rateActivations';

// ── Driver Review ─────────────────────────────────────────────────────────────
// Objectively assess a driver + their route to set a per-parcel price the driver
// will accept. Basis: their own captured shift times + parcels (from driver-history),
// parcels-per-hour, and the NSW Transport Industry — Courier & Taxi Truck Contract
// Determination minimum hourly rate.
//
// The per-parcel price = award $/hr ÷ target parcels-per-hour. Set the target at a
// pace that is ACHIEVABLE AND CAN BE BETTERED, so at that pace the driver earns the
// award minimum and any speed gain above it is pure upside for them.
//
// The Determination hourly rates below are the official "Minimum Safety Net Payment
// ($ per hour)" figures effective from the first full pay period on/after 1 July 2025
// — the final phased rate — sourced from the NSW IRC Gazette determination (C9406).
// They remain fully editable on the page (persisted locally) so a later safety-net
// adjustment can be entered without a code change.

// Depot (indoor) standard: one payable hour per 100 parcels. Not aspirational — the
// fleet median indoor pace measured over Jul–Aug 2026 was ~95 parcels/hr across 43
// drivers, so 100 is a good day made normal.
const PT_INDOOR_STD = 100;
const PERIOD_DAYS = 20;          // working days in a 4-week RCTI period
const BONUS_STEP_DEFAULT = 0.30; // default gap between the base and on-target rate

const CFG_KEY = 'hp-driver-review-config-v2';   // bumped when the baked-in rates change
const EX_KEY  = 'hp-driver-review-excludes-v1'; // manually-excluded days, keyed by driver login
const OFFERS_KEY = 'hp-driver-review-offers-v1'; // saved/finalised offers, keyed by driver login
const PT_KEY  = 'hp-driver-review-payable-v1'; // payable-time settings, keyed by driver login
const RATES_EFFECTIVE = '1 July 2025';

// The seven vehicle categories in the Courier & Taxi Truck Contract Determination.
// The rate is an all-in hourly figure meant to cover vehicle + running + labour costs,
// so it sits well above an employee award rate.
const VEHICLE_CLASSES = [
  { key: 'bicycle',   label: 'Bicycle',                    rate: 31.32 },
  { key: 'motorbike', label: 'Motorcycle',                 rate: 34.56 },
  { key: 'mv750',     label: 'Motor vehicle up to 750kg',   rate: 37.80 },
  { key: 'mv1t',      label: 'Motor vehicle over 750kg – 1t', rate: 39.15 },
  { key: 'mv15t',     label: 'Motor vehicle over 1 – 1.5t',  rate: 41.04 },
  { key: 'mv3t',      label: 'Motor vehicle over 1.5 – 3t',  rate: 43.74 },
  { key: 'mv45t',     label: 'Motor vehicle over 3 – 4.5t',  rate: 48.06 },
];

const DEFAULT_CFG = {
  vehicleClass: 'mv750',
  rates: Object.fromEntries(VEHICLE_CLASSES.map(v => [v.key, v.rate])),
  basis: 'median',          // 'median' | 'stretch' | 'expected'
  rampWeeks: 0,             // exclude the first N weeks (developing period) from stats
  workingDays: 250,         // productive delivery days / year
  peakWeeks: 8,             // Nov–Dec Christmas peak
  peakMultiplier: 1.4,      // parcel volume uplift during peak
  hourlyRate: 50,           // hourly-fee contract: the dialled $/hr (see hourlyGstInc)
  hourlyGstInc: true,       // is the dialled hourly rate GST-inclusive (true) or ex-GST (false)?
};

const todayISO = () => new Date().toISOString().slice(0, 10);
const daysAgoISO = (n) => { const d = new Date(); d.setDate(d.getDate() - n); return d.toISOString().slice(0, 10); };

const money = (n) => (isFinite(n) ? n : 0).toLocaleString('en-AU', { style: 'currency', currency: 'AUD' });
const money2 = (n) => (isFinite(n) ? n : 0).toLocaleString('en-AU', { style: 'currency', currency: 'AUD', minimumFractionDigits: 3, maximumFractionDigits: 3 });
const num = (n, d = 1) => (isFinite(n) ? n : 0).toLocaleString('en-AU', { minimumFractionDigits: d, maximumFractionDigits: d });
// Decimal hours → "Xh Ym" (or "Ym" under an hour), signed.
const fmtDur = (h) => {
  if (!isFinite(h)) h = 0;
  const sign = h < -0.0001 ? '−' : '';
  const mins = Math.round(Math.abs(h) * 60);
  const hh = Math.floor(mins / 60), mm = mins % 60;
  if (hh && mm) return `${sign}${hh}h ${mm}m`;
  if (hh) return `${sign}${hh}h`;
  return `${sign}${mm}m`;
};

// A stored UTC timestamp → "HH:mm" in Sydney (shift times are all local wall-clock).
const sydneyHHMM = (iso) => {
  if (!iso) return '';
  const d = new Date(iso);
  if (isNaN(d)) return '';
  return d.toLocaleTimeString('en-GB', { timeZone: 'Australia/Sydney', hour: '2-digit', minute: '2-digit', hour12: false });
};

// minutes past midnight → "HH:mm"
const fromMin = (m) => {
  if (!isFinite(m)) return '—';
  const t = Math.round(m), hh = Math.floor(((t % 1440) + 1440) % 1440 / 60), mm = Math.round(((t % 1440) + 1440) % 1440) % 60;
  return `${String(hh).padStart(2, '0')}:${String(mm).padStart(2, '0')}`;
};

// "HH:mm" → minutes past midnight
const toMin = (t) => {
  const m = /^(\d{1,2}):(\d{2})/.exec((t || '').trim());
  return m ? (+m[1]) * 60 + (+m[2]) : null;
};

// Worked hours for a shift: prefer the stored totalHours; else derive from
// start/finish minus break (handles finish-after-midnight by wrapping).
function workedHours(s) {
  if (s.totalHours > 0) return s.totalHours;
  const st = toMin(s.shiftStart), fi = toMin(s.finishTime);
  if (st == null || fi == null) return 0;
  let span = fi - st; if (span < 0) span += 24 * 60;
  const bs = toMin(s.breakStart), be = toMin(s.breakEnd);
  if (bs != null && be != null && be > bs) span -= (be - bs);
  return Math.max(0, span / 60);
}

const median = (arr) => {
  if (!arr.length) return 0;
  const a = [...arr].sort((x, y) => x - y);
  const m = Math.floor(a.length / 2);
  return a.length % 2 ? a[m] : (a[m - 1] + a[m]) / 2;
};
const percentile = (arr, p) => {
  if (!arr.length) return 0;
  const a = [...arr].sort((x, y) => x - y);
  const idx = Math.min(a.length - 1, Math.max(0, Math.round((p / 100) * (a.length - 1))));
  return a[idx];
};
const mean = (arr) => (arr.length ? arr.reduce((s, x) => s + x, 0) / arr.length : 0);

// ── The "delivering faster earns you more" pace table ────────────────────────
// One builder, shared by the printable HTML and the PDF, because a driver-facing
// document that disagrees with itself is worse than no document.
//
// It answers the question the driver actually asks: at what pace do I reach the
// guaranteed minimum, and what is every parcel per hour above that worth? So it runs
// from BELOW the break-even, not from the expected pace — an offer you can only see
// the upside of isn't an offer, it's a pitch.
//
// Every table is a cross-section of the same surface. A day is two legs, and the hourly
// rate the driver actually experiences is his earnings over the hours he actually works:
//
//     $/hr  =  rate  /  ( 1/depotPace + 1/roadPace )
//
// Hold the depot at the allowance and vary the road, and you get the DELIVERY table.
// Hold the road and vary the depot, and you get the SORTING table. Collapse both into
// one whole-day figure and you get the COMBINED table, where $/hr is simply rate x pace
// and the break-even is the obvious award / rate. All three agree where they meet —
// which is the point of printing all three: they are three ways to read one deal, and
// the driver gets to pick the lever he believes in.
//
// `mode`: 'road' | 'depot' | 'combined' | 'shift' ('shift' = the legacy whole-shift
// table used when the offer is not priced on payable time).
function buildPaceTable({ mode = 'shift', pRate, awardHr, pph, paceModel, depotPace, roadPace, nowOverride, step = 1 }) {
  const hrAt =
    mode === 'road'  ? (p) => (p > 0 ? pRate / (1 / depotPace + 1 / p) : 0) :
    mode === 'depot' ? (p) => (p > 0 ? pRate / (1 / p + 1 / roadPace) : 0) :
                       (p) => pRate * p;

  // Pace at which the fee first covers the floor. On a single leg the OTHER leg has
  // already taken its share out of every parcel's fee, so a rate at or under that
  // share can never reach the floor however fast he goes on this one.
  const perHr = awardHr > 0 ? pRate / awardHr : 0;
  let breakEven = 0;
  if (mode === 'road' || mode === 'depot') {
    const inv = perHr - 1 / (mode === 'road' ? depotPace : roadPace);
    breakEven = inv > 0.0000001 ? 1 / inv : 0;
  } else {
    breakEven = pRate > 0 ? awardHr / pRate : 0;
  }

  // His own pace on this axis, UNROUNDED. Rounding it onto the nearest integer row was
  // quietly wrong: a 16.5 expected pace landed on the "17" row and quoted 17's $/hr, so
  // the table contradicted the headline figure elsewhere in the same document.
  const nowPace = nowOverride != null ? nowOverride
    : (mode === 'shift' && paceModel?.hasRange ? paceModel.target : pph);

  const anchor = breakEven > 0 ? breakEven : (nowPace || 1);
  // Centre the ladder on the break-even — three steps under, three over — so the
  // topped-up zone and the upside are both on the page. When an expected range is
  // driving the price, start at the bottom of that range instead.
  const lowEnd = mode === 'shift' && paceModel?.hasRange ? Math.min(paceModel.low, anchor) : anchor - 3 * step;
  const startP = Math.max(step, Math.floor(lowEnd / step) * step);
  const topP = startP + 6 * step;

  const paceRows = [];
  for (let p = startP; p <= topP + 0.0001; p += step) {
    paceRows.push({ p, hr: hrAt(p), below: hrAt(p) < awardHr - 0.005 });
  }
  // Slot the exact break-even and his own pace in as their own labelled rows, so "the
  // minimum" and "where you are" are lines on the page rather than something he has to
  // interpolate between two rows. Skip either when an integer row already says it.
  // Dedupe tolerance for the inserted rows. It has to stay tight: at step/2 a break-even
  // of 17.8 was swallowed by the 18 row and vanished off the table — losing the one line
  // the whole thing is built around.
  const near = Math.max(0.05, step * 0.02);
  const insert = (p, row) => {
    if (!(p > 0)) return;
    if (paceRows.some(r => Math.abs(r.p - p) < near)) return;
    const at = paceRows.findIndex(r => r.p > p);
    if (at >= 0) paceRows.splice(at, 0, { p, ...row }); else paceRows.push({ p, ...row });
  };
  insert(breakEven, { hr: awardHr, isMin: true });
  insert(nowPace, { hr: hrAt(nowPace), now: true, below: hrAt(nowPace) < awardHr - 0.005 });
  // If either landed on a row that already exists, tag THAT row rather than drop the
  // label. Losing the target row entirely is the worst outcome — and it is the likely
  // one, because a well-set rate puts the target on a round number (the depot table's
  // target is the allowance itself, i.e. exactly 100) which is precisely where an
  // integer row already sits.
  if (breakEven > 0 && !paceRows.some(r => r.isMin)) {
    const hit = paceRows.find(r => Math.abs(r.p - breakEven) < near);
    if (hit) { hit.isMin = true; hit.below = false; }
  }
  if (!paceRows.some(r => r.now)) {
    const hit = paceRows.find(r => Math.abs(r.p - nowPace) < near);
    if (hit) hit.now = true;
  }
  return { mode, paceRows, breakEven, awardHr, nowPace, depotPace, roadPace, step };
}

// The three cross-sections, when the offer is priced on payable time. Each is captioned
// with what it HOLDS FIXED, because a pace table without that is unreadable.
function buildPaceTables({ pRate, awardHr, pph, paceModel, pt, measured }) {
  if (!pt) {
    return [{ ...buildPaceTable({ mode: 'shift', pRate, awardHr, pph, paceModel }),
      title: 'Delivering faster earns you more', unit: 'Parcels / hr' }];
  }
  // Each single-leg table holds the OTHER leg at its PRICED value — the depot allowance
  // and the road pace the rate was actually built on — not at what he happens to run
  // today. Holding the other leg at a measured shortfall makes this leg absorb that
  // shortfall too, and the target goes through the roof: at a rate priced on 17.8 but
  // held at his measured 16.9, the depot target came out at 144 parcels an hour. True,
  // and useless. Priced-against-priced keeps every target a real contract parameter —
  // table 2's target lands back on the allowance itself. His own pace still appears, as
  // its own row, so the gap is visible without becoming the benchmark.
  const roadPriced = pt.outdoorPace;
  const depotPriced = pt.indoorPace;
  const roadNow = measured?.outdoorPace || roadPriced;
  const depotNow = measured?.indoorPace || depotPriced;
  const combinedNow = 1 / (1 / depotNow + 1 / roadNow);
  return [
    { ...buildPaceTable({ mode: 'road', pRate, awardHr, pph, depotPace: depotPriced, nowOverride: roadNow }),
      title: '1 · On the road — delivering',
      unit: 'On-road parcels / hr',
      holds: `with your depot cleared at the ${depotPriced.toFixed(0)} parcels an hour you are paid for` },
    { ...buildPaceTable({ mode: 'depot', pRate, awardHr, pph, roadPace: roadPriced, nowOverride: depotNow, step: 10 }),
      title: '2 · In the depot — sorting & loading',
      unit: 'Depot parcels / hr',
      holds: `with your round run at ${roadPriced.toFixed(1)} parcels an hour on the road` },
    { ...buildPaceTable({ mode: 'combined', pRate, awardHr, pph, nowOverride: combinedNow }),
      title: '3 · Both together — your whole day',
      unit: 'Parcels / hr, whole day',
      holds: `start to finish, depot and road counted together` },
  ];
}

// ── small presentational helpers ──────────────────────────────────────────────
const Card = ({ children, style }) => (
  <div style={{ background: 'var(--hp-surface)', border: '1px solid var(--hp-border)', borderRadius: 12, padding: '20px 24px', marginBottom: 20, ...style }}>{children}</div>
);
const Title = ({ children, sub }) => (
  <div style={{ marginBottom: 16 }}>
    <div style={{ fontSize: '1.05rem', fontWeight: 800, color: 'var(--hp-text)', letterSpacing: '-0.01em' }}>{children}</div>
    {sub && <div style={{ fontSize: '0.8rem', color: 'var(--hp-dim)', marginTop: 3 }}>{sub}</div>}
  </div>
);
const Label = ({ children }) => (
  <label style={{ display: 'block', fontSize: 11, color: 'var(--hp-dim)', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: 6 }}>{children}</label>
);
const inputStyle = { padding: '8px 12px', borderRadius: 8, border: '1px solid var(--hp-border)', background: 'var(--hp-bg)', color: 'var(--hp-text)', fontSize: 14, fontFamily: 'var(--font-body)', fontWeight: 600 };
const stepBtn = { padding: '8px 10px', borderRadius: 8, border: '1px solid var(--hp-border)', background: 'var(--hp-surface)', color: 'var(--hp-text)', fontSize: 13, fontWeight: 700, cursor: 'pointer', fontFamily: 'var(--font-body)', display: 'inline-flex', alignItems: 'center', gap: 5 };
// A labelled range slider that shows its live value — drag it and the price moves.
const Glider = ({ label, value, unit, min, max, step, onChange, note, onReset, resetLabel, dp = 0, width = 300 }) => (
  <div style={{ flex: `1 1 ${width}px`, minWidth: 230 }}>
    <div style={{ display: 'flex', alignItems: 'baseline', justifyContent: 'space-between', gap: 8 }}>
      <Label>{label}</Label>
      {onReset && (
        <button onClick={onReset} title={`Back to ${resetLabel}`}
          style={{ background: 'none', border: 'none', padding: 0, cursor: 'pointer', fontSize: 10.5, fontWeight: 700, color: 'var(--hp-gold)', textTransform: 'uppercase', letterSpacing: '0.05em', fontFamily: 'var(--font-body)' }}>↺ {resetLabel}</button>
      )}
    </div>
    <div style={{ display: 'flex', alignItems: 'baseline', gap: 6, marginBottom: 4 }}>
      <span style={{ fontSize: '1.5rem', fontWeight: 900, color: 'var(--hp-text)', lineHeight: 1 }}>{num(value, dp)}</span>
      <span style={{ fontSize: 11, color: 'var(--hp-dim)', textTransform: 'uppercase', letterSpacing: '0.05em' }}>{unit}</span>
    </div>
    <input type="range" min={min} max={max} step={step}
      value={Math.min(max, Math.max(min, isFinite(value) ? value : min))}
      onChange={e => onChange(parseFloat(e.target.value))}
      style={{ width: '100%', accentColor: 'var(--hp-gold)', cursor: 'pointer' }} />
    {note && <div style={{ fontSize: 11.5, color: 'var(--hp-dim)', marginTop: 2, lineHeight: 1.45 }}>{note}</div>}
  </div>
);
const Stat = ({ label, value, tone, sub }) => (
  <div style={{ flex: '1 1 150px', minWidth: 140, background: 'var(--hp-bg)', border: '1px solid var(--hp-border)', borderRadius: 10, padding: '14px 16px' }}>
    <div style={{ fontSize: 11, color: 'var(--hp-dim)', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.05em' }}>{label}</div>
    <div style={{ fontSize: '1.5rem', fontWeight: 800, color: tone || 'var(--hp-text)', marginTop: 4, lineHeight: 1.1 }}>{value}</div>
    {sub && <div style={{ fontSize: 11, color: 'var(--hp-dim)', marginTop: 3 }}>{sub}</div>}
  </div>
);

export default function DriverReviewPage() {
  const { personnel, currentUser, addNotification, sheetData, setSheetData } = useApp();
  const isAdmin = currentUser?.role === 'admin';

  const drivers = useMemo(
    () => [...(personnel || [])].filter(d => d.loginName).sort((a, b) => a.loginName.localeCompare(b.loginName)),
    [personnel]
  );

  // ?driver= deep link (Onboarding task → "set his prices", global search). Seeded
  // into the initial state rather than applied in an effect, so the default-picker
  // effect below sees a driver already chosen and doesn't overwrite it.
  const [searchParams] = useSearchParams();
  const driverParam = (searchParams.get('driver') || '').toUpperCase();
  const [driver, setDriver] = useState(driverParam);
  const [photo, setPhoto] = useState('');
  useEffect(() => { if (driver) fetchDriverPhoto(driver).then(setPhoto); else setPhoto(''); }, [driver]);
  const [periodStart, setPeriodStart] = useState(daysAgoISO(56));
  const [periodEnd, setPeriodEnd] = useState(todayISO());
  const [history, setHistory] = useState(null); // { shifts, parcelsByDate }
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const [cfg, setCfg] = useState(() => {
    try {
      const saved = JSON.parse(localStorage.getItem(CFG_KEY) || '{}');
      return { ...DEFAULT_CFG, ...saved, rates: { ...DEFAULT_CFG.rates, ...(saved.rates || {}) } };
    } catch { return DEFAULT_CFG; }
  });
  const setCfgKey = (k, v) => setCfg(c => ({ ...c, [k]: v }));
  useEffect(() => { try { localStorage.setItem(CFG_KEY, JSON.stringify(cfg)); } catch {} }, [cfg]);

  // Manually-excluded days (per driver) — dropped from EVERY stat (median/avg/projection),
  // for days a manager judges unrepresentative (e.g. no captured time, scanner left on, a
  // half day). Persists locally, keyed by driver login. Toggle from the daily table.
  const [excludes, setExcludes] = useState(() => { try { return JSON.parse(localStorage.getItem(EX_KEY) || '{}'); } catch { return {}; } });
  useEffect(() => { try { localStorage.setItem(EX_KEY, JSON.stringify(excludes)); } catch {} }, [excludes]);
  const exSet = useMemo(() => new Set(excludes[driver] || []), [excludes, driver]);
  const toggleExclude = (date) => setExcludes(m => {
    const cur = new Set(m[driver] || []);
    cur.has(date) ? cur.delete(date) : cur.add(date);
    return { ...m, [driver]: [...cur] };
  });
  const clearExcludes = () => setExcludes(m => ({ ...m, [driver]: [] }));

  // Per-parcel unit rate the manager dials in. null = sit exactly on the award minimum
  // for the selected pace; a number = an explicit rate above (or below) it. Re-seeds to
  // the award minimum whenever the driver, vehicle class or target pace changes.
  const [unitOverride, setUnitOverride] = useState(null);
  useEffect(() => { setUnitOverride(null); }, [driver, cfg.vehicleClass, cfg.basis]);

  // Improvement workbench: a nominated target parcels-per-hour to model the upside of a
  // faster pace at the fixed unit rate. null = the default (next whole P/H above median).
  const [improveTarget, setImproveTarget] = useState(null);
  useEffect(() => { setImproveTarget(null); }, [driver]);

  // Consistency check: count days at/above vs below a nominated pace. null = default
  // to the driver's median rounded to the nearest 0.5.
  const [threshPPH, setThreshPPH] = useState(null);
  useEffect(() => { setThreshPPH(null); }, [driver]);

  // ── Pace allowance ───────────────────────────────────────────────────────────
  // Two related tools for pricing a NEW driver on a route whose only record belongs to
  // its PREVIOUS driver:
  //  • assessSource — assess on another driver's delivery record (e.g. price VALBEUNAS on
  //    ZUBRIAVID's route history) while the offer itself is still addressed to the driver
  //    selected above. '' = assess on the offer driver's own record.
  //  • an expected-pace RANGE (paceLow…paceHigh) that uplifts a measured pace we don't
  //    trust — e.g. a previous driver who gamed his hours at 16 P/H when the round truly
  //    runs at 18–19. The price point picks which end of that range sets the rate
  //    (rate = award ÷ pace): 'low' is safest for the driver (award-clear across the whole
  //    range), 'high' is leanest (leans on the safety net at the slow end), 'mid' splits it.
  const [assessSource, setAssessSource] = useState('');   // '' = offer driver himself
  const [paceLow, setPaceLow]   = useState('');           // expected P/H — low end ('' = no range)
  const [paceHigh, setPaceHigh] = useState('');           // expected P/H — high end
  const [pricePoint, setPricePoint] = useState('low');    // 'low' | 'mid' | 'high'
  useEffect(() => { setAssessSource(''); setPaceLow(''); setPaceHigh(''); setPricePoint('low'); }, [driver]);

  // ── Payable time model ───────────────────────────────────────────────────────
  // A whole-shift P/H rate silently pays for depot downtime: a driver who clocks on an
  // hour early and stands around drags his own measured pace down, which pushes the
  // per-parcel price UP. This model prices the two legs of the day separately:
  //
  //   depot leg   — DEEMED at a standard pace (parcels ÷ ptIndoor). Clock time in the
  //                 depot is not payable time; the allowance moves with the freight.
  //   on-road leg — ACTUAL, in full, measured from the onboard summary capture (the
  //                 moment the scanner is signed off and photographed) to finish.
  //
  //   rate = (1 + loading) × award $/hr × ( 1/indoorPace + 1/outdoorPace )
  //
  // Volume-invariant by construction: a light day and a heavy day return the same
  // effective $/hr, because payable time moves with the parcels. The Determination
  // floor is untouched — it still measures hours ACTUALLY worked, so the RCTI's GCCD
  // levy remains a genuine backstop (see the safety-net readout in the panel).
  const [ptOn, setPtOn]           = useState(false);      // payable-time model drives the price
  const [ptIndoor, setPtIndoor]   = useState(PT_INDOOR_STD); // depot allowance, parcels/hr
  const [ptOutdoor, setPtOutdoor] = useState(null);       // on-road parcels/hr; null = his measured
  const [ptDifficult, setPtDifficult] = useState(false);  // "difficult route" loading on/off
  const [ptLoad, setPtLoad]       = useState(10);         // that loading, %

  // ── Speed bonus ──────────────────────────────────────────────────────────────
  // A flat per-parcel rate cannot reward speed: the parcel count is set by Australia
  // Post, so working faster shortens the day without adding a cent. The bonus is the
  // only lever that makes "faster = more money" true, and without it the offer asks a
  // driver to go quicker for less.
  //
  // Shape: a TWO-TIER RATE (base, or a higher on-target rate for the whole period)
  // rather than a lump sum — same unit the driver already thinks in, scales with volume
  // without renegotiation, and lands on the RCTI as a rate instead of an ad-hoc line.
  //
  // ⚠ The target is scored on the ONBOARD CAPTURE, never on his own recorded hours.
  // Paying a bonus against self-reported time pays him to under-record it, and those
  // same hours are what the award-floor calculation uses to protect both sides — you
  // would be funding the behaviour that hides an underpayment. Nothing in the target is
  // self-reported: the nominated start is fixed in the contract, the parcels come from
  // AP, and the capture is timestamped server-side when he photographs the summary.
  // Which leg the bonus is scored on. Default ROAD: measured across the fleet, NOT ONE of
  // 43 drivers has a median depot departure before 06:49, and the earliest any of them has
  // ever left is 06:52 — the sort simply does not release freight before then, so a depot
  // target computed from a 05:30 start and a 100/hr standard is outside what the depot
  // produces, for anyone. VALBUENAS is away at 07:16 against a fleet median of 07:27, i.e.
  // in the faster half; his poor depot PACE is his early clock-on, which payable-time
  // pricing already handles. The room is on the road, where he runs 17.1/hr against a
  // fleet median of 22.6 and it is his own driving rather than AP's sort.
  const [bonusBasis, setBonusBasis] = useState('road');   // 'road' | 'depot'
  const [bonusRoadPace, setBonusRoadPace] = useState(null); // target parcels/on-road hour; null = measured + 1
  const [bonusOn, setBonusOn]     = useState(false);
  const [bonusRate, setBonusRate] = useState(null);       // on-target $/parcel; null = base + BONUS_STEP_DEFAULT
  const [bonusHitPct, setBonusHitPct] = useState(80);     // % of the period's days that must hit
  const [bonusStart, setBonusStart] = useState('');       // nominated start 'HH:mm'; '' = from his record
  // Payable-time settings persist PER DRIVER. They used to reset on every driver change and
  // vanish on every reload, and that is exactly how a $2.900 offer printed with one
  // whole-day table and no "How your day is paid" section: the toggle lives at the top of
  // section 2, Print lives at the bottom of section 5, and nothing in between said the
  // pricing model had quietly lapsed back to whole-shift pace.
  const [ptStore, setPtStore] = useState(() => { try { return JSON.parse(localStorage.getItem(PT_KEY) || '{}'); } catch { return {}; } });
  useEffect(() => { try { localStorage.setItem(PT_KEY, JSON.stringify(ptStore)); } catch {} }, [ptStore]);
  const ptStoreRef = useRef(ptStore);
  useEffect(() => { ptStoreRef.current = ptStore; }, [ptStore]);
  useEffect(() => {
    const s = ptStoreRef.current[driver] || {};
    setPtOn(s.ptOn ?? false);
    setPtIndoor(s.ptIndoor ?? PT_INDOOR_STD);
    setPtOutdoor(s.ptOutdoor ?? null);
    setPtDifficult(s.ptDifficult ?? false);
    setPtLoad(s.ptLoad ?? 10);
    setBonusOn(s.bonusOn ?? false);
    setBonusRate(s.bonusRate ?? null);
    setBonusHitPct(s.bonusHitPct ?? 80);
    setBonusStart(s.bonusStart ?? '');
    setBonusBasis(s.bonusBasis ?? 'road');
    setBonusRoadPace(s.bonusRoadPace ?? null);
  }, [driver]);
  // Skip the render on which the driver changed — the restore above has queued its state
  // updates but this closure still holds the PREVIOUS driver's values, and writing those
  // under the new driver's key would copy one driver's model onto another.
  const ptPersistFor = useRef(null);
  useEffect(() => {
    if (!driver) return;
    if (ptPersistFor.current !== driver) { ptPersistFor.current = driver; return; }
    setPtStore(m => ({ ...m, [driver]: { ptOn, ptIndoor, ptOutdoor, ptDifficult, ptLoad, bonusOn, bonusRate, bonusHitPct, bonusStart, bonusBasis, bonusRoadPace } }));
  }, [driver, ptOn, ptIndoor, ptOutdoor, ptDifficult, ptLoad, bonusOn, bonusRate, bonusHitPct, bonusStart, bonusBasis, bonusRoadPace]);
  // Re-seed the dialled unit rate to the award minimum whenever the priced pace changes, so
  // the rate tracks the price point cleanly (unless the manager then dials it themselves).
  useEffect(() => { setUnitOverride(null); }, [paceLow, paceHigh, pricePoint, ptOn, ptIndoor, ptOutdoor, ptDifficult, ptLoad]);
  const assessLogin = assessSource || driver;             // whose record we actually load/analyse
  const assessRec = useMemo(() => drivers.find(d => d.loginName === assessSource) || null, [drivers, assessSource]);

  // Driver-facing Route & Earnings report (printable) for the selected driver.
  const [showReport, setShowReport] = useState(false);
  useEffect(() => { setShowReport(false); }, [driver]);
  const resetWorkbench = () => {
    setUnitOverride(null); setImproveTarget(null); setCfgKey('basis', 'median');
    setPtOn(false); setPtIndoor(PT_INDOOR_STD); setPtOutdoor(null); setPtDifficult(false); setPtLoad(10);
  };

  // ── Offer Workbench state ────────────────────────────────────────────────────
  // Back-solve a per-unit rate from a total contract value: fix one rate (parcel OR
  // EP), the workbench solves the other so the two lines sum to the total. Volumes +
  // hours pre-fill from the driver's per-day averages × the chosen basis, all editable.
  const [offerBasis, setOfferBasis]   = useState('year');    // 'year' | 'period' | 'week'
  const [offerTotal, setOfferTotal]   = useState('');        // contract total ($) — affixed to parcels
  const [offerTotalInc, setOfferTotalInc] = useState(true);  // is the total entered GST-inc?
  const [offerEpRate, setOfferEpRate] = useState('4.00');    // EP fee — GST-INCLUSIVE — paid on top
  const [offerDepotRate, setOfferDepotRate] = useState('4.55'); // Depot-bag fee (ex GST) — on top
  const [offerDoeRate, setOfferDoeRate] = useState('4.55');  // Dept-of-Education fee (ex GST) — on top
  const [offerEpTarget, setOfferEpTarget] = useState(null);  // potential EP/WEEK to model bonus upside; null = suggested (10)
  const [offerOverflowPerDay, setOfferOverflowPerDay] = useState('20'); // Bonus #2: extra overflow parcels in a day
  const [offerParcelsOv, setOfferParcelsOv] = useState(null); // volume/hours overrides; null = projected
  const [offerEpOv, setOfferEpOv]     = useState(null);
  const [offerHoursOv, setOfferHoursOv] = useState(null);
  // Current-vs-proposed baseline. A driver being CONVERTED from hourly (e.g. PETRATOSS)
  // shows a proxy per-parcel rate on his RCTI ($1.60 + a "Prevailing Conditions" uplift
  // that reconciles to his true hourly deal), so the live RCTI rate understates him.
  // 'hourly' compares against his real $/hr; 'parcel' uses the live RCTI per-parcel rate.
  const [cmpBasis, setCmpBasis]       = useState('hourly'); // 'hourly' | 'parcel'
  const [cmpHourly, setCmpHourly]     = useState('');       // his current $/hr ('' = seed from his record)
  const [cmpHourlyInc, setCmpHourlyInc] = useState(false);  // is that $/hr GST-inc?
  const cmpSeededFor = useRef(null);                        // don't reseed over a restored offer

  // Saved/finalised offers, keyed by driver. This is the single source that carries a
  // finalised offer through to the PDF and (later) downstream. offerFilledFor gates the
  // annual-earnings prefill so it never clobbers a restored/saved total.
  const offerFilledFor = useRef(null);

  // ── Rate editor — write the driver's live Parcel/Exp service rates to the Rates card.
  // These feed RCTI, Contracts and GCCD; persisted via the setRate Apps Script action and
  // patched into the in-memory ratesMap so every page reflects the change without a reload.
  const [showRateEditor, setShowRateEditor] = useState(false);
  const [rateParcelInput, setRateParcelInput] = useState('');
  const [rateExpInput, setRateExpInput] = useState('');
  const [rateDoeInput, setRateDoeInput] = useState('');
  const [rateDepotInput, setRateDepotInput] = useState('');
  const [savingRates, setSavingRates] = useState(false);

  const [savedOffers, setSavedOffers] = useState(() => { try { return JSON.parse(localStorage.getItem(OFFERS_KEY) || '{}'); } catch { return {}; } });
  useEffect(() => { try { localStorage.setItem(OFFERS_KEY, JSON.stringify(savedOffers)); } catch {} }, [savedOffers]);
  const savedOffersRef = useRef(savedOffers);
  useEffect(() => { savedOffersRef.current = savedOffers; }, [savedOffers]);
  // The full set of inputs that define an offer — snapshot for save + dirty-check.
  // Payable-time settings ride along: they define the TERMS of the offer, not just its
  // price, so a restored offer has to come back with the same depot allowance and pace.
  // The vehicle class sets the award floor the offer is written against ($41.04 vs $43.74
  // prints a different safety net and different break-even paces), so it is part of the
  // offer too. Before this it was read live from the page config at Send, and an offer
  // saved at one class could go out at another without tripping the unsaved check.
  const offerSnapshot = () => ({ vehicleClass: cfg.vehicleClass,
    floorRate: (VEHICLE_CLASSES.find(v => v.key === cfg.vehicleClass) || {}).rate || 0,
    offerBasis, offerTotal, offerTotalInc, offerEpRate, offerDepotRate, offerDoeRate, offerEpTarget, offerOverflowPerDay, offerParcelsOv, offerEpOv, offerHoursOv, cmpBasis, cmpHourly, cmpHourlyInc, ptOn, ptIndoor, ptOutdoor, ptDifficult, ptLoad, bonusOn, bonusRate, bonusHitPct, bonusStart, bonusBasis, bonusRoadPace });

  // Apply a saved offer's figures to the workbench. Shared by the driver-change restore
  // and the server-side restore (an offer sent from another machine).
  const applyOffer = useCallback((s) => {
    // Offers saved before the class was stored leave the page's class alone.
    if (s.vehicleClass) setCfg(c => ({ ...c, vehicleClass: s.vehicleClass }));
    setOfferBasis(s.offerBasis ?? 'year');
    setOfferTotal(s.offerTotal ?? '');
    setOfferTotalInc(s.offerTotalInc ?? true);
    setOfferEpRate(s.offerEpRate ?? '4.00');
    setOfferDepotRate(s.offerDepotRate ?? '4.55');
    setOfferDoeRate(s.offerDoeRate ?? '4.55');
    setOfferEpTarget(s.offerEpTarget ?? null);
    setOfferOverflowPerDay(s.offerOverflowPerDay ?? '20');
    setOfferParcelsOv(s.offerParcelsOv ?? null);
    setOfferEpOv(s.offerEpOv ?? null);
    setOfferHoursOv(s.offerHoursOv ?? null);
    setCmpBasis(s.cmpBasis ?? 'hourly');
    setCmpHourly(s.cmpHourly ?? '');
    setCmpHourlyInc(s.cmpHourlyInc ?? false);
    setPtOn(s.ptOn ?? false);
    setPtIndoor(s.ptIndoor ?? PT_INDOOR_STD);
    setPtOutdoor(s.ptOutdoor ?? null);
    setPtDifficult(s.ptDifficult ?? false);
    setPtLoad(s.ptLoad ?? 10);
    setBonusOn(s.bonusOn ?? false);
    setBonusRate(s.bonusRate ?? null);
    setBonusHitPct(s.bonusHitPct ?? 80);
    setBonusStart(s.bonusStart ?? '');
    setBonusBasis(s.bonusBasis ?? 'road');
    setBonusRoadPace(s.bonusRoadPace ?? null);
  }, []);

  // On driver change: restore that driver's saved offer if one exists; else clear the
  // overrides so the annual-earnings prefill seeds a fresh total.
  useEffect(() => {
    const s = savedOffersRef.current[driver];
    if (s) {
      applyOffer(s);
      offerFilledFor.current = driver;                 // don't prefill over a restored offer
    } else {
      setOfferParcelsOv(null); setOfferEpOv(null); setOfferHoursOv(null);
      setOfferTotal(''); setOfferEpTarget(null);
    }
  }, [driver, applyOffer]);

  const saveOffer = () => {
    if (!driver) return;
    // Merge over the existing record — sentAt/sentDocId are not part of the snapshot,
    // so replacing the entry outright would unlink the offer we already sent and hide
    // the driver's acceptance.
    setSavedOffers(m => ({ ...m, [driver]: { ...(m[driver] || {}), ...offerSnapshot(), savedAt: new Date().toISOString() } }));
    addNotification(`Offer saved for ${driverRec?.name || driver}`, 'success');
  };

  // Default the picker to CHEEMAM2 once drivers load.
  useEffect(() => {
    if (driver || !drivers.length) return;
    const cheema = drivers.find(d => d.loginName.toUpperCase() === 'CHEEMAM2');
    setDriver((cheema || drivers[0]).loginName);
  }, [drivers, driver]);

  const driverRec = useMemo(() => drivers.find(d => d.loginName === driver) || null, [drivers, driver]);

  const load = useCallback(async () => {
    if (!assessLogin) return;
    setLoading(true); setError(null);
    try {
      const url = `/.netlify/functions/driver-history?driver=${encodeURIComponent(assessLogin)}&periodStart=${periodStart}&periodEnd=${periodEnd}`;
      const res = await fetch(url);
      const d = await res.json();
      if (!d.success) throw new Error(d.error || 'Load failed');
      setHistory({ shifts: d.shifts || [], parcelsByDate: d.parcelsByDate || {} });
    } catch (e) {
      setError(e.message); setHistory(null);
      addNotification(`Driver review: ${e.message}`, 'error');
    } finally { setLoading(false); }
  }, [assessLogin, periodStart, periodEnd, addNotification]);

  useEffect(() => { if (assessLogin) load(); }, [assessLogin, load]);

  // ── Onboard captures — the boundary between the depot leg and the on-road leg ──
  // The main (largest) summary capture per day is the moment the scanner was signed off
  // and photographed, i.e. the driver left the depot. Pulled separately from onboard-sb
  // rather than through driver-history so the money path (RCTI/GCCD) is untouched; same
  // "largest capture wins" rule the Indoor Time report uses.
  const [onboardByDate, setOnboardByDate] = useState({});
  useEffect(() => {
    let dead = false;
    if (!assessLogin) { setOnboardByDate({}); return undefined; }
    (async () => {
      try {
        const res = await fetch('/.netlify/functions/onboard-sb', {
          method: 'POST', headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ action: 'onboardRange', from: periodStart, to: periodEnd }),
        });
        const j = await res.json();
        if (dead) return;
        const want = assessLogin.toUpperCase();
        const byDate = {};
        (j.scans || []).forEach(s => {
          if ((s.driver || '').toUpperCase() !== want) return;
          const items = s.items == null ? -1 : s.items;
          const cur = byDate[s.scanDate];
          // Largest capture wins; on a tie the earliest, so a re-scan can't push the
          // boundary later and quietly shrink the on-road leg.
          if (!cur || items > cur.items || (items === cur.items && s.scannedAt < cur.scannedAt)) {
            byDate[s.scanDate] = { items, scannedAt: s.scannedAt, hhmm: sydneyHHMM(s.scannedAt) };
          }
        });
        setOnboardByDate(byDate);
      } catch { if (!dead) setOnboardByDate({}); }
    })();
    return () => { dead = true; };
  }, [assessLogin, periodStart, periodEnd]);

  // ── Build per-day rows + performance stats ───────────────────────────────────
  const analysis = useMemo(() => {
    if (!history) return null;
    const { shifts, parcelsByDate } = history;
    const dates = Array.from(new Set([...shifts.map(s => s.date), ...Object.keys(parcelsByDate)])).sort();
    const shiftBy = Object.fromEntries(shifts.map(s => [s.date, s]));

    const rows = dates.map(date => {
      const s = shiftBy[date] || {};
      const p = parcelsByDate[date] || { parcel: 0, express: 0, doe: 0, depot: 0 };
      const parcels = (p.parcel || 0) + (p.express || 0);
      const hrs = s.date ? workedHours(s) : 0;
      const pph = hrs > 0 ? parcels / hrs : 0;
      return {
        date, shiftStart: s.shiftStart || '', finishTime: s.finishTime || '',
        breakStart: s.breakStart || '', breakEnd: s.breakEnd || '',
        hours: hrs, km: s.totalKm || 0, status: s.status || '', source: s.source || '',
        parcel: p.parcel || 0, express: p.express || 0, doe: p.doe || 0, depot: p.depot || 0,
        parcels, pph, productive: hrs > 0 && parcels > 0, excluded: exSet.has(date),
      };
    });

    // Ramp-up exclusion: drop the first N weeks from the driver's earliest captured
    // productive day (his "developing period"), so the rating reflects settled pace.
    // Manually-excluded days drop out of EVERYTHING first (before ramp + stats).
    const allProdRaw = rows.filter(r => r.productive);       // rows are date-sorted asc
    const allProd = allProdRaw.filter(r => !r.excluded);
    const manualExcludedDays = allProdRaw.length - allProd.length;
    const firstProdDate = allProd.length ? allProd[0].date : null;
    const rw = Math.max(0, parseInt(cfg.rampWeeks, 10) || 0);
    let rampCutoffISO = null, prod = allProd;
    if (rw > 0 && firstProdDate) {
      const c = new Date(firstProdDate); c.setDate(c.getDate() + rw * 7);
      rampCutoffISO = c.toISOString().slice(0, 10);
      prod = allProd.filter(r => r.date >= rampCutoffISO);
    }
    const pphs = prod.map(r => r.pph);
    const totalParcels = prod.reduce((s, r) => s + r.parcels, 0);   // parcel + express (combined P/H basis)
    const totalHours = prod.reduce((s, r) => s + r.hours, 0);
    const totalParcelOnly = prod.reduce((s, r) => s + r.parcel, 0); // parcels only (excludes express)
    const totalExpress = prod.reduce((s, r) => s + r.express, 0);   // express / EP only
    const totalDoe = prod.reduce((s, r) => s + (r.doe || 0), 0);    // Dept of Education
    const totalDepot = prod.reduce((s, r) => s + (r.depot || 0), 0); // depot bags

    return {
      rows, prodRows: prod,
      productiveDays: prod.length,
      scannerDays: prod.filter(r => r.source === 'scanner').length,   // settled days filled from AP scanner (pre-portal)
      allProdDays: allProd.length, excludedRampDays: allProd.length - prod.length,
      manualExcludedDays,
      firstProdDate, rampCutoffISO, rampWeeks: rw,
      totalParcels, totalHours, totalParcelOnly, totalExpress,
      avgPPH: totalHours > 0 ? totalParcels / totalHours : 0,   // volume-weighted
      medianPPH: median(pphs),
      bestPPH: pphs.length ? Math.max(...pphs) : 0,
      worstPPH: pphs.length ? Math.min(...pphs) : 0,
      p75PPH: percentile(pphs, 75),
      avgParcelsPerDay: prod.length ? totalParcels / prod.length : 0,
      avgParcelOnlyPerDay: prod.length ? totalParcelOnly / prod.length : 0,
      avgExpressPerDay: prod.length ? totalExpress / prod.length : 0,
      avgDoePerDay: prod.length ? totalDoe / prod.length : 0,
      avgDepotPerDay: prod.length ? totalDepot / prod.length : 0,
      avgHoursPerDay: prod.length ? totalHours / prod.length : 0,
    };
  }, [history, cfg.rampWeeks, exSet]);

  // Expected-pace range → the pace the rate is actually priced on. When a range is set it
  // OVERRIDES the median/stretch/expected basis: we price on the chosen end of the band,
  // uplifting a measured pace we don't trust (see assessSource/paceLow notes above).
  const paceModel = useMemo(() => {
    if (!analysis) return null;
    const measured = analysis.medianPPH || 0;
    const lo = parseFloat(paceLow), hi = parseFloat(paceHigh);
    const hasRange = isFinite(lo) && lo > 0 && isFinite(hi) && hi >= lo;
    const low = hasRange ? lo : measured;
    const high = hasRange ? hi : measured;
    const mid = (low + high) / 2;
    const target = pricePoint === 'high' ? high : pricePoint === 'mid' ? mid : low;
    const upliftPct = measured > 0 ? ((target - measured) / measured) * 100 : 0;
    return { measured, hasRange, low, high, mid, target, upliftPct };
  }, [analysis, paceLow, paceHigh, pricePoint]);

  // ── Payable time — measured split + the priced rate ──────────────────────────
  // `measured` is what he actually does (evidence for the conversation); the sliders are
  // what he'll be PAID on. Keeping them separate is the whole point of the panel: the gap
  // between the two is the downtime the variation removes.
  const payable = useMemo(() => {
    if (!analysis) return null;
    const award = (VEHICLE_CLASSES.find(v => v.key === cfg.vehicleClass) || {}).rate || 0;

    const days = [];
    analysis.prodRows.forEach(r => {
      const cap = onboardByDate[r.date];
      if (!cap || !cap.hhmm) return;
      const st = toMin(r.shiftStart), on = toMin(cap.hhmm), fi = toMin(r.finishTime);
      if (st == null || on == null || fi == null) return;
      let indoor = (on - st) / 60, outdoor = (fi - on) / 60;
      if (outdoor < 0) outdoor += 24;                 // finished after midnight
      // A capture before clock-on (or a whole shift's worth of "indoor") is a bad
      // timestamp, not a slow driver — drop the day rather than poison the average.
      if (indoor <= 0 || indoor > 6 || outdoor <= 0 || outdoor > 16) return;
      days.push({ date: r.date, parcels: r.parcels, indoor, outdoor, clocked: indoor + outdoor });
    });

    const n = days.length;
    const sum = (k) => days.reduce((s, d) => s + d[k], 0);
    const measured = n ? {
      days: n,
      parcelsPerDay: sum('parcels') / n,
      indoorH: sum('indoor') / n,
      outdoorH: sum('outdoor') / n,
      clockedH: sum('clocked') / n,
      indoorPace: sum('indoor') > 0 ? sum('parcels') / sum('indoor') : 0,
      outdoorPace: sum('outdoor') > 0 ? sum('parcels') / sum('outdoor') : 0,
    } : null;

    // Sliders. The on-road default is his own measured pace — difficulty is priced by
    // being genuinely slow on the road, not by a fudge factor.
    const indoorPace = Math.max(1, parseFloat(ptIndoor) || PT_INDOOR_STD);
    const outdoorPace = ptOutdoor != null ? Math.max(0.5, parseFloat(ptOutdoor) || 0)
      : (measured ? measured.outdoorPace : analysis.medianPPH);
    const loadPct = ptDifficult ? Math.max(0, parseFloat(ptLoad) || 0) : 0;
    const u = loadPct / 100;
    const ok = award > 0 && indoorPace > 0 && outdoorPace > 0;
    if (!ok) return { measured, ok: false };

    const depotLeg = award / indoorPace;              // $ per parcel, depot allowance
    const roadLeg = award / outdoorPace;              // $ per parcel, on-road
    const floorRate = depotLeg + roadLeg;             // award-exact at these paces
    const loading = floorRate * u;
    const rate = floorRate + loading;
    // Expressed as a blended pace so it plugs into the existing award ÷ pace machinery.
    const blendedPace = rate > 0 ? award / rate : 0;

    // Impact, at the parcel volumes he actually runs.
    const parcelsPerDay = measured ? measured.parcelsPerDay : analysis.avgParcelsPerDay;
    const dayValue = rate * parcelsPerDay;
    const payableH = parcelsPerDay / indoorPace + parcelsPerDay / outdoorPace;
    const hourlyAtStandard = payableH > 0 ? dayValue / payableH : 0;
    const hourlyAtActual = measured && measured.clockedH > 0 ? dayValue / measured.clockedH : 0;
    // The Determination floor is measured on hours ACTUALLY worked, so this is the clock
    // at which the GCCD levy starts topping him up — the honest headroom in the offer.
    const levyTripH = award > 0 ? dayValue / award : 0;
    const savedH = measured ? measured.indoorH - parcelsPerDay / indoorPace : 0;

    return {
      measured, ok: true, award, indoorPace, outdoorPace, loadPct, u,
      depotLeg, roadLeg, floorRate, loading, rate, blendedPace,
      parcelsPerDay, dayValue, payableH, hourlyAtStandard, hourlyAtActual, levyTripH, savedH,
      abovePct: award > 0 ? ((hourlyAtStandard / award) - 1) * 100 : 0,
    };
  }, [analysis, cfg.vehicleClass, onboardByDate, ptIndoor, ptOutdoor, ptDifficult, ptLoad]);

  // ── Pricing model ────────────────────────────────────────────────────────────
  const pricing = useMemo(() => {
    if (!analysis || !analysis.productiveDays) return null;
    const awardHourly = (VEHICLE_CLASSES.find(v => v.key === cfg.vehicleClass) || {}).rate || 0;
    const expectedPH = driverRec?.expectedPH || 0;

    const bases = {
      median:   { label: 'Current median pace', pph: analysis.medianPPH,
                  note: 'At his demonstrated median speed he earns exactly the award minimum. Any day faster than this = money above award.' },
      stretch:  { label: 'Stretch — good-day pace (p75)', pph: analysis.p75PPH || analysis.medianPPH,
                  note: 'Target set to a pace he already hits on his better days. Lower price per parcel; he must sustain his good-day speed to reach award minimum.' },
      expected: { label: `Expected P/H (from record${expectedPH ? '' : ' — not set'})`, pph: expectedPH,
                  note: 'Target set to the expected parcels/hour on his driver record.' },
    };
    const priceFor = (pph) => (pph > 0 ? awardHourly / pph : 0);
    Object.values(bases).forEach(b => { b.perParcel = priceFor(b.pph); });

    // A live expected-pace range wins over the basis buttons.
    const rangeBasis = paceModel && paceModel.hasRange
      ? { label: `Expected pace — ${pricePoint} of ${num(paceModel.low)}–${num(paceModel.high)} P/H`, pph: paceModel.target, perParcel: priceFor(paceModel.target),
          note: `Priced on the ${pricePoint === 'high' ? 'top' : pricePoint === 'mid' ? 'midpoint' : 'bottom'} of the expected ${num(paceModel.low)}–${num(paceModel.high)} P/H range (measured ${num(paceModel.measured)}). The IR safety net still guarantees the award minimum if the round runs slower than expected.` }
      : null;
    // The payable-time model wins over everything: it prices the two legs of the day
    // separately instead of on one whole-shift pace.
    const payableBasis = ptOn && payable && payable.ok
      ? { label: `Payable time — ${num(payable.indoorPace, 0)} P/H depot · ${num(payable.outdoorPace)} P/H on-road`,
          pph: payable.blendedPace, perParcel: payable.rate,
          note: `Depot time is deemed at ${num(payable.indoorPace, 0)} parcels/hour (${fmtDur(payable.parcelsPerDay / payable.indoorPace)} on ${num(payable.parcelsPerDay, 0)} parcels); on-road time is paid in full at his ${num(payable.outdoorPace)} P/H${payable.loadPct > 0 ? `, plus a ${num(payable.loadPct)}% difficult-route loading` : ''}. Rate = ${money2(payable.depotLeg)} depot + ${money2(payable.roadLeg)} on-road${payable.loadPct > 0 ? ` + ${money2(payable.loading)} loading` : ''}. The award floor still measures hours ACTUALLY worked — the safety net tops him up above ${num(payable.levyTripH)} clocked hours a day.` }
      : null;
    const chosen = payableBasis || rangeBasis || (bases[cfg.basis] && bases[cfg.basis].pph > 0 ? bases[cfg.basis] : bases.median);
    const awardMinUnit = chosen.perParcel;                // per-parcel that hits award exactly at the target pace
    const perParcel = unitOverride != null ? unitOverride : awardMinUnit; // the dialled-in unit rate
    const uplift = perParcel - awardMinUnit;              // per-parcel above (or below) the award minimum

    // $/hr the driver actually earns at his CURRENT median pace, paid at this unit rate.
    const effectiveHourlyNow = analysis.medianPPH * perParcel;
    const perParcelGst = perParcel * 1.1;                 // GST-inclusive quote price
    const awardDelta = effectiveHourlyNow - awardHourly;  // +/- $/hr vs the award floor at current pace

    return { awardHourly, expectedPH, bases, chosen, awardMinUnit, perParcel, uplift, perParcelGst, effectiveHourlyNow, awardDelta };
  }, [analysis, cfg, driverRec, unitOverride, paceModel, pricePoint, ptOn, payable]);

  // ── Speed bonus — the rate, and how he is scored against the target ──────────
  // Target for a day = nominated start + (that day's parcels / the depot standard). It
  // scales with the freight, so a heavy day is not a trap and a light day is not a gift,
  // and clocking on early neither helps nor hurts — which removes the very habit the
  // whole variation is trying to break.
  const speedBonus = useMemo(() => {
    if (!analysis || !pricing) return null;
    const basis = bonusBasis === 'depot' ? 'depot' : 'road';
    const base = pricing.perParcel;
    const onTarget = bonusRate != null ? bonusRate : +(base + BONUS_STEP_DEFAULT).toFixed(3);
    const perParcel = Math.max(0, onTarget - base);
    const parcelsPerDay = (payable && payable.measured ? payable.measured.parcelsPerDay : 0) || analysis.avgParcelsPerDay || 0;
    const indoorPace = (payable && payable.ok ? payable.indoorPace : PT_INDOOR_STD);
    const wd = Math.max(0, parseFloat(cfg.workingDays) || 0);
    const startStr = bonusStart || driverRec?.indoorStart || '05:30';
    const startMin = toMin(startStr);

    // The road target defaults to one more parcel an hour than he already averages —
    // a stretch he can see from where he stands, not an aspiration.
    const measuredRoad = (payable && payable.measured ? payable.measured.outdoorPace : 0) || 0;
    const roadTarget = bonusRoadPace != null ? bonusRoadPace
      : (measuredRoad > 0 ? Math.round((measuredRoad + 1) * 2) / 2 : 18);

    // Score every captured day in the window against its own target.
    const rows = [];
    (analysis.prodRows || []).forEach(r => {
      const cap = onboardByDate[r.date];
      if (!cap || !cap.hhmm || !(r.parcels > 0)) return;
      const capMin = toMin(cap.hhmm);
      if (capMin == null) return;
      if (basis === 'road') {
        const finMin = toMin(r.finishTime);
        if (finMin == null) return;
        let roadH = (finMin - capMin) / 60;
        if (roadH < 0) roadH += 24;                       // finished after midnight
        if (!(roadH > 0)) return;
        const pace = r.parcels / roadH;
        rows.push({ date: r.date, parcels: r.parcels, roadH, pace,
          by: pace - roadTarget, hit: pace >= roadTarget - 0.005 });
      } else {
        if (startMin == null) return;
        const allowMin = (r.parcels / indoorPace) * 60;
        const targetMin = startMin + allowMin;
        rows.push({ date: r.date, parcels: r.parcels, capMin, targetMin, allowMin,
          byMin: capMin - targetMin, hit: capMin <= targetMin + 0.5 });
      }
    });
    const hits = rows.filter(r => r.hit).length;
    const need = Math.ceil((Math.max(0, Math.min(100, bonusHitPct)) / 100) * rows.length);
    const bestByMin = rows.length ? (basis === 'road' ? Math.max(...rows.map(r => r.by)) : Math.min(...rows.map(r => r.byMin))) : 0;

    return {
      basis, roadTarget, measuredRoad,
      base, onTarget, perParcel, parcelsPerDay, indoorPace, startStr, startMin,
      perPeriod: perParcel * parcelsPerDay * PERIOD_DAYS,
      perYear: perParcel * parcelsPerDay * wd,
      baseYear: base * parcelsPerDay * wd,
      onTargetYear: onTarget * parcelsPerDay * wd,
      typicalTargetMin: startMin == null ? null : startMin + (parcelsPerDay / indoorPace) * 60,
      rows, hits, need, days: rows.length,
      hitRate: rows.length ? (hits / rows.length) * 100 : 0,
      earned: rows.length > 0 && hits >= need && need > 0,
      medianByMin: rows.length ? median(rows.map(r => (basis === 'road' ? r.by : r.byMin))) : 0,
      bestByMin,
    };
  }, [analysis, pricing, payable, onboardByDate, bonusRate, bonusHitPct, bonusStart, bonusBasis, bonusRoadPace, driverRec, cfg.workingDays]);

  // ── Annual projection (with Christmas peak) ──────────────────────────────────
  const projection = useMemo(() => {
    if (!analysis || !pricing || !analysis.avgParcelsPerDay) return null;
    const { avgParcelsPerDay, medianPPH } = analysis;
    const wd = Math.max(0, parseFloat(cfg.workingDays) || 0);
    const peakDays = Math.min(wd, Math.max(0, (parseFloat(cfg.peakWeeks) || 0) * 5));
    const mult = Math.max(1, parseFloat(cfg.peakMultiplier) || 1);
    const normalDays = wd - peakDays;

    const normalParcels = avgParcelsPerDay * normalDays;
    const peakParcels = avgParcelsPerDay * mult * peakDays;
    const annualParcels = normalParcels + peakParcels;

    const annualGross = annualParcels * pricing.perParcel;
    const annualHours = medianPPH > 0 ? annualParcels / medianPPH : 0; // hours at his current pace
    const awardEquivAnnual = annualHours * pricing.awardHourly;

    return { wd, peakDays, normalDays, mult, normalParcels, peakParcels, annualParcels, annualGross, annualHours, awardEquivAnnual };
  }, [analysis, pricing, cfg]);

  // Pre-fill the Offer Workbench total with his projected annual earnings (ex GST) —
  // the section-3 annual income — once per driver, so the pot starts from a real number.
  // Only fills when nothing's been seeded/restored yet (offerFilledFor gate).
  useEffect(() => {
    if (projection && projection.annualGross > 0 && offerFilledFor.current !== driver) {
      setOfferTotal(String(Math.round(projection.annualGross)));
      setOfferTotalInc(false);                 // annual projected earnings is an ex-GST figure
      offerFilledFor.current = driver;
    }
  }, [projection, driver]);

  // When pricing on an expected-pace range, the price point sets the rate — so keep the
  // offer pot in step with it (parcels × award-min rate at the priced pace). Only while a
  // range is live; once it's cleared the manager's own total stands.
  useEffect(() => {
    if (paceModel?.hasRange && projection && projection.annualGross > 0) {
      setOfferTotal(String(Math.round(projection.annualGross)));
      setOfferTotalInc(false);
    }
  }, [paceModel?.hasRange, paceModel?.target, projection?.annualGross]);


  // ── Improvement workbench — benefit of a faster pace at the fixed unit rate ───
  const improvement = useMemo(() => {
    if (!analysis || !pricing || !analysis.medianPPH) return null;
    const cur = analysis.medianPPH;
    const unit = pricing.perParcel;
    const dflt = Math.floor(cur) + 1;                       // next whole P/H above his median
    const tgt = improveTarget != null ? improveTarget : dflt;
    const hoursYr = projection ? projection.annualHours : 0; // hours/yr at his current pace
    const hourlyCur = cur * unit;
    const hourlyTgt = tgt * unit;
    const extraHourly = (tgt - cur) * unit;
    const fasterPct = cur > 0 ? ((tgt - cur) / cur) * 100 : 0;
    const vsAwardTgt = hourlyTgt - pricing.awardHourly;
    const extraAnnual = (tgt - cur) * unit * hoursYr;       // same hours, more parcels delivered

    // The OTHER framing: same daily parcels, faster pace → he finishes sooner = time saved.
    const avgDaily = analysis.avgParcelsPerDay;
    const wd = projection ? projection.wd : (parseFloat(cfg.workingDays) || 0); // delivery days / yr
    const curDayH = cur > 0 ? avgDaily / cur : 0;          // hours to clear a day's parcels now
    const tgtDayH = tgt > 0 ? avgDaily / tgt : 0;          // …at the target pace
    const tsDay = curDayH - tgtDayH;                       // time saved per delivery day
    const saved = { day: tsDay, week: tsDay * (wd / 52), month: tsDay * (wd / 12), fourWk: tsDay * (wd / 13), year: tsDay * wd };

    return { cur, tgt, dflt, unit, hourlyCur, hourlyTgt, extraHourly, fasterPct, vsAwardTgt, extraAnnual, hoursYr, avgDaily, wd, curDayH, tgtDayH, saved };
  }, [analysis, pricing, projection, improveTarget, cfg.workingDays]);

  // ── Current deal — the driver's LIVE per-parcel rate (from the RCTI rate card) ─
  const currentDeal = useMemo(() => {
    if (!analysis || !pricing || !analysis.medianPPH) return null;
    const rm = sheetData?.ratesMap || {};
    const r = rm[driver] || rm[(driver || '').toUpperCase()] || {};
    const rate = parseFloat(r.Parcel);                     // live per-parcel service fee (ex GST)
    if (!isFinite(rate) || rate <= 0) return { hasRate: false };
    const cur = analysis.medianPPH;
    const award = pricing.awardHourly;
    const hourlyNow = cur * rate;                          // $/hr on the current rate at his pace
    const overHr = hourlyNow - award;                     // $/hr above the award floor
    const overPct = award > 0 ? (overHr / award) * 100 : 0;
    const awardMinAtPace = cur > 0 ? award / cur : 0;      // award-minimum per-parcel at his pace
    const overPerParcel = rate - awardMinAtPace;
    const annualNow = projection ? projection.annualParcels * rate : 0;
    const expRate = parseFloat(r.Exp);                     // express rate too, if set
    return { hasRate: true, rate, rateGst: rate * 1.1, expRate: isFinite(expRate) ? expRate : null, cur, award, hourlyNow, overHr, overPct, awardMinAtPace, overPerParcel, annualNow };
  }, [analysis, pricing, projection, sheetData, driver]);

  // The selected driver's raw rate row (works even before analysis loads / when no rate set).
  const liveRates = useMemo(() => {
    const rm = sheetData?.ratesMap || {};
    return rm[driver] || rm[(driver || '').toUpperCase()] || {};
  }, [sheetData, driver]);

  const openRateEditor = useCallback(() => {
    setRateParcelInput(liveRates.Parcel != null ? String(liveRates.Parcel) : '');
    setRateExpInput(liveRates.Exp != null ? String(liveRates.Exp) : '');
    setRateDoeInput(liveRates.DOE != null ? String(liveRates.DOE) : '');
    setRateDepotInput(liveRates['Depot Bags'] != null ? String(liveRates['Depot Bags']) : '');
    setShowRateEditor(true);
  }, [liveRates]);

  // Re-read the rate card from the server and report which of these jobs actually landed.
  // The bulk loader is the same read the page booted from and the same Rates rows the RCTI
  // bills on, so it settles the question rather than guessing. Returns the jobs confirmed
  // present on the server, or null when the driver has no rate card to check against.
  const verifyRates = useCallback(async (jobs) => {
    const res = await fetch('/.netlify/functions/sheets');
    const d = await res.json();
    const rm = d?.ratesMap || {};
    const key = Object.keys(rm).find(k => k.toUpperCase() === (driver || '').toUpperCase());
    if (!key) return null;
    const live = rm[key] || {};
    return jobs.filter(j => Number(live[j.type]) === Number(j.rate));
  }, [driver]);

  // Persist changed rates one type at a time (the setRate action is per key), then patch the
  // in-memory ratesMap so RCTI/Contracts/GCCD reflect the new rate immediately.
  const saveRates = useCallback(async () => {
    const cur = liveRates || {};
    const fields = [
      { key: 'Parcel',      label: 'Parcel',       input: rateParcelInput },
      { key: 'Exp',         label: 'Express Post', input: rateExpInput },
      { key: 'DOE',         label: 'DoE',          input: rateDoeInput },
      { key: 'Depot Bags',  label: 'Depot',        input: rateDepotInput },
    ];
    const jobs = [];
    for (const f of fields) {
      if (f.input.trim() === '') continue;                 // blank = leave unchanged
      const n = Number(f.input);
      if (!(isFinite(n) && n >= 0)) { addNotification(`${f.label} rate must be a positive number`, 'error'); return; }
      if (n !== Number(cur[f.key])) jobs.push({ type: f.key, label: f.label, rate: n });
    }
    if (!jobs.length) { addNotification('No rate changes to save', 'info'); setShowRateEditor(false); return; }

    setSavingRates(true);
    try {
      // The Apps Script write can land and the reply still be lost: the proxy gives up at
      // its own deadline (timedOut), or Netlify kills it outright and answers with an HTML
      // page that will not parse. Either way the rate may well have changed, so this is
      // UNKNOWN, not failed — a false failure on money is worse than a slow save, because
      // the next thing anyone does is re-enter a price that is already set. Only a reply
      // that clearly says "no" is a real failure; anything unsettled goes to the rate card.
      const unsettled = [];
      for (const j of jobs) {
        const res = await fetch('/.netlify/functions/appsscript', {
          method: 'POST', headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ action: 'setRate', login: driver, type: j.type, rate: j.rate }),
        });
        const text = await res.text();
        let d = null;
        try { d = JSON.parse(text); } catch { /* HTML error page — outcome unknown */ }
        if (!d || d.timedOut) { unsettled.push(j); continue; }
        if (!d.success) throw new Error(d.error || `Failed to save ${j.type} rate`);
      }

      let saved = jobs;
      if (unsettled.length) {
        saved = await verifyRates(jobs);
        if (!saved) throw new Error('the server did not answer and the rate card could not be re-read — reload the page and check the current rates before saving again');
      }
      const missed = jobs.filter(j => !saved.includes(j));

      // setRate stores the key upper-cased; patch under the key currentDeal reads.
      setSheetData(prev => {
        if (!prev) return prev;
        const rm = { ...(prev.ratesMap || {}) };
        const key = rm[driver] ? driver : (driver || '').toUpperCase();
        rm[key] = { ...(rm[key] || {}) };
        saved.forEach(j => { rm[key][j.type] = j.rate; });
        return { ...prev, ratesMap: rm };
      });

      const who = driverRec?.name || driver;
      if (missed.length) {
        const ok = saved.length ? `${saved.map(j => j.label).join(', ')} saved` : 'nothing saved';
        addNotification(`Rate save incomplete for ${who}: ${ok}; ${missed.map(j => j.label).join(', ')} did not go through — try again.`, 'error');
        return;                                            // leave the editor open on the rest
      }
      addNotification(`Rates saved for ${who}${unsettled.length ? ' (slow reply — confirmed against the rate card)' : ''}`, 'success');
      setShowRateEditor(false);
    } catch (e) {
      addNotification(`Rate save failed: ${e.message}`, 'error');
    } finally {
      setSavingRates(false);
    }
  }, [rateParcelInput, rateExpInput, rateDoeInput, rateDepotInput, liveRates, driver, driverRec, addNotification, setSheetData, verifyRates]);

  // ── Offer Workbench — total contract value is affixed to PARCELS; EP is a bonus ─
  // The contract pot pays for parcels only (parcelRate = total ÷ parcels). EP earns a
  // separate fixed bonus rate ON TOP — additional earnings, not part of the pot. The
  // award comparison is judged on the parcel contract (the guaranteed part); the EP
  // bonus is shown as extra upside.
  const offerWb = useMemo(() => {
    if (!analysis || !analysis.productiveDays) return null;
    // Basis → number of delivery days the offer covers. Volumes/hours = per-day avg × days.
    const daysFor = { week: 5, period: 20, year: Math.max(0, parseFloat(cfg.workingDays) || 0) || 250 };
    const days = daysFor[offerBasis] ?? 250;
    const wd = daysFor.year;                              // working days / yr
    const annualFactor = days > 0 ? wd / days : 1;        // basis → annual (year=1, 4wk=12.5, week=50)
    const projParcels = Math.round((analysis.avgParcelOnlyPerDay || 0) * days);
    const projEp      = Math.round((analysis.avgExpressPerDay || 0) * days);
    const projHours   = (analysis.avgHoursPerDay || 0) * days;

    const nParcel = offerParcelsOv != null ? offerParcelsOv : projParcels;
    const nEp     = offerEpOv     != null ? offerEpOv     : projEp;
    // When pricing on an expected-pace range, the offer's hours are the parcels cleared at
    // the priced (expected) pace — so the headline $/hr reflects the pace the rate is set
    // on, not the slower measured pace the record shows. Manual override still wins.
    const rangeHours = paceModel?.hasRange && paceModel.target > 0 ? nParcel / paceModel.target : null;
    // Priced on payable time → the offer's hours ARE payable hours (deemed depot +
    // actual road). Otherwise the headline $/hr would be measured against his current
    // clock, which still contains the depot downtime the variation removes — quoting a
    // rate against time the contract no longer pays for.
    const payableHours = ptOn && payable?.ok && payable.indoorPace > 0 && payable.outdoorPace > 0
      ? nParcel * (1 / payable.indoorPace + 1 / payable.outdoorPace) : null;
    const hours = offerHoursOv != null ? offerHoursOv
      : (payableHours != null ? payableHours : (rangeHours != null ? rangeHours : projHours));

    const parcelValueEx = (parseFloat(offerTotal) || 0) / (offerTotalInc ? 1.1 : 1);  // contract pot (parcels), ex GST
    const epRate = (parseFloat(offerEpRate) || 0) / 1.1;    // EP fee is entered GST-INCLUSIVE → ex GST

    let ok = true, msg = '';
    if (!(parseFloat(offerTotal) > 0)) { ok = false; msg = 'Enter the total contract value (affixed to parcels) to solve the parcel rate.'; }
    else if (nParcel <= 0)             { ok = false; msg = 'Parcel volume must be above 0 to solve a per-parcel rate.'; }

    const parcelRate = nParcel > 0 ? parcelValueEx / nParcel : 0;      // solved: total ÷ parcels
    const hourlyRegular = hours > 0 ? parcelValueEx / hours : 0;       // $/hr from the parcel contract ONLY

    // EP is a PURE standalone bonus — never folded into $/hr, above-award or any total.
    // We only ever surface the bonus $ figure (current + potential). Target is per WEEK.
    const epBonusEx = nEp * epRate;                                    // current EP bonus $ (standalone)
    const curEpPerWeek = (analysis.avgExpressPerDay || 0) * 5;
    const epTargetPerWeek = offerEpTarget != null ? offerEpTarget : 10;
    const epTargetBasis = epTargetPerWeek * (days / 5);               // EP over the basis (days ÷ 5 weeks)
    const epTargetBonusEx = epTargetBasis * epRate;                   // potential EP bonus $ (standalone)

    return {
      days, wd, annualFactor, projParcels, projEp, projHours, nParcel, nEp, hours, curEpPerWeek,
      totalEx: parcelValueEx, totalInc: parcelValueEx * 1.1,          // the parcel contract
      solvedParcel: parcelRate, solvedEp: epRate,
      parcelSub: parcelValueEx, epSub: epBonusEx,
      epBonusEx, epBonusInc: epBonusEx * 1.1,
      hourly: hourlyRegular,
      epTargetPerWeek, epTargetBasis, epTargetBonusEx, epTargetBonusInc: epTargetBonusEx * 1.1,
      usingSuggestedTarget: offerEpTarget == null,
      totalCalcEx: parcelValueEx, ok, msg,
      usingProjParcels: offerParcelsOv == null, usingProjEp: offerEpOv == null, usingProjHours: offerHoursOv == null,
    };
  }, [analysis, cfg.workingDays, offerBasis, offerTotal, offerTotalInc, offerEpRate, offerEpTarget, offerParcelsOv, offerEpOv, offerHoursOv, paceModel, ptOn, payable]);

  // Keep the offer pot in step with payable-time pricing, the way the range reseed above
  // does for a pace range — and here it matters more, because the two numbers drift apart
  // in a way that looks like a bug in the DOCUMENT rather than a stale field. The printed
  // rate is ALWAYS the workbench's total ÷ parcels, never the panel's rate directly. The
  // generic prefill seeds that total from the annual projection, which counts peak-uplifted
  // parcels AND express, while the workbench solves over parcels only — so a panel showing
  // $2.895 printed an offer at $3.080. Pin the pot to the rate actually dialled
  // (unitOverride included) times the volume actually being solved over.
  //
  // Must sit AFTER the offerWb memo: the dependency array is evaluated during render, so
  // reading offerWb?.nParcel from an effect declared above it is a temporal-dead-zone
  // crash ("Cannot access 'offerWb' before initialization") — a white screen, not a warning.
  useEffect(() => {
    if (!ptOn || !pricing || !(pricing.perParcel > 0) || !offerWb || !(offerWb.nParcel > 0)) return;
    setOfferTotal(String(Math.round(pricing.perParcel * offerWb.nParcel)));
    setOfferTotalInc(false);
  }, [ptOn, pricing, offerWb]);

  // Above-award figures for the current offer (hourly + annual) — the headline the
  // driver cares about. Annualised from his per-day averages so it's basis-independent.
  const aboveAward = useMemo(() => {
    if (!offerWb || !offerWb.ok || !pricing) return null;
    const wd = offerWb.wd;
    const annHours = offerWb.hours * offerWb.annualFactor;   // annual hours from the workbench (reflects overrides)
    const awardHr = pricing.awardHourly;
    const hr = offerWb.hourly;                       // implied $/hr (ex GST) at his pace
    const aboveHr = hr - awardHr;
    const abovePct = awardHr > 0 ? (aboveHr / awardHr) * 100 : 0;
    const aboveAnnual = aboveHr * annHours;          // $ above the award floor over a year
    const awardAnnual = awardHr * annHours;          // award-minimum annual for the same hours
    const annValueEx = hr * annHours;                // his annual earnings at this offer (ex GST) = parcel contract annualised
    return { awardHr, hr, aboveHr, abovePct, annHours, aboveAnnual, awardAnnual, annValueEx, wd };
  }, [offerWb, pricing]);

  // The "current" side of the comparison. Computed on the SAME parcels/hours as the
  // offer so current vs proposed is apples-to-apples (a higher rate can't produce a
  // lower annual). Hourly = his real $/hr; Parcel = his live RCTI per-parcel rate.
  const currentCompare = useMemo(() => {
    if (!analysis || !pricing || !offerWb) return null;
    const awardHr = pricing.awardHourly;
    const annHours   = offerWb.hours   * offerWb.annualFactor;   // same hours basis as the offer
    const annParcels = offerWb.nParcel * offerWb.annualFactor;   // same parcels as the offer
    const pph = annHours > 0 ? annParcels / annHours : 0;        // parcels per hour in the offer
    if (cmpBasis === 'hourly') {
      const hrEx = (parseFloat(cmpHourly) || 0) / (cmpHourlyInc ? 1.1 : 1);
      if (!(hrEx > 0)) return { has: false, basis: 'hourly' };
      return {
        has: true, basis: 'hourly',
        label: cmpHourlyInc ? `${money(hrEx * 1.1)}/hr inc` : `${money(hrEx)}/hr ex GST`,
        rate: pph > 0 ? hrEx / pph : 0,          // per-parcel equivalent at the offer's pace
        hourly: hrEx, overHr: hrEx - awardHr, annual: hrEx * annHours,
      };
    }
    if (!currentDeal || !currentDeal.hasRate) return { has: false, basis: 'parcel' };
    const curRate = currentDeal.rate;
    const curHourly = curRate * pph;             // current rate paid across the offer's parcels/hours
    return {
      has: true, basis: 'parcel', label: `${money2(curRate)}/parcel`,
      rate: curRate, hourly: curHourly, overHr: curHourly - awardHr, annual: curRate * annParcels,
    };
  }, [analysis, pricing, offerWb, cmpBasis, cmpHourly, cmpHourlyInc, currentDeal]);

  // Structured offer data — the single source both the PDF (offerPdf.js) and, later,
  // the contract build from. Mirrors the printOffer computations (keep them in sync).
  const buildOfferData = () => {
    if (!offerWb || !offerWb.ok || !aboveAward || !driverRec) return null;
    const money0 = (n) => (isFinite(n) ? n : 0).toLocaleString('en-AU', { style: 'currency', currency: 'AUD', maximumFractionDigits: 0 });
    const wd = aboveAward.wd, af = offerWb.annualFactor;
    const annParcel = Math.round(offerWb.nParcel * af), annEp = Math.round(offerWb.nEp * af);
    const pRate = offerWb.solvedParcel, eRate = offerWb.solvedEp;
    const depotRate = parseFloat(offerDepotRate) || 0, doeRate = parseFloat(offerDoeRate) || 0;
    const annDepot = Math.round((analysis.avgDepotPerDay || 0) * wd), annDoe = Math.round((analysis.avgDoePerDay || 0) * wd);
    const vClass = (VEHICLE_CLASSES.find(v => v.key === cfg.vehicleClass) || {}).label || '—';
    const today = new Date().toLocaleDateString('en-AU', { day: 'numeric', month: 'long', year: 'numeric' });
    const pph = offerWb.hours > 0 ? offerWb.nParcel / offerWb.hours : 0;
    const pt = ptOn && payable?.ok ? payable : null;
    const paceTables = buildPaceTables({ pRate, awardHr: aboveAward.awardHr, pph, paceModel, pt,
      measured: payable?.measured });
    const annEpTarget = Math.round(offerWb.epTargetPerWeek * (wd / 5));
    const overflowPerDay = parseFloat(offerOverflowPerDay) || 0;
    const cc = currentCompare;
    let cmp = null;
    if (cc && cc.has) {
      const chg = (cur, prop, fmt) => { const d = prop - cur; return { change: `${d >= 0 ? '+' : '−'}${fmt(Math.abs(d))}`, up: d >= 0 }; };
      const rows = [];
      if (cc.basis === 'parcel') rows.push({ label: 'Parcel rate (ex GST)', cur: money2(cc.rate), prop: money2(pRate), ...chg(cc.rate, pRate, v => money2(v)) });
      rows.push({ label: 'Effective $/hr', cur: money(cc.hourly), prop: money(aboveAward.hr), ...chg(cc.hourly, aboveAward.hr, v => money(v)) });
      rows.push({ label: 'Annual value (ex GST)', cur: money0(cc.annual), prop: money0(aboveAward.annValueEx), ...chg(cc.annual, aboveAward.annValueEx, v => money0(v)) });
      cmp = { has: true, heading: `Current vs proposed${cc.basis === 'hourly' ? ` — current: ${cc.label}` : ''}`, rows,
        note: cc.basis === 'hourly' ? `For reference, his ${cc.label} deal is ≈ ${money2(cc.rate)}/parcel equivalent at his current pace.` : null };
    }
    return {
      name: driverRec.name || driver, login: driver, today, vClass, ratesEffective: RATES_EFFECTIVE,
      sub: `Prepared ${today} · Login ${driver}${driverRec.route ? ' · Route ' + driverRec.route : ''}${vClass !== '—' ? ' · ' + vClass : ''}`,
      contractInc: offerWb.totalInc, contractEx: offerWb.totalEx, parcels: annParcel, parcelRateEx: pRate,
      fees: [
        { label: 'Parcel fee', contract: true, volYr: annParcel, rateEx: pRate, rateInc: pRate * 1.1 },
        { label: 'Express Post (EP) fee', volYr: annEp, rateEx: eRate, rateInc: eRate * 1.1 },
        { label: 'Depot bag fee', volYr: annDepot, rateEx: depotRate, rateInc: depotRate * 1.1 },
        { label: 'DoE fee', volYr: annDoe, rateEx: doeRate, rateInc: doeRate * 1.1 },
      ],
      pph, paceTables, paceOnRoad: !!pt, awardHr: aboveAward.awardHr,
      paceRange: paceModel?.hasRange
        ? { low: paceModel.low, high: paceModel.high, priced: paceModel.target, point: pricePoint, measured: paceModel.measured }
        : null,
      // Payable-time terms — these define how his day is paid, so they belong in the
      // document he signs, not just in the workbench.
      payable: ptOn && payable?.ok
        ? { indoorPace: payable.indoorPace, outdoorPace: payable.outdoorPace, loadPct: payable.loadPct,
            depotLeg: payable.depotLeg, roadLeg: payable.roadLeg, loading: payable.loading, rate: payable.rate,
            payableH: payable.payableH, parcelsPerDay: payable.parcelsPerDay,
            depotAllowanceH: payable.parcelsPerDay / payable.indoorPace, awardHr: payable.award }
        : null,
      speedBonus: bonusOn && speedBonus && speedBonus.perParcel > 0 ? {
        base: speedBonus.base, onTarget: speedBonus.onTarget, perParcel: speedBonus.perParcel,
        basis: speedBonus.basis, roadTarget: speedBonus.roadTarget,
        roundHours: fmtDur(speedBonus.parcelsPerDay / speedBonus.roadTarget),
        indoorPace: speedBonus.indoorPace, startStr: speedBonus.startStr,
        typicalTarget: fromMin(speedBonus.typicalTargetMin), typicalParcels: speedBonus.parcelsPerDay,
        hitPct: bonusHitPct, perPeriod: speedBonus.perPeriod, perYear: speedBonus.perYear,
        periodDays: PERIOD_DAYS,
      } : null,
      // Only offer the EP bonus if his round actually carries express. Projecting
      // "+$1,000/yr at 5 EP/week" onto a round with zero recorded EP is upside he cannot
      // collect, and on an offer whose base is already below his current pay it is the
      // line most likely to cost the whole document its credibility.
      bonus1: annEp > 0
        ? { perWeek: offerWb.epTargetPerWeek, perYr: annEpTarget, bonusYrInc: annEpTarget * eRate * 1.1, epRateInc: eRate * 1.1 }
        : null,
      bonus2: { perDay: overflowPerDay, dayBonusInc: overflowPerDay * pRate * 1.1, parcelFeeInc: pRate * 1.1 },
      cmp,
      award: { pct: aboveAward.abovePct, awardHr: aboveAward.awardHr, hourly: aboveAward.hr },
    };
  };

  // Send the offer PDF to the driver's portal (My Docs) — retains a copy + read receipt.
  const [sendingOffer, setSendingOffer] = useState(false);
  const sendToPortal = async () => {
    const d = buildOfferData();
    if (!d) { addNotification('Complete the offer before sending.', 'error'); return; }
    const saved = savedOffers[driver];
    const dirty = !saved || JSON.stringify(offerSnapshot()) !== JSON.stringify((({ savedAt, sentAt, sentDocId, ...rest }) => rest)(saved));
    if (dirty) { addNotification('Save the offer first (so the sent PDF matches).', 'error'); return; }
    if (!window.confirm(`Send this Contract Offer to ${d.name}'s portal? They'll be able to view and download it, and you'll see when they open it.`)) return;
    setSendingOffer(true);
    try {
      const bytes = await buildOfferPdfBytes(d);
      const res = await fetch('/.netlify/functions/driver-docs-sb', {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        // `offer` = the figures behind the PDF, so the accepted offer can be reopened and
        // turned into a contract from any machine (admin-only; not sent to the driver).
        body: JSON.stringify({ action: 'assignDocument', driverLogin: driver, category: 'Contracts', title: `Contract Offer — ${d.today}`, filename: `Contract-Offer-${driver}.pdf`, contentType: 'application/pdf', requiresResponse: true, offer: offerSnapshot(), fileBase64: offerBytesToBase64(bytes) }),
      });
      const j = await res.json();
      if (!j.success) throw new Error(j.error || 'Send failed');
      setSavedOffers(m => ({ ...m, [driver]: { ...(m[driver] || {}), sentAt: new Date().toISOString(), sentDocId: j.id } }));
      addNotification(`Contract Offer sent to ${d.name}'s portal`, 'success');
      setTimeout(refreshOfferStatus, 400);
    } catch (e) { addNotification(`Send failed: ${e.message}`, 'error'); }
    finally { setSendingOffer(false); }
  };

  // Live status of the sent offer (opened / accepted / rejected) — polled from My Docs.
  // The server is the source of truth: we prefer the doc id recorded at send time, but
  // fall back to the driver's most recent offer on file so an acceptance is never
  // invisible on a machine that didn't send it (or if the local link was lost).
  const [offerStatus, setOfferStatus] = useState(null);
  const refreshOfferStatus = useCallback(async () => {
    if (!driver) { setOfferStatus(null); return; }
    const sd = savedOffersRef.current[driver] && savedOffersRef.current[driver].sentDocId;
    try {
      const res = await fetch('/.netlify/functions/driver-docs-sb', {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ action: 'listAssignments', driverLogin: driver }),
      });
      const j = await res.json();
      const docs = j.documents || [];
      const doc = (sd && docs.find(x => x.id === sd))
        || docs.filter(x => x.requiresResponse && x.category === 'Contracts')
               .sort((a, b) => (b.assignedAt || '').localeCompare(a.assignedAt || ''))[0]
        || null;
      setOfferStatus(doc ? { id: doc.id, status: doc.status, openedAt: doc.openedAt, response: doc.response, respondedAt: doc.respondedAt, responseReason: doc.responseReason } : null);
      if (doc) {
        const local = savedOffersRef.current[driver];
        if (!local || !local.offerTotal) {
          // Nothing (or nothing usable) saved on this machine. If the offer's figures
          // travelled with the doc, rehydrate from them — that's the deal we actually
          // sent, so the contract can be generated here and still match the PDF.
          if (doc.offer && doc.offer.offerTotal) {
            applyOffer(doc.offer);
            offerFilledFor.current = driver;           // don't let the projection prefill clobber it
            setSavedOffers(m => ({ ...m, [driver]: { ...doc.offer, savedAt: doc.assignedAt, sentAt: doc.assignedAt, sentDocId: doc.id } }));
          }
        } else if (local.sentDocId !== doc.id) {
          // Terms are here but the link to the sent doc isn't — re-link it (self-heal).
          setSavedOffers(m => ({ ...m, [driver]: { ...m[driver], sentAt: m[driver].sentAt || doc.assignedAt, sentDocId: doc.id } }));
        }
      }
    } catch { setOfferStatus(null); }
  }, [driver, applyOffer]);
  useEffect(() => { refreshOfferStatus(); }, [refreshOfferStatus]);

  // ── Offers awaiting a contract ───────────────────────────────────────────────
  // Every driver who has ACCEPTED an offer with no live contract raised since. This is
  // the safety net: the acceptance SMS is best-effort (and silently no-ops if its env
  // isn't set), so the engine must be able to tell you this on its own.
  const [awaiting, setAwaiting] = useState([]);
  const [agreements, setAgreements] = useState([]);   // all of them — the billing safety net reads these too
  const loadAwaiting = useCallback(async () => {
    try {
      const post = (fn, body) => fetch(`/.netlify/functions/${fn}`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(body),
      }).then(r => r.json());
      const [docsRes, agsRes] = await Promise.all([
        post('driver-docs-sb', { action: 'listAssignments' }),          // no driverLogin = all
        post('subcontractor-agreement-sb', { action: 'getAgreements' }),
      ]);
      setAgreements(agsRes.agreements || []);
      // Latest accepted offer per driver.
      const latest = new Map();
      (docsRes.documents || [])
        .filter(d => d.requiresResponse && d.category === 'Contracts' && d.response === 'accepted')
        .forEach(d => {
          const k = (d.driverLogin || '').toUpperCase();
          const cur = latest.get(k);
          if (!cur || (d.respondedAt || '') > (cur.respondedAt || '')) latest.set(k, d);
        });
      // A contract counts only if it's live (pending/signed) AND was raised after that
      // acceptance — a withdrawn/declined one, or one predating a fresh offer, doesn't.
      const live = (agsRes.agreements || []).filter(a => a.status === 'pending' || a.status === 'signed');
      const rows = [];
      latest.forEach((d, k) => {
        const covered = live.some(a => (a.driverLogin || '').toUpperCase() === k && (a.createdAt || '') > (d.respondedAt || ''));
        if (!covered) rows.push(d);
      });
      rows.sort((a, b) => (a.respondedAt || '').localeCompare(b.respondedAt || ''));  // longest wait first
      setAwaiting(rows);
    } catch { setAwaiting([]); }
  }, []);
  useEffect(() => { loadAwaiting(); }, [loadAwaiting, offerStatus]);

  // ── Activate to RCTI ─────────────────────────────────────────────────────────
  // Saving a rate only changes what the driver earns from NOW — but the RCTI page
  // rebuilds every period from live data, so it also silently re-prices closed periods
  // that get rebuilt. Activating pins the new set to a period start and snapshots the old
  // one as a baseline, which is what keeps earlier periods reproducible. Gated on the
  // driver having ACCEPTED their contract offer in the portal: the signed contract is what
  // makes the change legitimate, so the engine enforces it rather than trusting memory.
  const [activations, setActivations] = useState({});
  const [activating, setActivating] = useState(false);
  const [activateFromISO, setActivateFromISO] = useState('');
  const [activateFloorInput, setActivateFloorInput] = useState('');

  const loadActivations = useCallback(async () => {
    try {
      const res = await fetch('/.netlify/functions/rate-activations-sb', {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ action: 'getRateActivations' }),
      });
      const j = await res.json();
      if (j.success) setActivations(j.activations || {});
    } catch (_) { /* non-fatal: the panel just shows nothing */ }
  }, []);
  useEffect(() => { loadActivations(); }, [loadActivations]);

  const driverActivations = useMemo(
    () => activations[(driver || '').toUpperCase()] || [],
    [activations, driver]
  );

  // ── Signed contracts billing wrong — the second safety net ───────────────────
  // Every ACTIVE driver whose latest signed agreement disagrees with what the RCTI
  // would bill (activation if any, else the live Rates sheet), plus signed rates that
  // match today but sit unpinned on the sheet. CHEEMAM2 was billed $2.00 against a
  // signed $2.388 for a month because nothing ran this comparison. Recomputes the
  // moment an activation lands, so fixing a driver clears his row immediately.
  const contractGaps = useMemo(() => {
    const activeLogins = new Set(drivers.filter(d => d.status === 'active').map(d => d.loginName.toUpperCase()));
    return contractBillingGaps(agreements, activations, sheetData?.ratesMap, l => activeLogins.has(l));
  }, [agreements, activations, sheetData, drivers]);

  // Selectable period starts on the same 28-day grid every other page uses. Activation is
  // per PERIOD, never mid-period — the engine bills a whole period on one basis.
  const periodChoices = useMemo(() => {
    const anchorEnd = '2026-05-16';
    const add = (iso, n) => {
      const [y, m, dd] = iso.split('-').map(Number);
      const d = new Date(y, m - 1, dd + n);
      return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
    };
    const fmt = (iso) => {
      const [y, m, dd] = iso.split('-').map(Number);
      return new Date(y, m - 1, dd).toLocaleDateString('en-AU', { day: 'numeric', month: 'short', year: 'numeric' });
    };
    // Centre the window on TODAY's period. A fixed window around the anchor went stale:
    // by Oct 2026 the newest choice was 12 Jul – 8 Aug and the current period was missing.
    const [ay, am, ad] = anchorEnd.split('-').map(Number);
    const now = new Date();
    const daysSince = Math.round((new Date(now.getFullYear(), now.getMonth(), now.getDate()) - new Date(ay, am - 1, ad)) / 86400000);
    const cur = Math.ceil(daysSince / 28);   // index of the period whose end is on/after today
    const out = [];
    for (let i = cur - 9; i <= cur + 2; i++) {
      const endISO = add(anchorEnd, i * 28);
      const startISO = add(endISO, -27);
      out.push({ startISO, endISO, label: `${fmt(startISO)} to ${fmt(endISO)}` });
    }
    return out.reverse();
  }, []);

  const currentFloor = useMemo(() => {
    const g = (sheetData?.gccdRates || {})[(driver || '').toUpperCase()] || {};
    return Number(g.minHourlyRate) || 0;
  }, [sheetData, driver]);

  // Seed the "current deal" side of the comparison from what he is ACTUALLY paid, not a
  // hardcoded figure. It defaulted to $50/hr GST-inc — PETRATOSS's deal, carried over from
  // the pilot — which quietly told every other driver's offer that he was on $45.45/hr ex.
  // For VALBUENAS, paid exactly the $43.74 floor, that invented a $1.63/hr pay CUT on the
  // face of the offer. His real rate is the GCCD minimum hourly rate on his record, which
  // is what the Hourly Drivers page bills him at (agreed hours x that rate), and it is an
  // EX-GST figure — the RCTI adds GST on top.
  useEffect(() => {
    if (!driver || cmpSeededFor.current === driver) return;
    if (savedOffersRef.current[driver]) { cmpSeededFor.current = driver; return; }  // restored offer wins
    if (!(currentFloor > 0)) return;                       // wait for the rates to load
    setCmpHourly(String(currentFloor));
    setCmpHourlyInc(false);
    setCmpBasis((driverRec?.type || '').toLowerCase() === 'hourly' ? 'hourly' : 'parcel');
    cmpSeededFor.current = driver;
  }, [driver, currentFloor, driverRec]);

  // Authorisation comes from the SERVER, which checks both signature paths — the
  // subcontractor agreement e-sign AND the Driver Review offer acceptance. Reading only
  // the offer here is what made PETRATOSS look unsigned when he had e-signed his
  // agreement, so never infer it from offerStatus alone.
  const [contractStatus, setContractStatus] = useState(null);
  useEffect(() => {
    let live = true;
    if (!driver) { setContractStatus(null); return undefined; }
    (async () => {
      try {
        const res = await fetch('/.netlify/functions/rate-activations-sb', {
          method: 'POST', headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ action: 'getContractStatus', login: driver }),
        });
        const j = await res.json();
        if (live) setContractStatus(j.success ? j : null);
      } catch (_) { if (live) setContractStatus(null); }
    })();
    return () => { live = false; };
  }, [driver]);

  const offerAccepted = !!(contractStatus && contractStatus.signed);

  const activateToRcti = useCallback(async () => {
    if (!offerAccepted) { addNotification('The driver has not accepted their contract offer yet.', 'error'); return; }
    if (!activateFromISO) { addNotification('Choose the period this takes effect from.', 'error'); return; }
    const floor = Number(activateFloorInput);
    if (!(isFinite(floor) && floor > 0)) { addNotification('Enter the minimum hourly floor (ex GST) that applies from this period.', 'error'); return; }

    // Spread the live map first so non-standard keys survive — RCTIPage turns those into
    // recurring fee line items, and an activation replaces the rate map wholesale, so
    // dropping them here would silently delete those lines from the invoice.
    const nextRates = { ...liveRates };
    const put = (k, v) => {
      if (v.trim() === '') return true;
      const n = Number(v);
      if (!(isFinite(n) && n >= 0)) { addNotification(`${k} rate must be a positive number`, 'error'); return false; }
      nextRates[k] = n; return true;
    };
    if (!put('Parcel', rateParcelInput) || !put('Exp', rateExpInput) || !put('DOE', rateDoeInput) || !put('Depot Bags', rateDepotInput)) return;
    if (!Object.keys(nextRates).length) { addNotification('No rates to activate.', 'error'); return; }

    const chosen = periodChoices.find(p => p.startISO === activateFromISO);
    const first = !driverActivations.some(a => a && a.baseline);
    const msg = `Activate these rates for ${driverRec?.name || driver} from ${chosen ? chosen.label : activateFromISO}?\n\n`
      + `Parcel ${nextRates.Parcel ?? '—'} · Exp ${nextRates.Exp ?? '—'} · DoE ${nextRates.DOE ?? '—'} · Depot ${nextRates['Depot Bags'] ?? '—'} (ex GST)\n`
      + `Minimum hourly floor ${floor} ex GST\n\n`
      + (first ? `His current rates will be snapshotted first, so every period before this one keeps billing exactly as it does today.` : `Earlier periods are already protected by his existing baseline.`);
    if (!window.confirm(msg)) return;

    setActivating(true);
    try {
      // Activate FIRST. The baseline must be the pre-change state, and saveRates below
      // overwrites liveRates — so if that ran first the old rates would be unrecoverable.
      const res = await fetch('/.netlify/functions/rate-activations-sb', {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          action: 'activateRates', login: driver, effectiveFromISO: activateFromISO,
          baseline: { rates: { ...liveRates }, minHourlyRate: currentFloor },
          next: { rates: nextRates, minHourlyRate: floor },
          // The server resolves + records which signed contract authorised this.
          note: `Activated on the signed contract.`,
        }),
      });
      const j = await res.json();
      if (!j.success) throw new Error(j.error || 'Activation failed');
      setActivations(m => ({ ...m, [(driver || '').toUpperCase()]: j.activations || [] }));
      addNotification(`Rates activated for ${driverRec?.name || driver} from ${chosen ? chosen.label : activateFromISO}`, 'success');

      // Now sync the "current" stores so the sheet, the CTTCD page and the uplift
      // workbench agree with what the RCTI will bill. Non-fatal: the activation is what
      // the invoice reads, so a failure here is a consistency warning, not a mispricing.
      try {
        await saveRates();
        const gr = await fetch('/.netlify/functions/gccd-uplift-sb', {
          method: 'POST', headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ action: 'updateGCCDRate', driver, minHourlyRate: floor }),
        });
        const gj = await gr.json();
        if (!gj.success) throw new Error(gj.error || 'floor update failed');
      } catch (e) {
        addNotification(`Activated, but syncing the current rate/floor failed: ${e.message}. The RCTI will bill the activated set; fix the Rates card + CTTCD rate so the other pages agree.`, 'error');
      }
    } catch (e) {
      addNotification(`Activation failed: ${e.message}`, 'error');
    } finally {
      setActivating(false);
    }
  }, [offerAccepted, activateFromISO, activateFloorInput, liveRates, rateParcelInput, rateExpInput, rateDoeInput,
      rateDepotInput, periodChoices, driverActivations, driver, driverRec, currentFloor, offerStatus, addNotification, saveRates]);

  // Generate Contract — hand the accepted offer's rates to the Contracts page, which
  // pre-fills the Subcontractor Agreement (baseRate + Schedule-1 extra rates) so the
  // manager reviews the legal particulars and sends it to the driver to e-sign.
  const generateContract = () => {
    if (!offerWb || !offerWb.ok || !driverRec) { addNotification('Complete the offer first.', 'error'); return; }
    const eRate = offerWb.solvedEp, depotRate = parseFloat(offerDepotRate) || 0, doeRate = parseFloat(offerDoeRate) || 0;
    const extraRates = [
      { label: 'Express Post (EP)', amount: +eRate.toFixed(3) },
      { label: 'Depot Bags', amount: +depotRate.toFixed(3) },
      { label: 'DoE', amount: +doeRate.toFixed(3) },
    ].filter(r => r.amount > 0);
    const prefill = {
      driverLogin: driver, driverName: driverRec.name || '',
      baseRate: +offerWb.solvedParcel.toFixed(3), extraRates,
      deliveryArea: driverRec.route || driverRec.area || '',
      from: 'offer', at: new Date().toISOString(),
    };
    try { localStorage.setItem('hp-contract-prefill', JSON.stringify(prefill)); } catch {}
    addNotification(`Contract particulars ready for ${driverRec.name || driver}. Open the Contracts page — the parcel + EP/Depot/DoE rates are pre-filled; review the legal details and send it for signing.`, 'success');
  };

  // Print a one-page Contract Offer for the driver, leading with how far above the
  // Determination minimum he sits (hourly + annual). Opens a print window (→ PDF).
  const printOffer = () => {
    if (!offerWb || !offerWb.ok || !aboveAward || !driverRec) { addNotification('Complete the offer (total + rates) before printing.', 'error'); return; }
    const name = driverRec.name || driver;
    const wd = aboveAward.wd;
    // Annualise the WORKBENCH's own volumes so the PDF matches the workbench exactly.
    const af = offerWb.annualFactor;
    const annParcel = Math.round(offerWb.nParcel * af);
    const annEp     = Math.round(offerWb.nEp * af);
    const pRate = offerWb.solvedParcel, eRate = offerWb.solvedEp;
    const m  = (n, d = 2) => (isFinite(n) ? n : 0).toLocaleString('en-AU', { style: 'currency', currency: 'AUD', minimumFractionDigits: d, maximumFractionDigits: d });
    const m0 = (n) => (isFinite(n) ? n : 0).toLocaleString('en-AU', { style: 'currency', currency: 'AUD', maximumFractionDigits: 0 });
    const n0 = (n) => Math.round(isFinite(n) ? n : 0).toLocaleString('en-AU');
    const vClass = (VEHICLE_CLASSES.find(v => v.key === cfg.vehicleClass) || {}).label || '';
    const today = new Date().toLocaleDateString('en-AU', { day: 'numeric', month: 'long', year: 'numeric' });
    const annContractInc = offerWb.totalInc;                    // HERO: annual contract fee (parcels), inc GST
    const annContractEx  = offerWb.totalEx;
    // Other per-item fees (paid on top) + their projected annual volumes.
    const depotRate = parseFloat(offerDepotRate) || 0, doeRate = parseFloat(offerDoeRate) || 0;
    const annDepot = Math.round((analysis.avgDepotPerDay || 0) * wd);
    const annDoe   = Math.round((analysis.avgDoePerDay || 0) * wd);
    // Pace → $/hr (per-parcel pay: faster = more per hour). Same builder the PDF uses,
    // so the two documents can't drift apart.
    const pph = offerWb.hours > 0 ? offerWb.nParcel / offerWb.hours : 0;
    const ptNow = ptOn && payable?.ok ? payable : null;
    const awardHr = aboveAward.awardHr;
    const paceOnRoad = !!ptNow;
    const paceTables = buildPaceTables({ pRate, awardHr, pph, paceModel, pt: ptNow, measured: payable?.measured });
    // Bonus #1 — EP potential.
    const annEpTarget = Math.round(offerWb.epTargetPerWeek * (wd / 5));
    const annEpTargetBonusInc = annEpTarget * eRate * 1.1;
    // Bonus #2 — overflow parcels, paid at his parcel fee.
    const overflowPerDay = parseFloat(offerOverflowPerDay) || 0;
    const overflowBonusDayInc = overflowPerDay * pRate * 1.1;   // per-day only — overflow isn't every day
    // Current vs proposed is DELIBERATELY not in the driver-facing document. It is a
    // manager's sanity check (section 5 keeps it), and on the page it invited a comparison
    // the driver should not be led into — his old rate against a new one measured on a
    // different basis. cmpHtml stays as an empty string so the template below is
    // untouched — one place to put it back if it is ever wanted again.
    const cmpHtml = '';
    const html = `<!doctype html><html><head><meta charset="utf-8"><title>Contract Offer — ${name}</title>
<style>
  @page { size: A4 portrait; margin: 16mm; }
  * { box-sizing: border-box; }
  body { font-family: Helvetica, Arial, sans-serif; color:#1f2430; margin:0; font-size:12px; }
  .hdr { background:#1a1a2e; padding:20px 26px; display:flex; justify-content:space-between; align-items:center; }
  .hdr .b { color:#c9a84c; font-weight:800; font-size:22px; letter-spacing:1px; }
  .hdr .s { color:#cfd3dc; font-size:10px; margin-top:3px; }
  .hdr .r { color:#cfd3dc; font-size:10px; text-align:right; }
  .wrap { padding:22px 26px; }
  h1 { font-size:19px; margin:0 0 2px; color:#1a1a2e; }
  .sub { color:#6b7280; font-size:12px; margin-bottom:18px; }
  .hero { background:#f7f3e8; border:1px solid #e4d9b8; border-left:5px solid #c9a84c; border-radius:10px; padding:18px 22px; margin:6px 0 20px; }
  .hero .lab { font-size:11px; font-weight:700; text-transform:uppercase; letter-spacing:.06em; color:#8a7a3c; }
  .hero .big { font-size:28px; font-weight:800; color:#1a1a2e; margin:4px 0; }
  .hero .big span { color:#2e7d32; }
  .hero .line { font-size:13px; color:#3a3f4b; margin-top:6px; }
  .hero .line b { color:#1a1a2e; }
  h2 { font-size:12px; text-transform:uppercase; letter-spacing:.05em; color:#8a7a3c; border-bottom:2px solid #eee; padding-bottom:5px; margin:22px 0 10px; }
  table { border-collapse:collapse; width:100%; }
  th, td { text-align:right; padding:7px 10px; border-bottom:1px solid #ececf1; }
  th:first-child, td:first-child { text-align:left; }
  thead th { background:#1a1a2e; color:#fff; font-size:10px; text-transform:uppercase; letter-spacing:.04em; }
  .grid { display:flex; gap:12px; margin-top:4px; }
  .cell { flex:1; background:#fafafa; border:1px solid #ececf1; border-radius:8px; padding:12px 14px; }
  .cell .l { font-size:10px; text-transform:uppercase; letter-spacing:.05em; color:#9ca3af; font-weight:700; }
  .cell .v { font-size:18px; font-weight:800; color:#1a1a2e; margin-top:3px; }
  .cell .g { color:#2e7d32; }
  .keep { break-inside: avoid; page-break-inside: avoid; }
  .bonus { background:#eef7ee; border:1px solid #cfe6cf; border-left:5px solid #2e7d32; border-radius:8px; padding:12px 16px; margin-top:10px; break-inside: avoid; page-break-inside: avoid; }
  .bonus .t { font-size:12px; font-weight:800; color:#1a1a2e; }
  .bonus .d { font-size:12.5px; color:#3a3f4b; margin-top:3px; }
  tr.now td { background:#f7f3e8; font-weight:800; }
  .foot { color:#9aa0aa; font-size:9px; border-top:1px solid #e5e7eb; padding-top:8px; margin-top:22px; line-height:1.5; }
</style></head><body>
  <div class="hdr"><div><div class="b">HOLLYPAUL</div><div class="s">Delivery Contractor Offer</div></div><div class="r">PH Bouveng / Hollypaul<br>hollypaul.com</div></div>
  <div class="wrap">
    <h1>Contract Offer — ${name}</h1>
    <div class="sub">Prepared ${today} · Login ${driver}${driverRec.route ? ' · Route ' + driverRec.route : ''}${vClass ? ' · ' + vClass : ''}</div>

    <div class="hero">
      <div class="lab">Your annual contract fee</div>
      <div class="big">${m0(annContractInc)} <span style="font-size:15px;color:#6b7280;font-weight:600">GST inc</span></div>
      <div class="line">${m0(annContractEx)} ex GST · paid across <b>${n0(annParcel)}</b> parcels a year (<b>${m(pRate, 3)}</b>/parcel + GST). Express Post, Depot &amp; DoE are paid as separate fees on top.</div>
    </div>

    <h2>Your fees</h2>
    <div style="font-size:13px;margin-bottom:6px">Total parcels delivered / yr: <b>${n0(annParcel)}</b></div>
    <table>
      <thead><tr><th>Fee</th><th>Projected / yr</th><th>Rate (ex GST)</th><th>Rate (+GST)</th></tr></thead>
      <tbody>
        <tr><td>Parcel fee <span style="color:#8a7a3c;font-weight:700">— contract</span></td><td>${n0(annParcel)}</td><td>${m(pRate, 3)}</td><td>${m(pRate * 1.1, 3)}</td></tr>
        <tr><td>Express Post (EP) fee</td><td>${n0(annEp)}</td><td>${m(eRate, 3)}</td><td>${m(eRate * 1.1, 3)}</td></tr>
        <tr><td>Depot bag fee</td><td>${n0(annDepot)}</td><td>${m(depotRate, 3)}</td><td>${m(depotRate * 1.1, 3)}</td></tr>
        <tr><td>DoE fee</td><td>${n0(annDoe)}</td><td>${m(doeRate, 3)}</td><td>${m(doeRate * 1.1, 3)}</td></tr>
      </tbody>
    </table>
    <div style="font-size:9.5px;color:#6b7280;margin-top:5px">The contract fee is paid across your parcels. Express Post, Depot bags &amp; DoE are paid per item as <b>separate fees on top</b> of your parcel earnings.</div>

    ${ptNow ? `
    <h2>How your day is paid</h2>
    <table>
      <thead><tr><th>Part of your day</th><th>Measured from</th><th>Paid</th></tr></thead>
      <tbody>
        <tr><td><b>Depot — sorting &amp; loading</b></td><td>Start to scanner sign-off</td><td><b>${n0(ptNow.indoorPace)} parcels/hr</b></td></tr>
        <tr><td><b>On the road — delivering</b></td><td>Scanner sign-off to finish</td><td><b>in full, actual</b></td></tr>
      </tbody>
    </table>
    <div style="font-size:13px;margin-bottom:6px">Your depot time is paid as an allowance of one hour per ${n0(ptNow.indoorPace)} parcels — on ${n0(ptNow.parcelsPerDay)} parcels that is <b>${fmtDur(ptNow.parcelsPerDay / ptNow.indoorPace)}</b>. Clearing the depot faster than that is time back in your day, and it is yours. Your road time is paid in full, however long the round takes, from the moment you sign off your scanner and photograph the summary in the portal. That photo is what starts your road clock, so take it as soon as you are loaded.</div>
    <div style="font-size:11.5px;color:#6b7280;margin-bottom:6px">Your rate of ${m(pRate, 3)} per parcel is built as <b>${m(awardHr / ptNow.indoorPace, 3)}</b> for the depot + <b>${m(pRate - awardHr / ptNow.indoorPace, 3)}</b> for the road — a payable day of ${fmtDur(ptNow.parcelsPerDay / ptNow.indoorPace + ptNow.parcelsPerDay / ptNow.outdoorPace)} on ${n0(ptNow.parcelsPerDay)} parcels.</div>
    ` : ''}

    <h2>Delivering faster earns you more</h2>
    <div style="font-size:13px;margin-bottom:6px">${paceOnRoad
      ? `Your fee is per parcel, so the faster you work the more you earn per hour. Your day has two parts and you can move either one. The three tables below are the same deal read three ways — pick whichever one you think in.`
      : `Your fee is per parcel — the quicker you clear your round, the more you earn per hour. ${paceModel?.hasRange ? `This round is expected to run at <b>${num(paceModel.low)}–${num(paceModel.high)} parcels/hr</b>, and your rate is set on that expectation.` : `You currently average <b>${pph.toFixed(1)} parcels/hr</b>.`}`}</div>
    <div style="font-size:13px;margin-bottom:10px"><b>${m(awardHr)}/hr</b> is the target to clear — it is also the NSW Contract Determination safety net for your vehicle class, so you are never paid less than it. Any pace below is short of the target; every pace above it, the extra is yours to keep.</div>
    ${paceTables.map((t) => `
    <div class="keep" style="margin-bottom:12px;border:1px solid #d8dbe2;border-radius:6px;padding:11px 13px 6px">
      ${t.title ? `<div style="font-size:13.5px;font-weight:700;color:#1a1a2e;margin-bottom:2px">${t.title}</div>` : ''}
      ${t.holds ? `<div style="font-size:11.5px;color:#6b7280;margin-bottom:4px">${t.holds}</div>` : ''}
      ${t.breakEven > 0 ? `<div style="font-size:11.5px;color:#6b7280;margin-bottom:5px">${(() => {
        if (t.mode === 'road' || t.mode === 'depot') {
          const otherPace = t.mode === 'road' ? t.depotPace : t.roadPace;
          const otherLbl = t.mode === 'road' ? 'depot allowance' : 'road time';
          const os = awardHr / otherPace, ts = pRate - os;
          return `<b>${m(os, 3)}</b> of your ${m(pRate, 3)} pays for your ${otherLbl}, leaving <b>${m(ts, 3)}</b>, so you hit the target here at ${m(awardHr)} ÷ ${m(ts, 3)} = <b>${t.breakEven.toFixed(1)}</b> an hour.`;
        }
        return `Across the whole day it is simply ${m(awardHr)} ÷ ${m(pRate, 3)} = <b>${t.breakEven.toFixed(1)} parcels an hour</b>.`;
      })()}</div>` : ''}
      <table>
        <thead><tr><th>${t.unit}</th><th>Your $/hr (ex GST)</th><th>Against the target</th></tr></thead>
        <tbody>
          ${t.paceRows.map((r) => { const pL = Number.isInteger(r.p) ? String(r.p) : r.p.toFixed(1); return r.isMin
            ? `<tr class="now"><td style="color:#8a7a3c;font-weight:700">${pL} — the target pace${r.now ? ', and your pace now' : ''}</td><td style="color:#8a7a3c;font-weight:700">${m(r.hr)}</td><td style="color:#8a7a3c;font-weight:700">you hit the target</td></tr>`
            : `<tr class="${r.now ? 'now' : ''}" style="${r.below ? 'color:#6b7280' : ''}"><td>${pL}${r.now ? ((paceModel?.hasRange && !paceOnRoad) ? ' (expected pace)' : ' (your pace now)') : ''}</td><td>${m(r.hr)}</td><td>${r.below ? `${m(awardHr - r.hr)} under target` : `<b style="color:#2e7d32">+${m(r.hr - awardHr)}</b>`}</td></tr>`; }).join('')}
        </tbody>
      </table>
    </div>`).join('')}

    ${bonusOn && speedBonus && speedBonus.perParcel > 0 ? `
    <h2>Earn the higher rate — your speed bonus</h2>
    <table>
      <thead><tr><th>Your rate per parcel</th><th>Ex GST</th><th>+GST</th></tr></thead>
      <tbody>
        <tr><td>Standard rate</td><td>${m(speedBonus.base, 3)}</td><td>${m(speedBonus.base * 1.1, 3)}</td></tr>
        <tr><td style="color:#2e7d32;font-weight:700">On-target rate — every parcel that period</td><td style="color:#2e7d32;font-weight:700">${m(speedBonus.onTarget, 3)}</td><td style="color:#2e7d32;font-weight:700">${m(speedBonus.onTarget * 1.1, 3)}</td></tr>
      </tbody>
    </table>
    <div style="font-size:13px;margin-bottom:6px">${speedBonus.basis === 'road'
      ? `How to earn it: deliver <b>${num(speedBonus.roadTarget)} parcels an hour</b> on the road — measured from the moment you sign off your scanner to the moment you finish. On a typical ${n0(speedBonus.parcelsPerDay)}-parcel day that is a <b>${fmtDur(speedBonus.parcelsPerDay / speedBonus.roadTarget)}</b> round.`
      : `How to earn it: sign off your scanner and capture the summary in the portal by your start time of <b>${speedBonus.startStr}</b> plus one hour for every <b>${n0(speedBonus.indoorPace)} parcels</b>. On a typical ${n0(speedBonus.parcelsPerDay)}-parcel day that means being away by <b>${fromMin(speedBonus.typicalTargetMin)}</b>.`}
      Do that on <b>${n0(bonusHitPct)}%</b> of the days in a period and <b>every</b> parcel in that period is paid at the higher rate — not just the days you hit it.</div>
    <div style="font-size:11.5px;color:#6b7280;margin-bottom:6px">That is worth about <b>${m0(speedBonus.perPeriod)}</b> extra a period, or <b>${m0(speedBonus.perYear)}</b> a year. Both ends are recorded for you — the scanner photo is timestamped when you take it, and your finish is logged where you finish — so none of it depends on paperwork. If Australia Post's freight is late that day, the day does not count.</div>` : ''}

    ${annEp > 0 ? `
    <div class="bonus">
      <div class="t">Bonus potential — improve your income with Express Post</div>
      <div class="d">Express Post is uncapped upside, paid on top of your contract. At <b>${n0(offerWb.epTargetPerWeek)} EP/week</b> (${n0(annEpTarget)}/yr) that's <b style="color:#2e7d32">+${m0(annEpTargetBonusInc)}/yr</b> — every EP earns you ${m(eRate * 1.1)} inc.</div>
    </div>` : ''}
    <div class="bonus">
      <div class="t">Bonus potential — assist with overflow parcels</div>
      <div class="d">Help clear overflow parcels from other routes — each is paid at your parcel fee of ${m(pRate * 1.1, 3)} inc. On a day you take an extra <b>${n0(overflowPerDay)}</b>, that's <b style="color:#2e7d32">+${m(overflowBonusDayInc)}</b> that day — on top, whenever it's needed.</div>
    </div>
    ${cmpHtml}
    <div class="foot">
      ${aboveAward.abovePct >= 0
        ? `This offer sits <b>${aboveAward.abovePct.toFixed(1)}% above</b> the NSW Transport Industry IR Safety Net award minimum of ${m(aboveAward.awardHr)}/hr for your vehicle class (${vClass || '—'}), effective ${RATES_EFFECTIVE} — your parcel contract alone works out to ${m(aboveAward.hr)}/hr at ${paceModel?.hasRange ? 'the expected pace' : 'your current pace'}.`
        : `The NSW Transport Industry IR Safety Net guarantees you a minimum of <b>${m(aboveAward.awardHr)}/hr</b> for your vehicle class (${vClass || '—'}), effective ${RATES_EFFECTIVE}, and you are never paid less than it. Working at the paces above is what carries you past it.`}
      ${paceModel?.hasRange ? `<b>IR Safety Net protection:</b> your per-parcel rate is set on an expected pace of ${num(paceModel.low)}–${num(paceModel.high)} parcels/hr — our estimate of the round, not a figure you carry the risk on. If it runs slower, you are still guaranteed the ${m(aboveAward.awardHr)}/hr minimum safety-net rate for your vehicle class; the contracted rate never leaves you below that floor, and you keep any upside above it. ` : ''}
      Projected parcel, Express Post, Depot and DoE volumes are indicative only, based on ${assessRec ? `${assessRec.name || assessSource}'s` : `${name}'s`} recorded delivery history on this round (assuming ${n0(aboveAward.wd)} working days a year). <b>Actual volumes are driven by Australia Post demand and are not guaranteed.</b> All fees are paid per item, so earnings move up and down with volume. This is an indicative offer summary for discussion, not a contract.
    </div>
  </div>
  <script>window.onload=function(){window.print();}</script>
</body></html>`;
    const w = window.open('', '_blank');
    if (w) { w.document.write(html); w.document.close(); } else { addNotification('Pop-up blocked — allow pop-ups to print the offer', 'error'); }
  };

  // Data for the driver-facing report (only ready once pricing has computed).
  const reportData = useMemo(() => {
    if (!analysis || !pricing || !driverRec) return null;
    return {
      name: driverRec.name, login: driver,
      route: driverRec.route, type: driverRec.type, vehicle: driverRec.vehicle,
      vehicleClassLabel: (VEHICLE_CLASSES.find(v => v.key === cfg.vehicleClass) || {}).label,
      periodStart, periodEnd, productiveDays: analysis.productiveDays,
      perParcel: pricing.perParcel, perParcelGst: pricing.perParcelGst,
      awardHourly: pricing.awardHourly, ratesEffective: RATES_EFFECTIVE,
      medianPPH: analysis.medianPPH, bestPPH: analysis.bestPPH,
      avgParcelsPerDay: analysis.avgParcelsPerDay,
      hoursYr: projection ? projection.annualHours : 0,
    };
  }, [analysis, pricing, projection, driverRec, driver, cfg.vehicleClass, periodStart, periodEnd]);

  if (!isAdmin) {
    return <Card><Title>Driver Review</Title><div style={{ color: 'var(--hp-dim)' }}>Access denied — admin only.</div></Card>;
  }

  return (
    <div style={{ maxWidth: 1080, margin: '0 auto', paddingBottom: 40 }}>
      {/* Header */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 12, margin: '4px 0 20px' }}>
        <div style={{ width: 42, height: 42, borderRadius: 11, background: 'var(--hp-gold)', display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'var(--hp-black)' }}><Gauge size={22} /></div>
        <div>
          <div style={{ fontSize: '1.4rem', fontWeight: 900, color: 'var(--hp-text)', letterSpacing: '-0.02em' }}>Driver Review</div>
          <div style={{ fontSize: '0.82rem', color: 'var(--hp-dim)' }}>Assess a driver &amp; route → set an achievable, beatable per-parcel price</div>
        </div>
      </div>

      {/* Offers accepted with no contract raised — never let one sit unseen again. */}
      {awaiting.length > 0 && (
        <Card style={{ borderColor: 'var(--hp-gold)', background: 'color-mix(in srgb, var(--hp-gold) 8%, transparent)' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 8 }}>
            <FileText size={15} />
            <span style={{ fontWeight: 900, color: 'var(--hp-text)' }}>
              {awaiting.length === 1 ? '1 accepted offer is awaiting a contract' : `${awaiting.length} accepted offers are awaiting a contract`}
            </span>
          </div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
            {awaiting.map(d => {
              const login = (d.driverLogin || '').toUpperCase();
              const rec = drivers.find(x => x.loginName === login);
              const days = d.respondedAt ? Math.floor((Date.now() - new Date(d.respondedAt).getTime()) / 86400000) : null;
              return (
                <div key={d.id} style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap', fontSize: 12.5 }}>
                  <span style={{ fontWeight: 700, color: 'var(--hp-text)' }}>{rec?.name || login}</span>
                  <span style={{ color: 'var(--hp-dim)' }}>
                    accepted {d.respondedAt ? new Date(d.respondedAt).toLocaleString('en-AU', { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' }) : '—'}
                    {days !== null && days > 0 ? ` · waiting ${days} day${days === 1 ? '' : 's'}` : ''}
                  </span>
                  {driver !== login && (
                    <button onClick={() => setDriver(login)} style={{ ...stepBtn, padding: '4px 10px' }}>Open →</button>
                  )}
                </div>
              );
            })}
          </div>
        </Card>
      )}

      {/* Signed contracts the billing doesn't honour — "signed" isn't done, "activated" is. */}
      {contractGaps.length > 0 && (
        <Card style={{ borderColor: contractGaps.some(g => g.kind === 'mismatch') ? 'var(--hp-red, #c0392b)' : 'var(--hp-gold)', background: contractGaps.some(g => g.kind === 'mismatch') ? 'color-mix(in srgb, #c0392b 7%, transparent)' : 'color-mix(in srgb, var(--hp-gold) 8%, transparent)' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 8 }}>
            <AlertTriangle size={15} />
            <span style={{ fontWeight: 900, color: 'var(--hp-text)' }}>
              {contractGaps.length === 1 ? '1 signed contract isn’t reflected in billing' : `${contractGaps.length} signed contracts aren’t reflected in billing`}
            </span>
          </div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 7 }}>
            {contractGaps.map(g => {
              const rec = drivers.find(x => x.loginName === g.login);
              const signed = g.agreement.signedAt ? new Date(g.agreement.signedAt).toLocaleDateString('en-AU', { day: 'numeric', month: 'short', year: 'numeric' }) : '—';
              return (
                <div key={g.login} style={{ display: 'flex', alignItems: 'baseline', gap: 10, flexWrap: 'wrap', fontSize: 12.5 }}>
                  <span style={{ fontWeight: 700, color: 'var(--hp-text)' }}>{rec?.name || g.login}</span>
                  <span style={{ color: 'var(--hp-dim)' }}>signed {signed}</span>
                  {g.kind === 'mismatch' ? (
                    <span style={{ color: 'var(--hp-text)' }}>
                      <span style={{ fontWeight: 800, color: 'var(--hp-red, #c0392b)' }}>bills the wrong rate{g.diffs.length === 1 ? '' : 's'}: </span>
                      {g.diffs.map(d => `${d.key} contract $${d.contract} vs ${d.billed == null ? 'nothing' : `$${d.billed}`} ${g.source === 'activation' ? 'activated' : 'on the sheet'}`).join(' · ')}
                    </span>
                  ) : (
                    <span style={{ color: 'var(--hp-dim)' }}>
                      rates match today but aren't pinned to a dated activation — a later rate edit would silently re-price his history
                    </span>
                  )}
                  {driver !== g.login && (
                    <button onClick={() => setDriver(g.login)} style={{ ...stepBtn, padding: '4px 10px' }}>Open →</button>
                  )}
                </div>
              );
            })}
          </div>
          <div style={{ fontSize: 11.5, color: 'var(--hp-dim)', marginTop: 8 }}>
            Fix: open the driver → Edit rates to the contract figures → Activate to RCTI from the right period → rebuild that period's RCTI.
          </div>
        </Card>
      )}

      {/* Controls */}
      <Card>
        <div style={{ display: 'flex', gap: 16, flexWrap: 'wrap', alignItems: 'flex-end' }}>
          <div>
            <Label>Driver (offer is addressed to)</Label>
            <select value={driver} onChange={e => { setHistory(null); setDriver(e.target.value); }} style={{ ...inputStyle, minWidth: 220 }}>
              {drivers.map(d => <option key={d.loginName} value={d.loginName}>{d.loginName} — {d.name}</option>)}
            </select>
          </div>
          <div>
            <Label>Assess on record of</Label>
            <select value={assessSource} onChange={e => { setHistory(null); setAssessSource(e.target.value); }} style={{ ...inputStyle, minWidth: 220 }}
              title="Price this driver on another driver's route history — e.g. a new driver taking over the previous driver's round. Leave as 'same driver' to use his own record.">
              <option value="">— same driver (own record) —</option>
              {drivers.filter(d => d.loginName !== driver).map(d => <option key={d.loginName} value={d.loginName}>{d.loginName} — {d.name}</option>)}
            </select>
          </div>
          <div>
            <Label>From</Label>
            <input type="date" value={periodStart} max={periodEnd} onChange={e => setPeriodStart(e.target.value)} style={inputStyle} />
          </div>
          <div>
            <Label>To</Label>
            <input type="date" value={periodEnd} min={periodStart} max={todayISO()} onChange={e => setPeriodEnd(e.target.value)} style={inputStyle} />
          </div>
          <div>
            <Label>Exclude ramp-up (weeks)</Label>
            <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
              <button onClick={() => setCfgKey('rampWeeks', Math.max(0, (parseInt(cfg.rampWeeks, 10) || 0) - 1))} style={stepBtn} title="Fewer ramp-up weeks">−</button>
              <input type="number" min="0" max="12" value={cfg.rampWeeks}
                onChange={e => setCfgKey('rampWeeks', Math.max(0, parseInt(e.target.value, 10) || 0))}
                style={{ ...inputStyle, width: 60, textAlign: 'center' }} title="Drop this many weeks from the driver's first delivery day (developing period)" />
              <button onClick={() => setCfgKey('rampWeeks', Math.min(12, (parseInt(cfg.rampWeeks, 10) || 0) + 1))} style={stepBtn} title="More ramp-up weeks">+</button>
            </div>
          </div>
          <button onClick={load} disabled={loading || !driver}
            style={{ ...inputStyle, cursor: loading ? 'wait' : 'pointer', background: 'var(--hp-gold)', color: 'var(--hp-black)', border: 'none', fontWeight: 800, display: 'flex', alignItems: 'center', gap: 8, height: 38 }}>
            <RefreshCw size={15} className={loading ? 'spin' : ''} /> {loading ? 'Loading…' : 'Load'}
          </button>
          {driverRec && (
            <div style={{ marginLeft: 'auto', display: 'flex', alignItems: 'center', gap: 12 }}>
              <div style={{ fontSize: 12, color: 'var(--hp-dim)', textAlign: 'right' }}>
                <div style={{ fontWeight: 800, color: 'var(--hp-text)', fontSize: 13 }}>{driverRec.name || driver}</div>
                <div><b style={{ color: 'var(--hp-text)' }}>Route:</b> {driverRec.route || '—'} · <b style={{ color: 'var(--hp-text)' }}>Type:</b> {driverRec.type || '—'}</div>
                <div><b style={{ color: 'var(--hp-text)' }}>Vehicle:</b> {driverRec.vehicle || '—'}</div>
              </div>
              <DriverAvatar login={driver} photo={photo} size={48} />
            </div>
          )}
        </div>
        {assessSource && assessSource !== driver && (
          <div style={{ marginTop: 14, padding: '10px 14px', background: 'color-mix(in srgb, var(--hp-gold) 12%, transparent)', border: '1px solid var(--hp-gold)', borderRadius: 8, fontSize: 12.5, color: 'var(--hp-dim)', display: 'flex', alignItems: 'center', gap: 8 }}>
            <Info size={15} style={{ flexShrink: 0, color: 'var(--hp-gold)' }} />
            <span>
              Assessing on <b style={{ color: 'var(--hp-text)' }}>{assessRec?.name || assessSource}</b>’s ({assessSource}) route record —
              the offer is addressed to <b style={{ color: 'var(--hp-text)' }}>{driverRec?.name || driver}</b> ({driver}). All performance, volumes and projections below are {assessRec?.name || assessSource}’s; the offer identity, route and current-deal comparison are {driverRec?.name || driver}’s.
            </span>
          </div>
        )}
      </Card>

      {error && <Card style={{ borderColor: 'var(--hp-red)' }}><div style={{ color: 'var(--hp-red)', fontWeight: 700 }}>{error}</div></Card>}

      {/* Hourly-fee contract — dial an hourly rate and watch the GST-inclusive $/hr move.
          Independent of the parcel analysis, for drivers paid by the hour (e.g. PETRATOSS). */}
      <Card>
        <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 12, flexWrap: 'wrap' }}>
          <Title sub="For drivers on an hourly fee (not per parcel). Dial the rate up or down and the GST-inclusive $/hr moves with it.">Hourly rate contract</Title>
          <div style={{ display: 'flex', gap: 4, background: 'var(--hp-bg)', border: '1px solid var(--hp-border)', borderRadius: 8, padding: 3 }}>
            {[{ k: true, l: 'GST inc' }, { k: false, l: 'Ex GST' }].map(o => {
              const on = !!cfg.hourlyGstInc === o.k;
              return (
                <button key={o.l} onClick={() => setCfgKey('hourlyGstInc', o.k)}
                  title={`Treat the dialled rate as ${o.l}`}
                  style={{ ...stepBtn, border: 'none', background: on ? 'var(--hp-gold)' : 'transparent', color: on ? 'var(--hp-black)' : 'var(--hp-dim)' }}>{o.l}</button>
              );
            })}
          </div>
        </div>

        <div style={{ display: 'flex', gap: 16, flexWrap: 'wrap', alignItems: 'flex-end' }}>
          <div>
            <Label>Hourly rate ({cfg.hourlyGstInc ? 'GST inc' : 'ex GST'}) — dial up/down</Label>
            <div style={{ display: 'flex', alignItems: 'center', gap: 6, flexWrap: 'wrap' }}>
              {[-1, -0.5].map(s => <button key={s} onClick={() => setCfgKey('hourlyRate', Math.max(0, +((parseFloat(cfg.hourlyRate) || 0) + s).toFixed(2)))} style={stepBtn}>{money(s).replace('-', '−')}</button>)}
              <input type="number" step="0.5" min="0" value={cfg.hourlyRate}
                onChange={e => setCfgKey('hourlyRate', Math.max(0, parseFloat(e.target.value) || 0))}
                style={{ ...inputStyle, width: 130, textAlign: 'center', fontSize: 22, fontWeight: 900, color: 'var(--hp-gold)' }} />
              {[0.5, 1].map(s => <button key={s} onClick={() => setCfgKey('hourlyRate', Math.max(0, +((parseFloat(cfg.hourlyRate) || 0) + s).toFixed(2)))} style={stepBtn}>+{money(s)}</button>)}
            </div>
          </div>
        </div>

        {(() => {
          const rate = Math.max(0, parseFloat(cfg.hourlyRate) || 0);
          const exGst = cfg.hourlyGstInc ? rate / 1.1 : rate;
          const gst = exGst * 0.1;
          const inc = exGst + gst;
          return (
            <>
              <div style={{ marginTop: 16, display: 'flex', gap: 12, flexWrap: 'wrap' }}>
                <Stat label="Rate /hr — GST inc" value={`${money(inc)}/h`} tone="var(--hp-gold)" sub="what the driver invoices, GST included" />
                <Stat label="Rate /hr — ex GST" value={`${money(exGst)}/h`} sub="base fee before GST" />
                <Stat label="GST /hr" value={`${money(gst)}/h`} sub="10% component" />
              </div>
              <div style={{ marginTop: 14, fontSize: 12.5, color: 'var(--hp-dim)', lineHeight: 1.6 }}>
                At <b style={{ color: 'var(--hp-gold)' }}>{money(inc)}/hr GST inc</b> the driver's fee is <b style={{ color: 'var(--hp-text)' }}>{money(exGst)}/hr</b> ex GST plus <b style={{ color: 'var(--hp-text)' }}>{money(gst)}/hr</b> GST. Dial the rate to see the GST-inclusive figure move.
              </div>
            </>
          );
        })()}
      </Card>

      {analysis && !analysis.productiveDays && (
        <Card>
          {analysis.rampWeeks > 0 && analysis.allProdDays > 0 ? (
            <div style={{ color: 'var(--hp-dim)' }}>
              All <b style={{ color: 'var(--hp-text)' }}>{analysis.allProdDays}</b> productive day{analysis.allProdDays === 1 ? '' : 's'} fall within the first <b style={{ color: 'var(--hp-text)' }}>{analysis.rampWeeks} week{analysis.rampWeeks === 1 ? '' : 's'}</b> of this driver’s record (their developing period, before {analysis.rampCutoffISO}) — there’s no settled data yet to rate. Lower the ramp-up weeks, or wait until they have days past the developing window.
            </div>
          ) : (
            <div style={{ color: 'var(--hp-dim)' }}>No productive days (a day with both a shift and parcels) in this window. Try a wider date range.</div>
          )}
        </Card>
      )}

      {analysis && !!analysis.productiveDays && pricing && (
        <>
          {/* 1 ── Performance summary */}
          <Card>
            <Title sub={`${analysis.productiveDays} productive days · ${num(analysis.totalParcels, 0)} parcels over ${num(analysis.totalHours)} h`}>1 · Performance</Title>

            {analysis.rampWeeks > 0 && (
              <div style={{ marginBottom: 14, padding: '10px 14px', background: 'color-mix(in srgb, var(--hp-gold) 10%, transparent)', border: '1px solid var(--hp-border)', borderRadius: 8, fontSize: 12.5, color: 'var(--hp-dim)', display: 'flex', alignItems: 'center', gap: 8 }}>
                <Info size={15} style={{ flexShrink: 0, color: 'var(--hp-gold)' }} />
                <span>
                  Excluding the first <b style={{ color: 'var(--hp-text)' }}>{analysis.rampWeeks} week{analysis.rampWeeks === 1 ? '' : 's'}</b> (developing period) —
                  <b style={{ color: 'var(--hp-text)' }}> {analysis.excludedRampDays}</b> of {analysis.allProdDays} days dropped
                  {analysis.rampCutoffISO ? ` (before ${analysis.rampCutoffISO})` : ''}. Stats below are on the remaining <b style={{ color: 'var(--hp-text)' }}>{analysis.productiveDays}</b> settled days.
                  {analysis.productiveDays < 5 && <b style={{ color: 'var(--hp-red)' }}> Thin sample — treat with care.</b>}
                </span>
              </div>
            )}

            {analysis.manualExcludedDays > 0 && (
              <div style={{ marginBottom: 14, padding: '10px 14px', background: 'color-mix(in srgb, var(--hp-gold) 10%, transparent)', border: '1px solid var(--hp-border)', borderRadius: 8, fontSize: 12.5, color: 'var(--hp-dim)', display: 'flex', alignItems: 'center', gap: 8 }}>
                <Info size={15} style={{ flexShrink: 0, color: 'var(--hp-gold)' }} />
                <span>
                  <b style={{ color: 'var(--hp-text)' }}>{analysis.manualExcludedDays}</b> day{analysis.manualExcludedDays === 1 ? '' : 's'} manually excluded — dropped from every stat and projection below. Re-include from the daily table (section 4).
                </span>
              </div>
            )}

            {analysis.scannerDays > 0 && (
              <div style={{ marginBottom: 14, padding: '10px 14px', background: 'color-mix(in srgb, var(--hp-gold) 10%, transparent)', border: '1px solid var(--hp-border)', borderRadius: 8, fontSize: 12.5, color: 'var(--hp-dim)', display: 'flex', alignItems: 'center', gap: 8 }}>
                <Info size={15} style={{ flexShrink: 0, color: 'var(--hp-gold)' }} />
                <span>
                  <b style={{ color: 'var(--hp-text)' }}>{analysis.scannerDays}</b> of {analysis.productiveDays} settled day{analysis.productiveDays === 1 ? '' : 's'} use <b style={{ color: 'var(--hp-text)' }}>AP scanner times</b> (from the Driver Activity Summary) — days before the portal captured shift times. The rest are portal clock-on/off. Tagged in the daily table below.
                </span>
              </div>
            )}

            <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap' }}>
              <Stat label="Median P/H" value={num(analysis.medianPPH)} tone="var(--hp-gold)" sub="typical day" />
              <Stat label="Average P/H" value={num(analysis.avgPPH)} sub="volume-weighted" />
              <Stat label="Good day (p75)" value={num(analysis.p75PPH)} tone="var(--hp-green)" sub="beats this 25% of days" />
              <Stat label="Best / Worst" value={`${num(analysis.bestPPH)} / ${num(analysis.worstPPH)}`} sub="range" />
              <Stat label="Avg / day" value={`${num(analysis.avgParcelsPerDay, 0)} pcl`} sub={`${num(analysis.avgHoursPerDay)} h/day`} />
              {pricing.expectedPH > 0 && (
                <Stat label="Expected P/H" value={num(pricing.expectedPH)} tone={analysis.medianPPH >= pricing.expectedPH ? 'var(--hp-green)' : 'var(--hp-red)'}
                  sub={analysis.medianPPH >= pricing.expectedPH ? 'at/above target' : 'below target'} />
              )}
            </div>

            {/* Consistency — days at/above vs below a chosen pace (0.5 steps) */}
            {(() => {
              const thr = threshPPH != null ? threshPPH : Math.round((analysis.medianPPH || 0) * 2) / 2;
              const rows = analysis.prodRows || [];
              const above = rows.filter(r => r.pph >= thr - 1e-9).length;
              const below = rows.length - above;
              const pct = rows.length ? (above / rows.length) * 100 : 0;
              return (
                <div style={{ marginTop: 16, padding: '14px 16px', background: 'var(--hp-bg)', border: '1px solid var(--hp-border)', borderRadius: 10 }}>
                  <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12, flexWrap: 'wrap', marginBottom: 10 }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8, fontWeight: 800, color: 'var(--hp-text)' }}>
                      <Gauge size={16} /> How often does he hit a pace?
                    </div>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                      <span style={{ fontSize: 12, color: 'var(--hp-dim)' }}>Threshold</span>
                      <button onClick={() => setThreshPPH(Math.max(0, +(thr - 0.5).toFixed(1)))} style={stepBtn}>−0.5</button>
                      <input type="number" step="0.5" min="0" value={thr}
                        onChange={e => setThreshPPH(Math.max(0, parseFloat(e.target.value) || 0))}
                        style={{ ...inputStyle, width: 82, textAlign: 'center', fontWeight: 800 }} />
                      <span style={{ fontSize: 12, color: 'var(--hp-dim)' }}>P/H</span>
                      <button onClick={() => setThreshPPH(+(thr + 0.5).toFixed(1))} style={stepBtn}>+0.5</button>
                      {threshPPH != null && <button onClick={() => setThreshPPH(null)} title="Reset to median" style={stepBtn}><RefreshCw size={12} /></button>}
                    </div>
                  </div>
                  <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap' }}>
                    <Stat label={`Days at/above ${num(thr)}`} value={`${above}`} tone="var(--hp-green)" sub={`${num(pct, 0)}% of ${rows.length} days`} />
                    <Stat label={`Days below ${num(thr)}`} value={`${below}`} tone={below > 0 ? 'var(--hp-red)' : 'var(--hp-dim)'} sub={`${num(100 - pct, 0)}% of ${rows.length} days`} />
                  </div>
                  {/* proportion bar */}
                  <div style={{ marginTop: 12, height: 12, borderRadius: 6, overflow: 'hidden', display: 'flex', border: '1px solid var(--hp-border)' }}>
                    <div style={{ width: `${pct}%`, background: 'var(--hp-green)' }} title={`${above} at/above`} />
                    <div style={{ width: `${100 - pct}%`, background: 'color-mix(in srgb, var(--hp-red) 55%, transparent)' }} title={`${below} below`} />
                  </div>
                  <div style={{ marginTop: 8, fontSize: 12, color: 'var(--hp-dim)' }}>
                    He runs at <b style={{ color: 'var(--hp-text)' }}>{num(thr)} P/H or better on {above} of {rows.length} days</b> ({num(pct, 0)}%). Dial the threshold to test how reliably he holds a scheduled pace — the more days above, the safer that pace is to price on.
                  </div>
                </div>
              );
            })()}

            {/* Improvement opportunity */}
            <div style={{ marginTop: 16, padding: '14px 16px', background: 'var(--hp-bg)', border: '1px solid var(--hp-border)', borderRadius: 10 }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8, fontWeight: 800, color: 'var(--hp-text)', marginBottom: 6 }}>
                <TrendingUp size={16} /> Speed improvement opportunity
              </div>
              {(() => {
                const gap = analysis.bestPPH - analysis.medianPPH;
                const pct = analysis.medianPPH > 0 ? (gap / analysis.medianPPH) * 100 : 0;
                const hoursIfBest = analysis.bestPPH > 0 ? analysis.totalParcels / analysis.bestPPH : 0;
                const hoursSaved = analysis.totalHours - hoursIfBest;
                return (
                  <div style={{ fontSize: 13, color: 'var(--hp-dim)', lineHeight: 1.6 }}>
                    He already hits <b style={{ color: 'var(--hp-green)' }}>{num(analysis.bestPPH)} P/H</b> on his best day but sits at a median of <b style={{ color: 'var(--hp-text)' }}>{num(analysis.medianPPH)} P/H</b> — a <b style={{ color: 'var(--hp-gold)' }}>{num(pct, 0)}%</b> lift is on the table using pace he has already proven.
                    <br />Closing that gap would deliver the same {num(analysis.totalParcels, 0)} parcels in <b style={{ color: 'var(--hp-text)' }}>~{num(hoursSaved)} h less</b> over this window — freeing capacity or lifting his effective hourly earnings by the same margin.
                  </div>
                );
              })()}
            </div>
          </Card>

          {/* 1b ── Current deal — the live per-parcel rate he's actually paid */}
          <Card>
            <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 12, flexWrap: 'wrap' }}>
              <Title sub="His live per-parcel rate from the RCTI rate card, and how far above the award it already sits — for advising the driver, not for cutting pay.">Current per-parcel deal</Title>
              <button onClick={() => showRateEditor ? setShowRateEditor(false) : openRateEditor()}
                title={`Edit ${driverRec?.name || driver}'s live service rates (Parcel, Express, DoE, Depot)`}
                style={{ ...stepBtn, padding: '10px 18px', fontSize: 14, fontWeight: 800,
                  background: showRateEditor ? 'var(--hp-surface)' : 'var(--hp-blue)',
                  color: showRateEditor ? 'var(--hp-text)' : '#fff',
                  border: showRateEditor ? '1.5px solid var(--hp-border)' : '2px solid var(--hp-gold)',
                  boxShadow: showRateEditor ? 'none' : '0 2px 8px rgba(0,0,0,0.22)' }}>
                <Pencil size={15} /> {showRateEditor ? 'Close editor' : 'Edit rates'}
              </button>
            </div>
            {currentDeal && currentDeal.hasRate ? (
              <>
                <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap' }}>
                  <Stat label="Current parcel price" value={money2(currentDeal.rate)} tone="var(--hp-gold)" sub={`${money2(currentDeal.rateGst)} GST inc`} />
                  <Stat label="His $/hr on this rate" value={money(currentDeal.hourlyNow)} tone="var(--hp-green)" sub={`at ${num(currentDeal.cur)} P/H median`} />
                  <Stat label="Over award floor" value={`${currentDeal.overHr >= 0 ? '+' : '−'}${money(Math.abs(currentDeal.overHr))}/h`} tone={currentDeal.overHr >= 0 ? 'var(--hp-green)' : 'var(--hp-red)'} sub={`${num(currentDeal.overPct)}% above ${money(currentDeal.award)}/h`} />
                  <Stat label="Over award / parcel" value={`${currentDeal.overPerParcel >= 0 ? '+' : '−'}${money2(Math.abs(currentDeal.overPerParcel))}`} tone={currentDeal.overPerParcel >= 0 ? 'var(--hp-green)' : 'var(--hp-red)'} sub={`award min ${money2(currentDeal.awardMinAtPace)} at his pace`} />
                  {currentDeal.annualNow > 0 && <Stat label="Annual income (current)" value={money(currentDeal.annualNow)} tone="var(--hp-green)" sub="at projected volumes" />}
                </div>
                <div style={{ marginTop: 14, fontSize: 12.5, color: 'var(--hp-dim)', lineHeight: 1.6 }}>
                  At his current <b style={{ color: 'var(--hp-text)' }}>{money2(currentDeal.rate)}/parcel</b>{currentDeal.expRate ? ` (express ${money2(currentDeal.expRate)})` : ''}, {driverRec?.name || driver} earns about <b style={{ color: 'var(--hp-green)' }}>{money(currentDeal.hourlyNow)}/hr</b> at his {num(currentDeal.cur)} P/H pace — <b style={{ color: 'var(--hp-green)' }}>{num(currentDeal.overPct)}% ({money(currentDeal.overHr)}/h) above</b> the {money(currentDeal.award)}/hr award floor. That's a strong deal: the per-parcel price already sits at the ceiling of what the route can sustain, which is why it can't go higher.
                </div>
              </>
            ) : (
              <div style={{ fontSize: 13, color: 'var(--hp-dim)' }}>No live per-parcel rate on file for {driver} yet — hit <b style={{ color: 'var(--hp-text)' }}>Edit rates</b> to set one, or add it on the <b style={{ color: 'var(--hp-text)' }}>RCTI &amp; Xero</b> rate card.</div>
            )}

            {showRateEditor && (
              <div style={{ marginTop: 16, padding: 16, borderRadius: 10, border: '1px solid var(--hp-border)', background: 'var(--hp-bg)' }}>
                <div style={{ fontSize: 12.5, fontWeight: 800, color: 'var(--hp-text)', marginBottom: 4 }}>Set per-driver service rates — {driverRec?.name || driver}</div>
                <div style={{ fontSize: 11.5, color: 'var(--hp-dim)', marginBottom: 14, lineHeight: 1.5 }}>
                  Writes the <b style={{ color: 'var(--hp-text)' }}>Rates</b> card (<code>{driver}|Parcel</code>, <code>{driver}|Exp</code>, <code>{driver}|DOE</code>, <code>{driver}|Depot Bags</code>) used by RCTI, Contracts and GCCD. Enter rates <b>ex-GST</b>. Leave a field blank to leave that rate unchanged.
                </div>
                <div style={{ display: 'flex', gap: 16, flexWrap: 'wrap', alignItems: 'flex-end' }}>
                  <div>
                    <Label>Parcel rate ($ ex GST)</Label>
                    <input type="number" step="0.001" min="0" value={rateParcelInput} onChange={e => setRateParcelInput(e.target.value)} placeholder={liveRates.Parcel != null ? String(liveRates.Parcel) : 'e.g. 0.930'} style={{ ...inputStyle, width: 170 }} />
                    <div style={{ fontSize: 11, color: 'var(--hp-dim)', marginTop: 4 }}>{rateParcelInput.trim() !== '' && isFinite(Number(rateParcelInput)) ? `${money2(Number(rateParcelInput) * 1.1)} GST inc` : ' '}</div>
                  </div>
                  <div>
                    <Label>Express Post rate ($ ex GST)</Label>
                    <input type="number" step="0.001" min="0" value={rateExpInput} onChange={e => setRateExpInput(e.target.value)} placeholder={liveRates.Exp != null ? String(liveRates.Exp) : 'e.g. 4.550'} style={{ ...inputStyle, width: 170 }} />
                    <div style={{ fontSize: 11, color: 'var(--hp-dim)', marginTop: 4 }}>{rateExpInput.trim() !== '' && isFinite(Number(rateExpInput)) ? `${money2(Number(rateExpInput) * 1.1)} GST inc` : ' '}</div>
                  </div>
                  <div>
                    <Label>DoE rate ($ ex GST)</Label>
                    <input type="number" step="0.001" min="0" value={rateDoeInput} onChange={e => setRateDoeInput(e.target.value)} placeholder={liveRates.DOE != null ? String(liveRates.DOE) : 'e.g. 4.550'} style={{ ...inputStyle, width: 170 }} />
                    <div style={{ fontSize: 11, color: 'var(--hp-dim)', marginTop: 4 }}>{rateDoeInput.trim() !== '' && isFinite(Number(rateDoeInput)) ? `${money2(Number(rateDoeInput) * 1.1)} GST inc` : ' '}</div>
                  </div>
                  <div>
                    <Label>Depot bag rate ($ ex GST)</Label>
                    <input type="number" step="0.001" min="0" value={rateDepotInput} onChange={e => setRateDepotInput(e.target.value)} placeholder={liveRates['Depot Bags'] != null ? String(liveRates['Depot Bags']) : 'e.g. 4.550'} style={{ ...inputStyle, width: 170 }} />
                    <div style={{ fontSize: 11, color: 'var(--hp-dim)', marginTop: 4 }}>{rateDepotInput.trim() !== '' && isFinite(Number(rateDepotInput)) ? `${money2(Number(rateDepotInput) * 1.1)} GST inc` : ' '}</div>
                  </div>
                  <div style={{ display: 'flex', gap: 8, paddingBottom: 20 }}>
                    <button onClick={saveRates} disabled={savingRates}
                      style={{ ...stepBtn, background: 'var(--hp-green)', color: 'var(--hp-black)', border: 'none', fontWeight: 800, opacity: savingRates ? 0.6 : 1, cursor: savingRates ? 'default' : 'pointer' }}>
                      <Save size={13} /> {savingRates ? 'Saving…' : 'Save rates'}
                    </button>
                    <button onClick={() => setShowRateEditor(false)} disabled={savingRates} style={stepBtn}>Cancel</button>
                  </div>
                </div>

                {/* Activate to RCTI — dated rates. "Save rates" above changes what he earns
                    from now AND re-prices any closed period that later gets rebuilt.
                    Activating pins this set to a period and freezes everything before it. */}
                <div style={{ marginTop: 18, paddingTop: 16, borderTop: '1px solid var(--hp-border)' }}>
                  <div style={{ fontSize: 12.5, fontWeight: 800, color: 'var(--hp-text)', marginBottom: 4 }}>Activate to RCTI</div>
                  <div style={{ fontSize: 11.5, color: 'var(--hp-dim)', marginBottom: 12, lineHeight: 1.5 }}>
                    Pins the rates above to a billing period, so the RCTI bills them <b style={{ color: 'var(--hp-text)' }}>from that period onward only</b>.
                    Everything earlier keeps billing on his current rates, even if the period is rebuilt. Requires a <b style={{ color: 'var(--hp-text)' }}>signed contract</b>.
                  </div>

                  {driverActivations.length > 0 && (
                    <div style={{ fontSize: 11.5, marginBottom: 12, padding: '8px 10px', borderRadius: 8, background: 'var(--hp-surface)', border: '1px solid var(--hp-border)' }}>
                      <b style={{ color: 'var(--hp-green)' }}>On dated rates.</b>{' '}
                      <span style={{ color: 'var(--hp-dim)' }}>The RCTI reads these, not the Rates card:</span>
                      {activationHistory(driverActivations).map(a => (
                        <div key={a.effectiveFromISO} style={{ color: 'var(--hp-text)', marginTop: 4 }}>
                          From <b>{a.effectiveFromISO}</b> — Parcel {a.rates?.Parcel ?? '—'} · Exp {a.rates?.Exp ?? '—'} · DoE {a.rates?.DOE ?? '—'} · Depot {a.rates?.['Depot Bags'] ?? '—'} · floor {a.minHourlyRate || '—'}/hr
                          <span style={{ color: 'var(--hp-dim)' }}> (by {a.activatedBy || '—'})</span>
                        </div>
                      ))}
                    </div>
                  )}

                  {offerAccepted ? (
                    <div style={{ fontSize: 11.5, marginBottom: 12, padding: '8px 10px', borderRadius: 8, background: 'var(--hp-surface)', border: '1px solid var(--hp-border)', color: 'var(--hp-text)' }}>
                      <b style={{ color: 'var(--hp-green)' }}>Signed contract on file</b>{' '}
                      <span style={{ color: 'var(--hp-dim)' }}>
                        ({contractStatus.source === 'subcontractor-agreement' ? 'subcontractor agreement' : 'accepted offer'}
                        {contractStatus.at ? `, ${new Date(contractStatus.at).toLocaleDateString('en-AU', { day: 'numeric', month: 'short', year: 'numeric' })}` : ''}) — a rate change is authorised.
                      </span>
                    </div>
                  ) : (
                    <div style={{ fontSize: 11.5, marginBottom: 12, padding: '8px 10px', borderRadius: 8, background: 'var(--hp-surface)', border: '1px solid var(--hp-amber)', color: 'var(--hp-text)' }}>
                      <AlertTriangle size={12} style={{ verticalAlign: -2 }} />{' '}
                      {offerStatus?.response === 'rejected'
                        ? 'The driver rejected this contract offer — nothing to activate.'
                        : contractStatus === null
                          ? 'Checking for a signed contract…'
                          : 'No signed contract on file — neither a signed subcontractor agreement nor an accepted offer.'}
                    </div>
                  )}

                  <div style={{ display: 'flex', gap: 16, flexWrap: 'wrap', alignItems: 'flex-end' }}>
                    <div>
                      <Label>Takes effect from period</Label>
                      <select value={activateFromISO} onChange={e => setActivateFromISO(e.target.value)} disabled={!offerAccepted} style={{ ...inputStyle, width: 250 }}>
                        <option value="">Choose a period…</option>
                        {periodChoices.map(p => <option key={p.startISO} value={p.startISO}>{p.label}</option>)}
                      </select>
                    </div>
                    <div>
                      <Label>Minimum hourly floor ($ ex GST)</Label>
                      <input type="number" step="0.01" min="0" value={activateFloorInput} onChange={e => setActivateFloorInput(e.target.value)}
                        disabled={!offerAccepted} placeholder={currentFloor ? String(currentFloor) : 'e.g. 43.74'} style={{ ...inputStyle, width: 190 }} />
                      <div style={{ fontSize: 11, color: 'var(--hp-dim)', marginTop: 4 }}>
                        {currentFloor ? `now ${money2(currentFloor)}/hr` : 'no floor set'}
                        {rateParcelInput.trim() !== '' && isFinite(Number(rateParcelInput)) && Number(rateParcelInput) > 0 && activateFloorInput.trim() !== '' && isFinite(Number(activateFloorInput))
                          ? ` · breakeven ${(Number(activateFloorInput) / Number(rateParcelInput)).toFixed(1)} parcels/hr` : ''}
                      </div>
                    </div>
                    <div style={{ display: 'flex', gap: 8, paddingBottom: 20 }}>
                      <button onClick={activateToRcti} disabled={!offerAccepted || activating}
                        style={{ ...stepBtn, background: offerAccepted ? 'var(--hp-gold)' : 'var(--hp-surface)', color: offerAccepted ? 'var(--hp-black)' : 'var(--hp-dim)',
                          border: 'none', fontWeight: 800, opacity: activating ? 0.6 : 1, cursor: (!offerAccepted || activating) ? 'default' : 'pointer' }}>
                        <Zap size={13} /> {activating ? 'Activating…' : 'Activate to RCTI'}
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            )}
          </Card>

          {/* 2 ── Award per-parcel price */}
          <Card>
            <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 12, flexWrap: 'wrap' }}>
              <Title sub="Award floor is fixed by vehicle class. Dial the per-parcel unit rate, then model the upside of a faster pace.">2 · Pricing workbench</Title>
              <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                <button onClick={() => setShowReport(true)} title={`Generate a printable Route & Earnings Plan for ${driverRec?.name || driver}`}
                  style={{ ...stepBtn, background: 'var(--hp-gold)', color: 'var(--hp-black)', border: 'none', fontWeight: 800 }}><FileText size={14} /> Driver report</button>
                <button onClick={resetWorkbench} title="Reset the unit rate, target pace and improvement target back to defaults"
                  style={{ ...stepBtn, background: 'var(--hp-surface)' }}><RefreshCw size={13} /> Reset workbench</button>
              </div>
            </div>

            {/* Vehicle class → STATIC award floor (fixed by the Determination) */}
            <div style={{ display: 'flex', gap: 16, flexWrap: 'wrap', alignItems: 'flex-end', marginBottom: 16 }}>
              <div>
                <Label>Vehicle class (Determination)</Label>
                <select value={cfg.vehicleClass} onChange={e => setCfgKey('vehicleClass', e.target.value)} style={{ ...inputStyle, minWidth: 240 }}>
                  {VEHICLE_CLASSES.map(v => <option key={v.key} value={v.key}>{v.label}</option>)}
                </select>
              </div>
              <div>
                <Label>Minimum award $/hr — fixed</Label>
                <div style={{ ...inputStyle, width: 160, display: 'flex', alignItems: 'baseline', gap: 6, background: 'var(--hp-surface)', cursor: 'default' }}>
                  <span style={{ fontSize: 18, fontWeight: 800 }}>{money(pricing.awardHourly)}</span>
                  <span style={{ fontSize: 10, color: 'var(--hp-dim)', textTransform: 'uppercase', letterSpacing: '0.05em' }}>/hr · locked</span>
                </div>
              </div>
            </div>

            <div style={{ display: 'flex', gap: 8, alignItems: 'flex-start', padding: '10px 14px', background: 'color-mix(in srgb, var(--hp-gold) 10%, transparent)', border: '1px solid var(--hp-border)', borderRadius: 8, marginBottom: 18, fontSize: 12, color: 'var(--hp-dim)' }}>
              <Info size={15} style={{ flexShrink: 0, marginTop: 1, color: 'var(--hp-gold)' }} />
              <span>Award floor set by the vehicle class — <i>Transport Industry — Courier &amp; Taxi Truck Contract Determination</i> Minimum Safety Net Payment ($ per hour) effective <b>{RATES_EFFECTIVE}</b> (NSW IRC Gazette). It stays fixed as you dial the unit rate below.</span>
            </div>

            {/* Three bases — superseded (and visibly stood down) while payable time prices. */}
            <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap', opacity: ptOn ? 0.45 : 1 }}>
              {['median', 'stretch', 'expected'].map(key => {
                const b = pricing.bases[key];
                const active = cfg.basis === key && !ptOn;
                const usable = b.pph > 0;
                return (
                  <button key={key} onClick={() => usable && setCfgKey('basis', key)} disabled={!usable}
                    style={{ flex: '1 1 240px', minWidth: 230, textAlign: 'left', cursor: usable ? 'pointer' : 'not-allowed', opacity: usable ? 1 : 0.5,
                      background: active ? 'color-mix(in srgb, var(--hp-gold) 14%, var(--hp-bg))' : 'var(--hp-bg)',
                      border: `2px solid ${active ? 'var(--hp-gold)' : 'var(--hp-border)'}`, borderRadius: 10, padding: '14px 16px' }}>
                    <div style={{ fontSize: 11, fontWeight: 800, textTransform: 'uppercase', letterSpacing: '0.05em', color: active ? 'var(--hp-gold)' : 'var(--hp-dim)' }}>{b.label}</div>
                    <div style={{ fontSize: '1.7rem', fontWeight: 900, color: 'var(--hp-text)', margin: '6px 0 2px' }}>{usable ? money2(b.perParcel) : '—'}</div>
                    <div style={{ fontSize: 12, color: 'var(--hp-dim)' }}>per parcel · target {num(b.pph)} P/H</div>
                  </button>
                );
              })}
            </div>

            {ptOn && (
              <div style={{ marginTop: 10, fontSize: 12, fontWeight: 700, color: 'var(--hp-gold)' }}>
                ↓ Superseded — the payable-time model below is setting the price.
              </div>
            )}

            <div style={{ marginTop: 14, fontSize: 13, color: 'var(--hp-dim)', lineHeight: 1.6 }}>{pricing.chosen.note}</div>

            {/* Payable time — deem the depot leg, pay the on-road leg in full. Overrides
                every basis above when switched on. */}
            {payable && (
              <div style={{ marginTop: 18, padding: '16px 18px', borderRadius: 10,
                background: ptOn ? 'color-mix(in srgb, var(--hp-gold) 8%, var(--hp-bg))' : 'var(--hp-bg)',
                border: `${ptOn ? 2 : 1}px solid ${ptOn ? 'var(--hp-gold)' : 'var(--hp-border)'}` }}>

                <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 12, flexWrap: 'wrap' }}>
                  <div style={{ flex: '1 1 380px' }}>
                    <div style={{ fontSize: 14, fontWeight: 800, color: 'var(--hp-text)' }}>Payable time — depot allowance &amp; on-road pace</div>
                    <div style={{ fontSize: 12, color: 'var(--hp-dim)', marginTop: 2, maxWidth: 620, lineHeight: 1.55 }}>
                      Prices the two legs of the day separately. The depot leg is <b style={{ color: 'var(--hp-text)' }}>deemed</b> — parcels ÷ the standard below, so clocking on early buys nothing. The on-road leg is <b style={{ color: 'var(--hp-text)' }}>paid in full</b>, measured from the onboard summary capture to finish. Overrides the basis and the range.
                    </div>
                  </div>
                  <button onClick={() => setPtOn(v => !v)}
                    title={ptOn ? 'Stop pricing on payable time — go back to the basis above' : 'Price the rate on payable time'}
                    style={{ ...stepBtn, fontWeight: 900, padding: '10px 16px',
                      border: `2px solid ${ptOn ? 'var(--hp-gold)' : 'var(--hp-border)'}`,
                      background: ptOn ? 'var(--hp-gold)' : 'var(--hp-surface)',
                      color: ptOn ? 'var(--hp-black)' : 'var(--hp-dim)' }}>
                    <Zap size={14} /> {ptOn ? 'Pricing on payable time' : 'Use payable time'}
                  </button>
                </div>

                {/* What he actually does today — the evidence behind the conversation. */}
                {payable.measured ? (
                  <div style={{ display: 'flex', gap: 18, flexWrap: 'wrap', marginTop: 14, padding: '10px 14px', background: 'var(--hp-surface)', border: '1px solid var(--hp-border)', borderRadius: 8, fontSize: 12 }}>
                    <span style={{ color: 'var(--hp-dim)', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.05em', fontSize: 10.5 }}>Measured · {payable.measured.days} days</span>
                    <span style={{ color: 'var(--hp-dim)' }}>depot <b style={{ color: 'var(--hp-text)' }}>{fmtDur(payable.measured.indoorH)}</b> = <b style={{ color: payable.measured.indoorPace < ptIndoor ? 'var(--hp-red)' : 'var(--hp-green)' }}>{num(payable.measured.indoorPace, 0)} P/H</b></span>
                    <span style={{ color: 'var(--hp-dim)' }}>on-road <b style={{ color: 'var(--hp-text)' }}>{fmtDur(payable.measured.outdoorH)}</b> = <b style={{ color: 'var(--hp-text)' }}>{num(payable.measured.outdoorPace)} P/H</b></span>
                    <span style={{ color: 'var(--hp-dim)' }}>clocked <b style={{ color: 'var(--hp-text)' }}>{fmtDur(payable.measured.clockedH)}</b></span>
                    <span style={{ color: 'var(--hp-dim)' }}>{num(payable.measured.parcelsPerDay, 0)} parcels/day</span>
                  </div>
                ) : (
                  <div style={{ marginTop: 14, padding: '10px 14px', background: 'var(--hp-surface)', border: '1px solid var(--hp-border)', borderRadius: 8, fontSize: 12, color: 'var(--hp-dim)', display: 'flex', gap: 8, alignItems: 'flex-start' }}>
                    <AlertTriangle size={14} style={{ flexShrink: 0, marginTop: 1, color: 'var(--hp-gold)' }} />
                    <span>No onboard summary captures in this window, so the depot/on-road split can’t be measured — the on-road pace below has defaulted to his whole-shift median ({num(analysis.medianPPH)} P/H), which <b style={{ color: 'var(--hp-text)' }}>understates</b> it. Set it by hand, or widen the date range.</span>
                  </div>
                )}

                {/* The three controls */}
                <div style={{ display: 'flex', gap: 22, flexWrap: 'wrap', marginTop: 16 }}>
                  <Glider label="Depot sorting — allowance" value={payable.indoorPace} unit="parcels / hr"
                    min={40} max={200} step={5} onChange={setPtIndoor}
                    note={`${fmtDur(payable.parcelsPerDay / payable.indoorPace)} paid on ${num(payable.parcelsPerDay, 0)} parcels${payable.measured ? ` · he runs ${num(payable.measured.indoorPace, 0)}` : ''}`}
                    onReset={payable.indoorPace !== PT_INDOOR_STD ? () => setPtIndoor(PT_INDOOR_STD) : null} resetLabel={`Standard ${PT_INDOOR_STD}`} />

                  <Glider label="On-road delivery — pace" value={payable.outdoorPace} unit="parcels / hr"
                    min={5} max={40} step={0.5} onChange={setPtOutdoor} dp={1}
                    note={`${fmtDur(payable.parcelsPerDay / payable.outdoorPace)} on the road${payable.measured ? ` · he runs ${num(payable.measured.outdoorPace)}` : ''}`}
                    onReset={ptOutdoor != null ? () => setPtOutdoor(null) : null} resetLabel="Measured" />
                </div>

                {/* Difficult route — an explicit loading on top of the award-exact rate */}
                <div style={{ display: 'flex', gap: 16, flexWrap: 'wrap', alignItems: 'flex-end', marginTop: 16 }}>
                  <button onClick={() => setPtDifficult(v => !v)}
                    title="Add a loading for a difficult round — high-rise, walk-ups, low bulk, hand-to-hand"
                    style={{ ...stepBtn, fontWeight: 800, padding: '10px 14px',
                      border: `2px solid ${ptDifficult ? 'var(--hp-gold)' : 'var(--hp-border)'}`,
                      background: ptDifficult ? 'color-mix(in srgb, var(--hp-gold) 16%, var(--hp-bg))' : 'var(--hp-surface)',
                      color: ptDifficult ? 'var(--hp-gold)' : 'var(--hp-dim)' }}>
                    <AlertTriangle size={14} /> Difficult route{ptDifficult ? ` · +${num(payable.loadPct)}%` : ''}
                  </button>
                  {ptDifficult && (
                    <Glider label="Route loading" value={payable.loadPct} unit="%" min={0} max={25} step={0.5} onChange={setPtLoad} dp={1}
                      note={`${money2(payable.loading)} per parcel on top of the award-exact ${money2(payable.floorRate)}`} width={230} />
                  )}
                  <div style={{ flex: '1 1 260px', minWidth: 240, fontSize: 12, color: 'var(--hp-dim)', lineHeight: 1.55 }}>
                    Most of the difficulty is already priced: a hard round shows up as a <b style={{ color: 'var(--hp-text)' }}>slow on-road pace</b>, which lifts the rate on its own. Use the loading for what the pace can’t see — and as the margin that makes the offer worth signing.
                  </div>
                </div>

                {/* Live rate + what it means both ways */}
                {payable.ok && (
                  <div style={{ marginTop: 16, padding: '14px 16px', background: 'var(--hp-surface)', border: `1px solid ${ptOn ? 'var(--hp-gold)' : 'var(--hp-border)'}`, borderRadius: 10 }}>
                    <div style={{ display: 'flex', gap: 20, flexWrap: 'wrap', alignItems: 'baseline' }}>
                      <div>
                        <div style={{ fontSize: 10.5, fontWeight: 800, textTransform: 'uppercase', letterSpacing: '0.06em', color: 'var(--hp-dim)' }}>Rate at these settings</div>
                        <div style={{ fontSize: '2rem', fontWeight: 900, color: 'var(--hp-gold)', lineHeight: 1.15 }}>{money2(payable.rate)}</div>
                        <div style={{ fontSize: 12, color: 'var(--hp-dim)' }}>per parcel ex GST · {money2(payable.rate * 1.1)} inc</div>
                      </div>
                      <div style={{ fontSize: 12.5, color: 'var(--hp-dim)', lineHeight: 1.9 }}>
                        <b style={{ color: 'var(--hp-text)' }}>{money2(payable.depotLeg)}</b> depot
                        &nbsp;+&nbsp; <b style={{ color: 'var(--hp-text)' }}>{money2(payable.roadLeg)}</b> on-road
                        {payable.loadPct > 0 && <> &nbsp;+&nbsp; <b style={{ color: 'var(--hp-gold)' }}>{money2(payable.loading)}</b> loading</>}
                        <div style={{ fontSize: 11.5 }}>= award-exact {money2(payable.floorRate)}{payable.loadPct > 0 ? ` + ${num(payable.loadPct)}%` : ''} · blended {num(payable.blendedPace)} P/H</div>
                      </div>
                    </div>

                    <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', marginTop: 14 }}>
                      <Stat label="Payable / day" value={fmtDur(payable.payableH)} sub={`${money(payable.dayValue)} at ${num(payable.parcelsPerDay, 0)} parcels`} />
                      <Stat label="$/hr at the standard" value={money(payable.hourlyAtStandard)}
                        tone={payable.hourlyAtStandard >= payable.award ? 'var(--hp-green)' : 'var(--hp-red)'}
                        sub={`${payable.abovePct >= 0 ? '+' : '−'}${num(Math.abs(payable.abovePct))}% vs ${money(payable.award)} floor`} />
                      {payable.measured && (
                        <Stat label="$/hr if he never tightens" value={money(payable.hourlyAtActual)}
                          tone={payable.hourlyAtActual >= payable.award ? 'var(--hp-green)' : 'var(--hp-red)'}
                          sub={`on his current ${fmtDur(payable.measured.clockedH)} clock`} />
                      )}
                      <Stat label="Safety net trips above" value={`${num(payable.levyTripH)} h`}
                        tone={payable.measured && payable.levyTripH <= payable.measured.clockedH ? 'var(--hp-red)' : undefined}
                        sub="clocked hours/day — GCCD levy tops up" />
                    </div>

                    {payable.measured && payable.levyTripH <= payable.measured.clockedH && (
                      <div style={{ marginTop: 12, padding: '10px 14px', background: 'color-mix(in srgb, var(--hp-red) 10%, transparent)', border: '1px solid var(--hp-red)', borderRadius: 8, fontSize: 12.5, color: 'var(--hp-text)', display: 'flex', gap: 8, alignItems: 'flex-start' }}>
                        <AlertTriangle size={15} style={{ flexShrink: 0, marginTop: 1, color: 'var(--hp-red)' }} />
                        <span>At this rate the levy trips at <b>{num(payable.levyTripH)} h</b>, under the <b>{fmtDur(payable.measured.clockedH)}</b> he clocks today — so until he actually tightens up he lands on the floor and the top-up fires most days. That’s the incentive working, but the pay rise is <b>earned, not given</b>: say so when you present it.</span>
                      </div>
                    )}

                    {payable.measured && payable.savedH > 0.01 && (
                      <div style={{ marginTop: 10, fontSize: 12.5, color: 'var(--hp-dim)', lineHeight: 1.6 }}>
                        The allowance removes <b style={{ color: 'var(--hp-text)' }}>{fmtDur(payable.savedH)} a day</b> of depot time you pay for today — {money(payable.savedH * payable.award * (parseFloat(cfg.workingDays) || 0))} a year at the {money(payable.award)} floor.
                      </div>
                    )}
                  </div>
                )}
              </div>
            )}

            {/* Speed bonus — the only lever that turns speed into money on a per-parcel deal */}
            {speedBonus && payable && (
              <div style={{ marginTop: 18, padding: '16px 18px', borderRadius: 10,
                background: bonusOn ? 'color-mix(in srgb, var(--hp-green) 7%, var(--hp-bg))' : 'var(--hp-bg)',
                border: `${bonusOn ? 2 : 1}px solid ${bonusOn ? 'var(--hp-green)' : 'var(--hp-border)'}` }}>

                <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 12, flexWrap: 'wrap' }}>
                  <div style={{ flex: '1 1 400px' }}>
                    <div style={{ fontSize: 14, fontWeight: 800, color: 'var(--hp-text)' }}>Speed bonus — a higher rate for hitting the standard</div>
                    <div style={{ fontSize: 12, color: 'var(--hp-dim)', marginTop: 2, maxWidth: 640, lineHeight: 1.55 }}>
                      A flat per-parcel rate <b style={{ color: 'var(--hp-text)' }}>cannot</b> reward speed — AP sets the parcel count, so going faster just shortens his day. A second, higher rate for the whole period is what makes finishing early worth money. Scored on the <b style={{ color: 'var(--hp-text)' }}>onboard capture</b>, never on his own recorded hours.
                    </div>
                  </div>
                  <button onClick={() => setBonusOn(v => !v)}
                    style={{ ...stepBtn, fontWeight: 900, padding: '10px 16px',
                      border: `2px solid ${bonusOn ? 'var(--hp-green)' : 'var(--hp-border)'}`,
                      background: bonusOn ? 'var(--hp-green)' : 'var(--hp-surface)',
                      color: bonusOn ? '#fff' : 'var(--hp-dim)' }}>
                    <TrendingUp size={14} /> {bonusOn ? 'Bonus in the offer' : 'Add a speed bonus'}
                  </button>
                </div>

                {bonusOn && (<>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginTop: 14, flexWrap: 'wrap' }}>
                    <span style={{ fontSize: 11, fontWeight: 700, color: 'var(--hp-dim)', textTransform: 'uppercase', letterSpacing: '0.06em' }}>Score him on</span>
                    {[{ k: 'road', l: 'On the road' }, { k: 'depot', l: 'Out of the depot' }].map(o => {
                      const on = speedBonus.basis === o.k;
                      return (
                        <button key={o.k} onClick={() => setBonusBasis(o.k)}
                          style={{ ...stepBtn, fontWeight: 800, border: `2px solid ${on ? 'var(--hp-green)' : 'var(--hp-border)'}`,
                            background: on ? 'color-mix(in srgb, var(--hp-green) 14%, var(--hp-bg))' : 'var(--hp-surface)',
                            color: on ? 'var(--hp-green)' : 'var(--hp-dim)' }}>{o.l}</button>
                      );
                    })}
                    <span style={{ fontSize: 11.5, color: 'var(--hp-dim)', flex: '1 1 260px' }}>
                      {speedBonus.basis === 'road'
                        ? 'His own driving — the leg with the most room and the most money in it.'
                        : 'Departure time — check the scorecard: the sort may not release freight early enough for this to be winnable.'}
                    </span>
                  </div>

                  <div style={{ display: 'flex', gap: 16, flexWrap: 'wrap', alignItems: 'flex-end', marginTop: 14 }}>
                    <div>
                      <Label>On-target rate (per parcel, ex GST)</Label>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                        {[-0.05, -0.01].map(st => <button key={st} onClick={() => setBonusRate(Math.max(speedBonus.base, +(speedBonus.onTarget + st).toFixed(3)))} style={stepBtn}>{money2(st).replace('-', '−')}</button>)}
                        <input type="number" step="0.01" min="0" value={speedBonus.onTarget.toFixed(3)}
                          onChange={e => setBonusRate(Math.max(0, parseFloat(e.target.value) || 0))}
                          style={{ ...inputStyle, width: 118, textAlign: 'center', fontSize: 18, fontWeight: 900, color: 'var(--hp-green)' }} />
                        {[0.01, 0.05].map(st => <button key={st} onClick={() => setBonusRate(+(speedBonus.onTarget + st).toFixed(3))} style={stepBtn}>+{money2(st)}</button>)}
                      </div>
                      <div style={{ fontSize: 11.5, color: 'var(--hp-dim)', marginTop: 4 }}>base {money2(speedBonus.base)} · <b style={{ color: 'var(--hp-green)' }}>+{money2(speedBonus.perParcel)}</b> a parcel on target</div>
                    </div>
                    {speedBonus.basis === 'road' ? (
                      <Glider label="Target on-road pace" value={speedBonus.roadTarget} unit="parcels / hr"
                        min={8} max={35} step={0.5} dp={1} onChange={setBonusRoadPace} width={230}
                        note={`he averages ${num(speedBonus.measuredRoad)} · fleet median 22.6`}
                        onReset={bonusRoadPace != null ? () => setBonusRoadPace(null) : null} resetLabel="Measured +1" />
                    ) : (
                      <div>
                        <Label>Nominated start</Label>
                        <input type="time" value={speedBonus.startStr} onChange={e => setBonusStart(e.target.value)}
                          style={{ ...inputStyle, width: 116, textAlign: 'center', fontWeight: 800 }} />
                        <div style={{ fontSize: 11.5, color: 'var(--hp-dim)', marginTop: 4 }}>{driverRec?.indoorStart ? `his record says ${driverRec.indoorStart}` : 'not set on his record'}</div>
                      </div>
                    )}
                    <Glider label="Days that must hit" value={bonusHitPct} unit="% of the period" min={50} max={100} step={5}
                      onChange={setBonusHitPct} width={210}
                      note={`${speedBonus.need} of ${speedBonus.days || PERIOD_DAYS} days`} />
                  </div>

                  {/* An on-target rate at or under the base makes the bonus worth $0 and
                      drops it out of the offer silently — easy to do with the steppers, and
                      invisible until someone notices the section missing from the PDF. */}
                  {speedBonus.perParcel <= 0 && (
                    <div style={{ marginTop: 12, padding: '10px 14px', background: 'color-mix(in srgb, var(--hp-red) 9%, transparent)', border: '1px solid var(--hp-red)', borderRadius: 8, fontSize: 12.5, color: 'var(--hp-text)', display: 'flex', gap: 8, alignItems: 'flex-start' }}>
                      <AlertTriangle size={15} style={{ flexShrink: 0, marginTop: 1, color: 'var(--hp-red)' }} />
                      <span>The on-target rate ({money2(speedBonus.onTarget)}) is not above the base ({money2(speedBonus.base)}), so the bonus is worth <b>nothing</b> and will not appear in the offer at all. Raise it, or switch the bonus off.</span>
                    </div>
                  )}

                  {/* The rule, in the words it will be written in */}
                  <div style={{ marginTop: 14, padding: '11px 14px', background: 'var(--hp-surface)', border: '1px solid var(--hp-border)', borderRadius: 8, fontSize: 12.5, lineHeight: 1.65, color: 'var(--hp-text)' }}>
                    {speedBonus.basis === 'road' ? (<>
                      <b>The rule:</b> deliver <b style={{ color: 'var(--hp-green)' }}>{num(speedBonus.roadTarget)} parcels an hour</b> on the road — from signing off the scanner to finishing.
                      On a typical {num(speedBonus.parcelsPerDay, 0)}-parcel day that is a <b>{fmtDur(speedBonus.parcelsPerDay / speedBonus.roadTarget)}</b> round.
                    </>) : (<>
                      <b>The rule:</b> scanner signed off and captured in the portal by <b>{speedBonus.startStr} + (parcels ÷ {num(speedBonus.indoorPace, 0)})</b>.
                      On a typical {num(speedBonus.parcelsPerDay, 0)}-parcel day that is <b style={{ color: 'var(--hp-green)' }}>{fromMin(speedBonus.typicalTargetMin)}</b>.
                    </>)}
                    {' '}Hit it on <b>{num(bonusHitPct, 0)}%</b> of the period’s days and every parcel that period pays <b style={{ color: 'var(--hp-green)' }}>{money2(speedBonus.onTarget)}</b> instead of {money2(speedBonus.base)}.
                    <div style={{ fontSize: 11.5, color: 'var(--hp-dim)', marginTop: 5 }}>Both ends are captured for you — the scanner photo is timestamped by the server and the finish is geofenced — so none of it rests on what he writes down. Days AP’s freight ran late should be excluded by agreement.</div>
                  </div>

                  <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', marginTop: 14 }}>
                    <Stat label="Bonus / period" value={money(speedBonus.perPeriod)} tone="var(--hp-green)" sub={`${PERIOD_DAYS} days × ${num(speedBonus.parcelsPerDay, 0)} parcels`} />
                    <Stat label="Bonus / year" value={money(speedBonus.perYear)} tone="var(--hp-green)" sub={`${num(cfg.workingDays, 0)} days`} />
                    <Stat label="On target / year" value={money(speedBonus.onTargetYear)} sub={`base ${money(speedBonus.baseYear)}`} />
                    <Stat label="On his record" value={`${speedBonus.hits} / ${speedBonus.days}`}
                      tone={speedBonus.earned ? 'var(--hp-green)' : 'var(--hp-red)'}
                      sub={`${num(speedBonus.hitRate, 0)}% — needs ${num(bonusHitPct, 0)}%`} />
                  </div>

                  {/* Day-by-day scorecard — what the period-end check will produce */}
                  {speedBonus.days > 0 ? (
                    <div style={{ marginTop: 14 }}>
                      <div style={{ fontSize: 12, fontWeight: 800, color: 'var(--hp-dim)', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: 6 }}>
                        Scorecard — every captured day in this window
                      </div>
                      <div style={{ overflowX: 'auto' }}>
                        <table style={{ width: '100%', borderCollapse: 'collapse', minWidth: 520, fontSize: 12.5 }}>
                          <thead>
                            <tr style={{ textAlign: 'left', fontSize: 10.5, textTransform: 'uppercase', letterSpacing: '0.05em', color: 'var(--hp-dim)' }}>
                              {(speedBonus.basis === 'road'
                                ? ['Date', 'Parcels', 'On the road', 'His pace', 'Target', 'Result']
                                : ['Date', 'Parcels', 'Allowed', 'Target', 'Captured', 'Result']).map((h, i) => (
                                <th key={h} style={{ padding: '7px 9px', borderBottom: '1px solid var(--hp-gold)', textAlign: i > 0 ? 'right' : 'left' }}>{h}</th>
                              ))}
                            </tr>
                          </thead>
                          <tbody>
                            {speedBonus.rows.map(r => (
                              <tr key={r.date} style={{ background: r.hit ? 'color-mix(in srgb, var(--hp-green) 8%, transparent)' : 'transparent' }}>
                                <td style={{ padding: '6px 9px', borderBottom: '1px solid var(--hp-border)' }}>{r.date}</td>
                                <td style={{ padding: '6px 9px', borderBottom: '1px solid var(--hp-border)', textAlign: 'right' }}>{num(r.parcels, 0)}</td>
                                {speedBonus.basis === 'road' ? (<>
                                  <td style={{ padding: '6px 9px', borderBottom: '1px solid var(--hp-border)', textAlign: 'right', color: 'var(--hp-dim)' }}>{fmtDur(r.roadH)}</td>
                                  <td style={{ padding: '6px 9px', borderBottom: '1px solid var(--hp-border)', textAlign: 'right', fontWeight: 700 }}>{num(r.pace)}</td>
                                  <td style={{ padding: '6px 9px', borderBottom: '1px solid var(--hp-border)', textAlign: 'right' }}>{num(speedBonus.roadTarget)}</td>
                                  <td style={{ padding: '6px 9px', borderBottom: '1px solid var(--hp-border)', textAlign: 'right', fontWeight: 800, color: r.hit ? 'var(--hp-green)' : 'var(--hp-red)' }}>
                                    {r.hit ? `+${num(r.by)} over` : `${num(Math.abs(r.by))} short`}
                                  </td>
                                </>) : (<>
                                  <td style={{ padding: '6px 9px', borderBottom: '1px solid var(--hp-border)', textAlign: 'right', color: 'var(--hp-dim)' }}>{fmtDur(r.allowMin / 60)}</td>
                                  <td style={{ padding: '6px 9px', borderBottom: '1px solid var(--hp-border)', textAlign: 'right' }}>{fromMin(r.targetMin)}</td>
                                  <td style={{ padding: '6px 9px', borderBottom: '1px solid var(--hp-border)', textAlign: 'right', fontWeight: 700 }}>{fromMin(r.capMin)}</td>
                                  <td style={{ padding: '6px 9px', borderBottom: '1px solid var(--hp-border)', textAlign: 'right', fontWeight: 800, color: r.hit ? 'var(--hp-green)' : 'var(--hp-red)' }}>
                                    {r.hit ? `${fmtDur(Math.abs(r.byMin) / 60)} early` : `${fmtDur(r.byMin / 60)} late`}
                                  </td>
                                </>)}
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                      <div style={{ marginTop: 10, fontSize: 12.5, color: 'var(--hp-dim)', lineHeight: 1.6 }}>
                        On this window he would have {speedBonus.earned ? <b style={{ color: 'var(--hp-green)' }}>EARNED</b> : <b style={{ color: 'var(--hp-red)' }}>MISSED</b>} the bonus —
                        {' '}{speedBonus.hits} of {speedBonus.days} days ({num(speedBonus.hitRate, 0)}%), needing {speedBonus.need}. His median day lands
                        {' '}{speedBonus.basis === 'road'
                          ? <><b style={{ color: 'var(--hp-text)' }}>{num(Math.abs(speedBonus.medianByMin))} {speedBonus.medianByMin >= 0 ? 'over' : 'short'}</b>, his best <b style={{ color: 'var(--hp-text)' }}>{num(Math.abs(speedBonus.bestByMin))} {speedBonus.bestByMin >= 0 ? 'over' : 'short'}</b></>
                          : <><b style={{ color: 'var(--hp-text)' }}>{fmtDur(Math.abs(speedBonus.medianByMin) / 60)} {speedBonus.medianByMin > 0 ? 'late' : 'early'}</b>, his best <b style={{ color: 'var(--hp-text)' }}>{fmtDur(Math.abs(speedBonus.bestByMin) / 60)} {speedBonus.bestByMin > 0 ? 'late' : 'early'}</b></>}.
                        A target he has never once hit is not an incentive — if the whole column is red, move the nominated start or the standard until it is a stretch rather than a wall.
                      </div>
                    </div>
                  ) : (
                    <div style={{ marginTop: 12, fontSize: 12.5, color: 'var(--hp-dim)' }}>No onboard captures in this window, so there is nothing to score him against yet.</div>
                  )}
                </>)}
              </div>
            )}

            {/* Pace allowance — uplift a measured pace we don't trust to an expected range,
                then toggle which end of the range prices the rate and compare the impact. */}
            <div style={{ marginTop: 18, padding: '16px 18px', background: 'var(--hp-bg)', border: '1px solid var(--hp-border)', borderRadius: 10 }}>
              <div style={{ display: 'flex', alignItems: 'flex-end', justifyContent: 'space-between', gap: 12, flexWrap: 'wrap' }}>
                <div>
                  <div style={{ fontSize: 14, fontWeight: 800, color: 'var(--hp-text)' }}>Pace allowance — price on an expected range</div>
                  <div style={{ fontSize: 12, color: 'var(--hp-dim)', marginTop: 2, maxWidth: 560, lineHeight: 1.55 }}>
                    Measured pace is <b style={{ color: 'var(--hp-text)' }}>{num(analysis.medianPPH)} P/H</b>. If that understates the round (e.g. the previous driver ran slow), set the expected range and toggle which end sets the rate. Overrides the basis above. The IR safety net still guarantees the award floor if the round runs slower than expected.
                  </div>
                </div>
                <div style={{ display: 'flex', alignItems: 'flex-end', gap: 12, flexWrap: 'wrap' }}>
                  <div>
                    <Label>Expected P/H — low</Label>
                    <input type="number" step="0.5" min="0" value={paceLow} placeholder={num(analysis.medianPPH)}
                      onChange={e => setPaceLow(e.target.value)} style={{ ...inputStyle, width: 92, textAlign: 'center', fontWeight: 800 }} />
                  </div>
                  <div>
                    <Label>Expected P/H — high</Label>
                    <input type="number" step="0.5" min="0" value={paceHigh} placeholder={num(analysis.medianPPH)}
                      onChange={e => setPaceHigh(e.target.value)} style={{ ...inputStyle, width: 92, textAlign: 'center', fontWeight: 800 }} />
                  </div>
                  {paceModel?.hasRange && (
                    <button onClick={() => { setPaceLow(''); setPaceHigh(''); }} title="Clear the range and go back to the basis above" style={{ ...stepBtn, background: 'var(--hp-surface)' }}><RefreshCw size={13} /> Clear</button>
                  )}
                </div>
              </div>

              {paceModel?.hasRange && (
                <>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginTop: 16, flexWrap: 'wrap' }}>
                    <span style={{ fontSize: 12, fontWeight: 700, color: 'var(--hp-dim)', textTransform: 'uppercase', letterSpacing: '0.05em' }}>Price the rate on</span>
                    {[{ k: 'low', l: `Low · ${num(paceModel.low)}` }, { k: 'mid', l: `Mid · ${num(paceModel.mid)}` }, { k: 'high', l: `High · ${num(paceModel.high)}` }].map(o => {
                      const on = pricePoint === o.k;
                      return (
                        <button key={o.k} onClick={() => setPricePoint(o.k)}
                          style={{ ...stepBtn, fontWeight: 800, border: `2px solid ${on ? 'var(--hp-gold)' : 'var(--hp-border)'}`, background: on ? 'color-mix(in srgb, var(--hp-gold) 16%, var(--hp-bg))' : 'var(--hp-surface)', color: on ? 'var(--hp-gold)' : 'var(--hp-dim)' }}>{o.l} P/H</button>
                      );
                    })}
                    <span style={{ fontSize: 12, color: 'var(--hp-dim)' }}>uplift <b style={{ color: paceModel.upliftPct >= 0 ? 'var(--hp-green)' : 'var(--hp-red)' }}>{paceModel.upliftPct >= 0 ? '+' : '−'}{num(Math.abs(paceModel.upliftPct))}%</b> vs measured</span>
                  </div>

                  {/* Impact of each price point — the higher the priced pace, the leaner the
                      rate and the more the safety net has to catch if he runs at the measured pace. */}
                  {(() => {
                    const award = pricing.awardHourly, measured = paceModel.measured;
                    const annParcels = projection ? projection.annualParcels : 0;
                    const pts = [{ k: 'low', pace: paceModel.low }, { k: 'mid', pace: paceModel.mid }, { k: 'high', pace: paceModel.high }];
                    const cell = { padding: '9px 10px', fontSize: 12.5, borderBottom: '1px solid var(--hp-border)' };
                    return (
                      <div style={{ marginTop: 14, overflowX: 'auto' }}>
                        <table style={{ width: '100%', borderCollapse: 'collapse', minWidth: 520 }}>
                          <thead>
                            <tr style={{ textAlign: 'left', fontSize: 10.5, textTransform: 'uppercase', letterSpacing: '0.05em', color: 'var(--hp-dim)' }}>
                              <th style={{ ...cell, borderBottom: '1px solid var(--hp-gold)' }}>Price point</th>
                              <th style={{ ...cell, borderBottom: '1px solid var(--hp-gold)', textAlign: 'right' }}>Rate /parcel (ex)</th>
                              <th style={{ ...cell, borderBottom: '1px solid var(--hp-gold)', textAlign: 'right' }}>$/hr if he runs {num(measured)} P/H</th>
                              <th style={{ ...cell, borderBottom: '1px solid var(--hp-gold)', textAlign: 'right' }}>Safety-net top-up /hr</th>
                              <th style={{ ...cell, borderBottom: '1px solid var(--hp-gold)', textAlign: 'right' }}>Annual value (ex)</th>
                            </tr>
                          </thead>
                          <tbody>
                            {pts.map(pt => {
                              const rate = pt.pace > 0 ? award / pt.pace : 0;
                              const hourlyAtMeasured = rate * measured;
                              const gap = Math.max(0, award - hourlyAtMeasured);
                              const annual = rate * annParcels;
                              const on = pricePoint === pt.k;
                              return (
                                <tr key={pt.k} onClick={() => setPricePoint(pt.k)} style={{ cursor: 'pointer', background: on ? 'color-mix(in srgb, var(--hp-gold) 12%, transparent)' : 'transparent' }}>
                                  <td style={{ ...cell, fontWeight: on ? 800 : 600, color: on ? 'var(--hp-gold)' : 'var(--hp-text)' }}>{pt.k[0].toUpperCase() + pt.k.slice(1)} · {num(pt.pace)} P/H{on ? '  ◂ priced' : ''}</td>
                                  <td style={{ ...cell, textAlign: 'right', fontWeight: 800 }}>{money2(rate)}</td>
                                  <td style={{ ...cell, textAlign: 'right', color: hourlyAtMeasured >= award ? 'var(--hp-green)' : 'var(--hp-red)' }}>{money(hourlyAtMeasured)}</td>
                                  <td style={{ ...cell, textAlign: 'right', color: gap > 0.005 ? 'var(--hp-red)' : 'var(--hp-dim)' }}>{gap > 0.005 ? `+${money(gap)}` : '—'}</td>
                                  <td style={{ ...cell, textAlign: 'right' }}>{annParcels > 0 ? money(annual) : '—'}</td>
                                </tr>
                              );
                            })}
                          </tbody>
                        </table>
                        <div style={{ marginTop: 10, fontSize: 12, color: 'var(--hp-dim)', lineHeight: 1.6 }}>
                          “$/hr if he runs {num(measured)} P/H” is the honest worst case — his pay if he turns out exactly as slow as the record. Where that dips under the {money(award)} award floor, the <b style={{ color: 'var(--hp-text)' }}>safety-net top-up</b> is what you’d owe to lift him back to the minimum. Pricing on the <b style={{ color: 'var(--hp-text)' }}>Low</b> end keeps him award-clear across the whole expected range; the <b style={{ color: 'var(--hp-text)' }}>High</b> end is leaner but leans on the top-up at the slow end.
                        </div>
                      </div>
                    );
                  })()}
                </>
              )}
            </div>

            {/* Dynamic unit-rate control — dial the per-parcel price above/below the award minimum */}
            <div style={{ marginTop: 16, padding: '16px 18px', background: 'var(--hp-bg)', border: '1px solid var(--hp-border)', borderRadius: 10, display: 'flex', alignItems: 'flex-end', justifyContent: 'space-between', gap: 16, flexWrap: 'wrap' }}>
              <div>
                <Label>Unit rate (per parcel) — dial above the award minimum</Label>
                <div style={{ display: 'flex', alignItems: 'center', gap: 6, flexWrap: 'wrap' }}>
                  {[-0.05, -0.01].map(s => <button key={s} onClick={() => setUnitOverride(Math.max(0, +(pricing.perParcel + s).toFixed(3)))} style={stepBtn}>{money2(s).replace('-', '−')}</button>)}
                  <input type="number" step="0.01" min="0" value={pricing.perParcel.toFixed(3)}
                    onChange={e => setUnitOverride(Math.max(0, parseFloat(e.target.value) || 0))}
                    style={{ ...inputStyle, width: 130, textAlign: 'center', fontSize: 20, fontWeight: 900, color: 'var(--hp-gold)' }} />
                  {[0.01, 0.05].map(s => <button key={s} onClick={() => setUnitOverride(Math.max(0, +(pricing.perParcel + s).toFixed(3)))} style={stepBtn}>+{money2(s)}</button>)}
                  <button onClick={() => setUnitOverride(null)} disabled={Math.abs(pricing.uplift) < 0.0005}
                    title="Reset the unit rate to the award minimum for this pace"
                    style={{ ...stepBtn, marginLeft: 6, opacity: Math.abs(pricing.uplift) < 0.0005 ? 0.5 : 1 }}><RefreshCw size={13} /> Award min</button>
                </div>
              </div>
              <div style={{ textAlign: 'right' }}>
                <div style={{ fontSize: 11, color: 'var(--hp-dim)', textTransform: 'uppercase', letterSpacing: '0.05em' }}>Above award minimum</div>
                <div style={{ fontSize: '1.5rem', fontWeight: 900, color: pricing.uplift >= -0.0005 ? 'var(--hp-green)' : 'var(--hp-red)' }}>{pricing.uplift >= -0.0005 ? '+' : '−'}{money2(Math.abs(pricing.uplift))}<span style={{ fontSize: 12, color: 'var(--hp-dim)', fontWeight: 600 }}> /parcel</span></div>
                <div style={{ fontSize: 11, color: 'var(--hp-dim)' }}>award min {money2(pricing.awardMinUnit)} · target {num(pricing.chosen.pph)} P/H</div>
              </div>
            </div>

            {/* Chosen headline */}
            <div style={{ marginTop: 16, display: 'flex', gap: 12, flexWrap: 'wrap' }}>
              <Stat label="Quote price (ex GST)" value={money2(pricing.perParcel)} tone="var(--hp-gold)" sub={`${pricing.uplift >= -0.0005 ? '+' : '−'}${money2(Math.abs(pricing.uplift))} vs award min`} />
              <Stat label="Quote price (GST inc)" value={money2(pricing.perParcelGst)} tone="var(--hp-gold)" sub="ex GST + 10%" />
              <Stat label="His $/hr at current pace" value={money(pricing.effectiveHourlyNow)}
                tone={pricing.effectiveHourlyNow >= pricing.awardHourly ? 'var(--hp-green)' : 'var(--hp-red)'}
                sub={`at ${num(analysis.medianPPH)} P/H median`} />
              <Stat label="More / less per hour vs award" value={`${pricing.awardDelta >= 0 ? '+' : '−'}${money(Math.abs(pricing.awardDelta))}/h`}
                tone={pricing.awardDelta >= 0 ? 'var(--hp-green)' : 'var(--hp-red)'}
                sub={pricing.awardDelta >= 0 ? 'above award at current pace' : 'below award — lift pace or rate'} />
              <Stat label="Award floor" value={`${money(pricing.awardHourly)}/h`} sub="Determination minimum · fixed" />
            </div>

            {/* Improvement workbench — model a faster pace at the fixed unit rate */}
            {improvement && (
              <div style={{ marginTop: 20, paddingTop: 18, borderTop: '1px dashed var(--hp-border)' }}>
                <div style={{ display: 'flex', alignItems: 'flex-end', justifyContent: 'space-between', gap: 12, flexWrap: 'wrap', marginBottom: 14 }}>
                  <div>
                    <div style={{ fontSize: 14, fontWeight: 800, color: 'var(--hp-text)' }}>Improve parcels-per-hour</div>
                    <div style={{ fontSize: 12, color: 'var(--hp-dim)', marginTop: 2 }}>Upside of a faster pace at the same {money2(improvement.unit)}/parcel — he runs {num(improvement.cur)} P/H now.</div>
                  </div>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 6, flexWrap: 'wrap' }}>
                    {[improvement.dflt, improvement.dflt + 1, improvement.dflt + 2].map(t => {
                      const on = Math.abs(improvement.tgt - t) < 0.001;
                      return <button key={t} onClick={() => setImproveTarget(t)} style={{ ...stepBtn, background: on ? 'color-mix(in srgb, var(--hp-gold) 20%, var(--hp-bg))' : 'var(--hp-surface)', borderColor: on ? 'var(--hp-gold)' : 'var(--hp-border)' }}>{t} P/H</button>;
                    })}
                    <button onClick={() => setImproveTarget(Math.max(0, +(improvement.tgt - 0.5).toFixed(1)))} style={stepBtn}>−0.5</button>
                    <input type="number" step="0.1" min="0" value={improvement.tgt}
                      onChange={e => setImproveTarget(Math.max(0, parseFloat(e.target.value) || 0))}
                      style={{ ...inputStyle, width: 78, textAlign: 'center', fontWeight: 800 }} />
                    <button onClick={() => setImproveTarget(+(improvement.tgt + 0.5).toFixed(1))} style={stepBtn}>+0.5</button>
                    <button onClick={() => setImproveTarget(null)} disabled={improveTarget == null} title="Reset target to the default" style={{ ...stepBtn, opacity: improveTarget == null ? 0.5 : 1 }}><RefreshCw size={12} /></button>
                  </div>
                </div>
                <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap' }}>
                  <Stat label="Target pace" value={`${num(improvement.tgt)} P/H`} tone="var(--hp-gold)" sub={`${improvement.tgt >= improvement.cur ? '+' : '−'}${num(Math.abs(improvement.tgt - improvement.cur))} vs ${num(improvement.cur)} now`} />
                  <Stat label="His $/hr at target" value={money(improvement.hourlyTgt)} tone="var(--hp-green)" sub={`was ${money(improvement.hourlyCur)}`} />
                  <Stat label="Extra per hour" value={`${improvement.extraHourly >= 0 ? '+' : '−'}${money(Math.abs(improvement.extraHourly))}/h`} tone={improvement.extraHourly >= 0 ? 'var(--hp-green)' : 'var(--hp-red)'} sub={`${num(improvement.fasterPct)}% faster`} />
                  <Stat label="vs award at target" value={`${improvement.vsAwardTgt >= 0 ? '+' : '−'}${money(Math.abs(improvement.vsAwardTgt))}/h`} tone={improvement.vsAwardTgt >= 0 ? 'var(--hp-green)' : 'var(--hp-red)'} sub="above award floor" />
                  <Stat label="Extra income / yr" value={`${improvement.extraAnnual >= 0 ? '' : '−'}${money(Math.abs(improvement.extraAnnual))}`} tone={improvement.extraAnnual >= 0 ? 'var(--hp-green)' : 'var(--hp-red)'} sub={`same hours · ${num(improvement.hoursYr, 0)} h/yr`} />
                </div>

                {/* …or, if he gets no extra parcels — same load, cleared faster = time saved */}
                <div style={{ fontSize: 11, fontWeight: 800, textTransform: 'uppercase', letterSpacing: '0.05em', color: 'var(--hp-dim)', margin: '18px 0 8px' }}>Or — no extra parcels, same load cleared faster = time saved</div>
                <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap' }}>
                  <Stat label="Per day" value={fmtDur(improvement.saved.day)} tone={improvement.saved.day >= 0 ? 'var(--hp-green)' : 'var(--hp-red)'} sub={`${fmtDur(improvement.curDayH)} → ${fmtDur(improvement.tgtDayH)}`} />
                  <Stat label="Per week" value={fmtDur(improvement.saved.week)} tone={improvement.saved.week >= 0 ? 'var(--hp-green)' : 'var(--hp-red)'} sub={`${num(improvement.wd / 52)} days`} />
                  <Stat label="Per month" value={fmtDur(improvement.saved.month)} tone={improvement.saved.month >= 0 ? 'var(--hp-green)' : 'var(--hp-red)'} sub={`${num(improvement.wd / 12, 0)} days`} />
                  <Stat label="Per 4 weeks" value={fmtDur(improvement.saved.fourWk)} tone={improvement.saved.fourWk >= 0 ? 'var(--hp-green)' : 'var(--hp-red)'} sub={`${num(improvement.wd / 13, 0)} days`} />
                  <Stat label="Per year" value={fmtDur(improvement.saved.year)} tone={improvement.saved.year >= 0 ? 'var(--hp-green)' : 'var(--hp-red)'} sub={`${num(improvement.wd, 0)} days`} />
                </div>

                <div style={{ marginTop: 12, fontSize: 12.5, color: 'var(--hp-dim)', lineHeight: 1.6 }}>
                  At {money2(improvement.unit)}/parcel, lifting from {num(improvement.cur)} to <b style={{ color: 'var(--hp-text)' }}>{num(improvement.tgt)} P/H</b> is worth <b style={{ color: 'var(--hp-green)' }}>{improvement.extraHourly >= 0 ? '+' : '−'}{money(Math.abs(improvement.extraHourly))}/h</b> (≈ <b style={{ color: 'var(--hp-green)' }}>{money(Math.abs(improvement.extraAnnual))}</b>/yr) if he takes on more parcels — <b>or</b>, on the same {num(improvement.avgDaily, 0)} parcels a day, he clears the round <b style={{ color: 'var(--hp-green)' }}>{fmtDur(improvement.saved.day)}</b> sooner each day ({fmtDur(improvement.saved.year)} a year). Either way, the rate never drops.
                </div>
              </div>
            )}
          </Card>

          {/* 3 ── Annual projection */}
          <Card>
            <Title sub="Projected annual volume &amp; earnings at the quote price, with a Christmas peak uplift.">3 · Annual earnings projection</Title>
            <div style={{ display: 'flex', gap: 16, flexWrap: 'wrap', alignItems: 'flex-end', marginBottom: 16 }}>
              <div>
                <Label>Working days / yr</Label>
                <input type="number" min="0" value={cfg.workingDays} onChange={e => setCfgKey('workingDays', e.target.value)} style={{ ...inputStyle, width: 110 }} />
              </div>
              <div>
                <Label>Peak weeks (Nov–Dec)</Label>
                <input type="number" min="0" value={cfg.peakWeeks} onChange={e => setCfgKey('peakWeeks', e.target.value)} style={{ ...inputStyle, width: 110 }} />
              </div>
              <div>
                <Label>Peak volume ×</Label>
                <input type="number" step="0.05" min="1" value={cfg.peakMultiplier} onChange={e => setCfgKey('peakMultiplier', e.target.value)} style={{ ...inputStyle, width: 100 }} />
              </div>
            </div>
            {projection && (
              <>
                <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap' }}>
                  <Stat label="Projected annual parcels" value={num(projection.annualParcels, 0)}
                    sub={`${num(projection.normalParcels, 0)} normal + ${num(projection.peakParcels, 0)} peak`} />
                  <Stat label="Projected annual income" value={money(projection.annualGross)} tone="var(--hp-green)"
                    sub={`at ${money2(pricing.perParcel)}/parcel`} />
                  <Stat label="Implied hours / yr" value={num(projection.annualHours, 0)} sub="at his current pace" />
                  <Stat label="Award-equiv / yr" value={money(projection.awardEquivAnnual)}
                    sub={`${num(projection.annualHours, 0)} h × ${money(pricing.awardHourly)}`} />
                </div>
                <div style={{ marginTop: 14, display: 'flex', alignItems: 'center', gap: 8, fontSize: 13, color: 'var(--hp-dim)' }}>
                  <Info size={15} style={{ flexShrink: 0 }} />
                  <span>
                    Peak = {num(projection.peakDays, 0)} days at {num(projection.mult, 2)}× normal volume.
                    {projection.annualGross >= projection.awardEquivAnnual
                      ? ' At his current pace this price already clears the award-equivalent for the hours worked — he keeps the upside.'
                      : ' At his current pace this sits below the award-equivalent — he reaches it by lifting to the target P/H.'}
                  </span>
                </div>
              </>
            )}
          </Card>

          {/* 4 ── Daily detail */}
          <Card>
            <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 12, flexWrap: 'wrap' }}>
              <Title sub="Every captured day in the window. Grey rows = no shift or no parcels (already out of stats). Use Exclude to drop a specific day from every calculation.">4 · Captured shifts &amp; parcels</Title>
              {analysis.manualExcludedDays > 0 && (
                <button onClick={clearExcludes} title="Re-include every manually-excluded day"
                  style={{ ...stepBtn, background: 'var(--hp-surface)' }}><RefreshCw size={13} /> Clear {analysis.manualExcludedDays} excluded</button>
              )}
            </div>
            <div style={{ overflowX: 'auto' }}>
              <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13, minWidth: 760 }}>
                <thead>
                  <tr style={{ textAlign: 'left', color: 'var(--hp-dim)', fontSize: 11, textTransform: 'uppercase', letterSpacing: '0.04em' }}>
                    {['Date', 'Src', 'Start', 'Finish', 'Break', 'Hours', 'Parcels', 'Exp', 'P/H', 'KM'].map(h => (
                      <th key={h} style={{ padding: '8px 10px', borderBottom: '1px solid var(--hp-border)', textAlign: (h === 'Date' || h === 'Src') ? 'left' : 'right' }}>{h}</th>
                    ))}
                    <th style={{ padding: '8px 10px', borderBottom: '1px solid var(--hp-border)', textAlign: 'right' }}></th>
                  </tr>
                </thead>
                <tbody>
                  {analysis.rows.map(r => {
                    const good = r.productive && !r.excluded && r.pph >= analysis.medianPPH;
                    return (
                      <tr key={r.date} style={{ opacity: r.excluded ? 0.4 : (r.productive ? 1 : 0.5) }}>
                        <td style={{ padding: '7px 10px', borderBottom: '1px solid var(--hp-border)', fontWeight: 600, textDecoration: r.excluded ? 'line-through' : 'none' }}>{r.date}</td>
                        <td style={{ padding: '7px 10px', borderBottom: '1px solid var(--hp-border)' }}>
                          {r.source === 'scanner'
                            ? <span title="Filled from AP scanner times (pre-portal)" style={{ fontSize: 10, fontWeight: 800, textTransform: 'uppercase', letterSpacing: '0.04em', color: 'var(--hp-gold)', border: '1px solid var(--hp-gold)', borderRadius: 5, padding: '1px 5px' }}>Scan</span>
                            : r.source === 'portal'
                              ? <span title="Driver portal clock-on/off" style={{ fontSize: 10, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.04em', color: 'var(--hp-dim)' }}>Portal</span>
                              : <span style={{ color: 'var(--hp-dim)' }}>—</span>}
                        </td>
                        <td style={{ padding: '7px 10px', borderBottom: '1px solid var(--hp-border)', textAlign: 'right' }}>{r.shiftStart || '—'}</td>
                        <td style={{ padding: '7px 10px', borderBottom: '1px solid var(--hp-border)', textAlign: 'right' }}>{r.finishTime || '—'}</td>
                        <td style={{ padding: '7px 10px', borderBottom: '1px solid var(--hp-border)', textAlign: 'right' }}>{r.breakStart && r.breakEnd ? `${r.breakStart}–${r.breakEnd}` : '—'}</td>
                        <td style={{ padding: '7px 10px', borderBottom: '1px solid var(--hp-border)', textAlign: 'right' }}>{r.hours > 0 ? num(r.hours) : '—'}</td>
                        <td style={{ padding: '7px 10px', borderBottom: '1px solid var(--hp-border)', textAlign: 'right', fontWeight: 700 }}>{r.parcel || '—'}</td>
                        <td style={{ padding: '7px 10px', borderBottom: '1px solid var(--hp-border)', textAlign: 'right' }}>{r.express || '—'}</td>
                        <td style={{ padding: '7px 10px', borderBottom: '1px solid var(--hp-border)', textAlign: 'right', fontWeight: 800, color: r.productive ? (good ? 'var(--hp-green)' : 'var(--hp-text)') : 'var(--hp-dim)' }}>{r.productive ? num(r.pph) : '—'}</td>
                        <td style={{ padding: '7px 10px', borderBottom: '1px solid var(--hp-border)', textAlign: 'right' }}>{r.km > 0 ? num(r.km, 0) : '—'}</td>
                        <td style={{ padding: '7px 10px', borderBottom: '1px solid var(--hp-border)', textAlign: 'right' }}>
                          <button onClick={() => toggleExclude(r.date)}
                            title={r.excluded ? 'Re-include this day in the review' : 'Exclude this day from every calculation'}
                            style={{ ...stepBtn, padding: '3px 9px', fontSize: 11, color: r.excluded ? 'var(--hp-gold)' : 'var(--hp-dim)', borderColor: r.excluded ? 'var(--hp-gold)' : 'var(--hp-border)' }}>
                            {r.excluded ? 'Include' : 'Exclude'}
                          </button>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </Card>

          {/* 5 ── Offer Workbench */}
          {offerWb && (
          <Card>
            <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 12, flexWrap: 'wrap' }}>
              <Title sub="Contract value is affixed to parcels (rate = total ÷ parcels); EP earns a bonus on top. Save to finalise — the printed offer reflects exactly what's here.">5 · Offer Workbench</Title>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
                {(() => {
                  const s = savedOffers[driver];
                  const dirty = !s || JSON.stringify(offerSnapshot()) !== JSON.stringify((({ savedAt, sentAt, sentDocId, ...rest }) => rest)(s));
                  return (
                    <>
                      <span style={{ fontSize: 11, fontWeight: 700, color: dirty ? 'var(--hp-red)' : 'var(--hp-green)' }}>
                        {s ? (dirty ? '● Unsaved changes' : (s.sentAt ? `✓ Sent ${new Date(s.sentAt).toLocaleString('en-AU', { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' })}` : `✓ Saved ${new Date(s.savedAt).toLocaleString('en-AU', { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' })}`)) : '● Not saved'}
                      </span>
                      <button onClick={saveOffer} disabled={!dirty} title="Finalise & save this offer for the driver"
                        style={{ ...stepBtn, background: dirty ? 'var(--hp-gold)' : 'var(--hp-surface)', color: dirty ? 'var(--hp-black)' : 'var(--hp-dim)', border: dirty ? 'none' : '1px solid var(--hp-border)', fontWeight: 800, opacity: dirty ? 1 : 0.7, cursor: dirty ? 'pointer' : 'default' }}>
                        <Save size={14} /> Save offer
                      </button>
                      <button onClick={sendToPortal} disabled={dirty || sendingOffer} title={dirty ? 'Save the offer first' : `Send this offer to ${driverRec?.name || driver}'s portal`}
                        style={{ ...stepBtn, background: (!dirty && !sendingOffer) ? 'var(--hp-green)' : 'var(--hp-surface)', color: (!dirty && !sendingOffer) ? '#fff' : 'var(--hp-dim)', border: (!dirty && !sendingOffer) ? 'none' : '1px solid var(--hp-border)', fontWeight: 800, opacity: dirty ? 0.6 : 1, cursor: dirty ? 'default' : 'pointer' }}>
                        <Send size={14} /> {sendingOffer ? 'Sending…' : 'Send to portal'}
                      </button>
                    </>
                  );
                })()}
                <div style={{ display: 'flex', gap: 4, background: 'var(--hp-bg)', border: '1px solid var(--hp-border)', borderRadius: 8, padding: 3, height: 'fit-content' }}>
                  {[{ k: 'week', l: 'Per week' }, { k: 'period', l: 'Per 4 wks' }, { k: 'year', l: 'Per year' }].map(o => (
                    <button key={o.k} onClick={() => setOfferBasis(o.k)} title={`Project volumes over a ${o.l.toLowerCase()} basis`}
                      style={{ ...stepBtn, border: 'none', background: offerBasis === o.k ? 'var(--hp-gold)' : 'transparent', color: offerBasis === o.k ? 'var(--hp-black)' : 'var(--hp-dim)' }}>{o.l}</button>
                  ))}
                </div>
              </div>
            </div>

            {/* Sent-offer live status (opened / accepted / rejected) — driven by the
                server record, so it shows on any machine, not just the one that sent it. */}
            {offerStatus && (
              <div style={{ marginBottom: 14, padding: '10px 14px', borderRadius: 8, border: '1px solid var(--hp-border)', display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap',
                background: offerStatus && offerStatus.response === 'accepted' ? 'color-mix(in srgb, var(--hp-green) 12%, transparent)' : offerStatus && offerStatus.response === 'rejected' ? 'color-mix(in srgb, var(--hp-red) 10%, transparent)' : 'var(--hp-bg)' }}>
                <span style={{ fontSize: 12.5, fontWeight: 700, color: 'var(--hp-text)' }}>Offer status:</span>
                {(() => {
                  if (offerStatus && offerStatus.response === 'accepted') return <span style={{ fontSize: 12.5, fontWeight: 800, color: 'var(--hp-green)' }}>✓ Accepted{offerStatus.respondedAt ? ` · ${new Date(offerStatus.respondedAt).toLocaleString('en-AU', { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' })}` : ''} — ready to generate the contract</span>;
                  if (offerStatus && offerStatus.response === 'rejected') return <span style={{ fontSize: 12.5, fontWeight: 800, color: 'var(--hp-red)' }}>✕ Rejected{offerStatus.responseReason ? ` — "${offerStatus.responseReason}"` : ''}. Revise &amp; re-send.</span>;
                  if (offerStatus && offerStatus.status === 'opened') return <span style={{ fontSize: 12.5, color: 'var(--hp-dim)' }}>Opened{offerStatus.openedAt ? ` ${new Date(offerStatus.openedAt).toLocaleString('en-AU', { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' })}` : ''} · awaiting his response</span>;
                  return <span style={{ fontSize: 12.5, color: 'var(--hp-dim)' }}>Sent · not opened yet</span>;
                })()}
                <div style={{ marginLeft: 'auto', display: 'flex', gap: 8, alignItems: 'center' }}>
                  {offerStatus && offerStatus.response === 'accepted' && (
                    savedOffers[driver] && savedOffers[driver].offerTotal ? (
                      <button onClick={generateContract} title="Pre-fill the Subcontractor Agreement with this offer's rates (Contracts page)"
                        style={{ ...stepBtn, background: 'var(--hp-green)', color: '#fff', border: 'none', fontWeight: 800 }}><FileText size={13} /> Generate contract →</button>
                    ) : (
                      // No saved figures on this machine — the workbench would fall back to a
                      // fresh projection, which may not be the deal he accepted. Don't offer
                      // to build a contract off numbers we can't tie to the sent PDF.
                      <span style={{ fontSize: 12, color: 'var(--hp-dim)', maxWidth: 460, textAlign: 'right' }}>
                        This offer's figures aren't saved on this device — open his offer PDF in My Docs and re-enter the total to match, then Save, before generating the contract.
                      </span>
                    )
                  )}
                  <button onClick={refreshOfferStatus} title="Refresh status" style={{ ...stepBtn, padding: '5px 8px' }}><RefreshCw size={12} /> Refresh</button>
                </div>
              </div>
            )}

            {/* Projected volumes & hours (editable) */}
            <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap', marginBottom: 16 }}>
              <div style={{ flex: '1 1 150px', minWidth: 150, background: 'var(--hp-bg)', border: '1px solid var(--hp-border)', borderRadius: 10, padding: '12px 14px' }}>
                <Label>Projected parcels</Label>
                <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                  <input type="number" min="0" value={offerWb.nParcel}
                    onChange={e => setOfferParcelsOv(Math.max(0, parseInt(e.target.value, 10) || 0))}
                    style={{ ...inputStyle, width: '100%', fontWeight: 800 }} />
                  {!offerWb.usingProjParcels && <button onClick={() => setOfferParcelsOv(null)} title="Reset to projected" style={{ ...stepBtn, padding: '6px 8px' }}><RefreshCw size={12} /></button>}
                </div>
                <div style={{ fontSize: 11, color: 'var(--hp-dim)', marginTop: 4 }}>{offerWb.usingProjParcels ? `projected · ${num(analysis.avgParcelOnlyPerDay, 0)}/day × ${offerWb.days}d` : `edited · projected ${num(offerWb.projParcels, 0)}`}</div>
              </div>
              <div style={{ flex: '1 1 150px', minWidth: 150, background: 'var(--hp-bg)', border: '1px solid var(--hp-border)', borderRadius: 10, padding: '12px 14px' }}>
                <Label>Projected EP</Label>
                <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                  <input type="number" min="0" value={offerWb.nEp}
                    onChange={e => setOfferEpOv(Math.max(0, parseInt(e.target.value, 10) || 0))}
                    style={{ ...inputStyle, width: '100%', fontWeight: 800 }} />
                  {!offerWb.usingProjEp && <button onClick={() => setOfferEpOv(null)} title="Reset to projected" style={{ ...stepBtn, padding: '6px 8px' }}><RefreshCw size={12} /></button>}
                </div>
                <div style={{ fontSize: 11, color: 'var(--hp-dim)', marginTop: 4 }}>{offerWb.usingProjEp ? `projected · ${num(analysis.avgExpressPerDay, 1)}/day × ${offerWb.days}d` : `edited · projected ${num(offerWb.projEp, 0)}`}</div>
              </div>
              <div style={{ flex: '1 1 150px', minWidth: 150, background: 'var(--hp-bg)', border: '1px solid var(--hp-border)', borderRadius: 10, padding: '12px 14px' }}>
                <Label>Projected hours</Label>
                <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                  <input type="number" min="0" step="1" value={Math.round(offerWb.hours)}
                    onChange={e => setOfferHoursOv(Math.max(0, parseFloat(e.target.value) || 0))}
                    style={{ ...inputStyle, width: '100%', fontWeight: 800 }} />
                  {!offerWb.usingProjHours && <button onClick={() => setOfferHoursOv(null)} title="Reset to projected" style={{ ...stepBtn, padding: '6px 8px' }}><RefreshCw size={12} /></button>}
                </div>
                <div style={{ fontSize: 11, color: 'var(--hp-dim)', marginTop: 4 }}>{offerWb.usingProjHours ? `projected · ${num(analysis.avgHoursPerDay)}h/day × ${offerWb.days}d` : `edited · projected ${num(offerWb.projHours, 0)}h`}</div>
              </div>
            </div>

            {/* Total contract value */}
            <div style={{ marginBottom: 14 }}>
              <Label>Total contract value</Label>
              <div style={{ display: 'flex', alignItems: 'center', gap: 6, flexWrap: 'wrap' }}>
                <input type="number" min="0" step="10" value={offerTotal} placeholder="0.00"
                  onChange={e => setOfferTotal(e.target.value)}
                  style={{ ...inputStyle, width: 160, fontSize: 18, fontWeight: 900, color: 'var(--hp-gold)' }} />
                <div style={{ display: 'flex', gap: 3, background: 'var(--hp-bg)', border: '1px solid var(--hp-border)', borderRadius: 8, padding: 3 }}>
                  {[{ k: true, l: 'GST inc' }, { k: false, l: 'Ex GST' }].map(o => (
                    <button key={o.l} onClick={() => setOfferTotalInc(o.k)}
                      style={{ ...stepBtn, padding: '6px 9px', border: 'none', background: offerTotalInc === o.k ? 'var(--hp-gold)' : 'transparent', color: offerTotalInc === o.k ? 'var(--hp-black)' : 'var(--hp-dim)' }}>{o.l}</button>
                  ))}
                </div>
                {projection && projection.annualGross > 0 && (
                  <button onClick={() => { setOfferTotal(String(Math.round(projection.annualGross))); setOfferTotalInc(false); }}
                    title="Fill with his projected annual earnings from section 3 (ex GST)"
                    style={{ ...stepBtn }}><RefreshCw size={12} /> Annual earnings (ex GST)</button>
                )}
              </div>
            </div>

            {/* Parcel rate is solved from the total; EP is a bonus rate you set. */}
            <div style={{ marginBottom: 16 }}>
              <Label>Rates — parcel rate is solved from the total; EP is a bonus, paid on top</Label>
              <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap' }}>
                {/* Parcel — solved from the total */}
                <div style={{ flex: '1 1 240px', minWidth: 220, border: '2px solid var(--hp-gold)', borderRadius: 10, padding: '12px 14px', background: 'color-mix(in srgb, var(--hp-gold) 10%, var(--hp-bg))' }}>
                  <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 8, marginBottom: 8 }}>
                    <span style={{ fontSize: 12, fontWeight: 800, color: 'var(--hp-text)' }}>Parcel rate</span>
                    <span style={{ fontSize: 10, fontWeight: 800, textTransform: 'uppercase', letterSpacing: '0.04em', color: 'var(--hp-gold)', border: '1px solid var(--hp-gold)', borderRadius: 5, padding: '1px 6px' }}>Solved ←</span>
                  </div>
                  <div style={{ fontSize: '1.6rem', fontWeight: 900, color: 'var(--hp-text)', lineHeight: 1.1 }}>{offerWb.ok ? money2(offerWb.solvedParcel) : '—'} <span style={{ fontSize: 13, fontWeight: 700, color: 'var(--hp-dim)' }}>ex GST</span></div>
                  <div style={{ fontSize: 11, color: 'var(--hp-dim)', marginTop: 3 }}><b style={{ color: 'var(--hp-text)' }}>{offerWb.ok ? money2(offerWb.solvedParcel * 1.1) : '—'}</b> inc · total ÷ parcels</div>
                </div>
                {/* EP — bonus rate you set */}
                <div style={{ flex: '1 1 240px', minWidth: 220, border: '2px solid var(--hp-border)', borderRadius: 10, padding: '12px 14px', background: 'var(--hp-bg)' }}>
                  <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 8, marginBottom: 8 }}>
                    <span style={{ fontSize: 12, fontWeight: 800, color: 'var(--hp-text)' }}>EP fee (GST inc)</span>
                    <span style={{ fontSize: 10, fontWeight: 800, textTransform: 'uppercase', letterSpacing: '0.04em', color: 'var(--hp-green)', border: '1px solid var(--hp-green)', borderRadius: 5, padding: '1px 6px' }}>Bonus</span>
                  </div>
                  <input type="number" min="0" step="0.05" value={offerEpRate} placeholder="0.00"
                    onChange={e => setOfferEpRate(e.target.value)}
                    style={{ ...inputStyle, width: '100%', fontSize: 18, fontWeight: 800 }} />
                  <div style={{ fontSize: 11, color: 'var(--hp-dim)', marginTop: 4 }}>GST inc{offerEpRate ? ` · ${money2((parseFloat(offerEpRate) || 0) / 1.1)} ex` : ''} · paid on top</div>
                </div>
              </div>
              {/* Other per-item fees, also paid on top of the parcel contract */}
              <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap', marginTop: 10 }}>
                {[{ label: 'Depot bag fee', val: offerDepotRate, set: setOfferDepotRate }, { label: 'DoE fee', val: offerDoeRate, set: setOfferDoeRate }].map(f => (
                  <div key={f.label} style={{ flex: '1 1 200px', minWidth: 180, border: '1px solid var(--hp-border)', borderRadius: 10, padding: '10px 12px', background: 'var(--hp-bg)' }}>
                    <Label>{f.label} — ex GST, on top</Label>
                    <input type="number" min="0" step="0.05" value={f.val} placeholder="0.00"
                      onChange={e => f.set(e.target.value)}
                      style={{ ...inputStyle, width: '100%', fontWeight: 800 }} />
                    <div style={{ fontSize: 11, color: 'var(--hp-dim)', marginTop: 4 }}>{f.val ? `${money2((parseFloat(f.val) || 0) * 1.1)} inc` : 'ex GST'} · paid on top</div>
                  </div>
                ))}
              </div>
            </div>

            {offerWb.msg && (
              <div style={{ marginBottom: 16, padding: '10px 14px', background: 'color-mix(in srgb, var(--hp-red) 10%, transparent)', border: '1px solid var(--hp-red)', borderRadius: 8, fontSize: 12.5, color: 'var(--hp-red)', fontWeight: 600 }}>{offerWb.msg}</div>
            )}

            {/* Totals — parcel contract is the deal; EP bonus is a separate standalone figure */}
            <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap', marginBottom: 16 }}>
              <Stat label="Parcel contract — inc GST" value={money(offerWb.totalInc)} tone="var(--hp-gold)" sub={`${money(offerWb.totalEx)} ex · the contract`} />
              <Stat label="Parcel $/hr" value={offerWb.ok ? `${money(offerWb.hourly)}/h` : '—'} tone={offerWb.ok ? (offerWb.hourly >= pricing.awardHourly ? 'var(--hp-green)' : 'var(--hp-red)') : 'var(--hp-dim)'} sub={`parcels only · vs ${money(pricing.awardHourly)} award`} />
              <Stat label="EP bonus (separate)" value={offerWb.ok ? `+${money(offerWb.epBonusInc)}` : '—'} tone="var(--hp-green)" sub={offerWb.ok ? `${num(offerWb.nEp, 0)} EP × ${money2(offerWb.solvedEp)} inc · not in any $/hr` : `${num(offerWb.nEp, 0)} EP × ${money2(offerWb.solvedEp)}`} />
            </div>

            {/* Line breakdown — contract (parcels) then bonus (EP) */}
            <div style={{ overflowX: 'auto' }}>
              <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13, minWidth: 560 }}>
                <thead>
                  <tr style={{ textAlign: 'right', color: 'var(--hp-dim)', fontSize: 11, textTransform: 'uppercase', letterSpacing: '0.04em' }}>
                    {['Line', 'Volume', 'Rate ex', 'Rate inc', 'Subtotal ex', 'Subtotal inc'].map((h, i) => (
                      <th key={h} style={{ padding: '8px 10px', borderBottom: '1px solid var(--hp-border)', textAlign: i === 0 ? 'left' : 'right' }}>{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {[
                    { name: 'Parcels', tag: 'contract', tagColor: 'var(--hp-gold)', vol: offerWb.nParcel, rate: offerWb.solvedParcel, sub: offerWb.parcelSub },
                    { name: 'EP',      tag: 'bonus',    tagColor: 'var(--hp-green)', vol: offerWb.nEp,     rate: offerWb.solvedEp,     sub: offerWb.epSub },
                  ].map(l => (
                    <tr key={l.name}>
                      <td style={{ padding: '8px 10px', borderBottom: '1px solid var(--hp-border)', fontWeight: 700 }}>
                        {l.name}<span style={{ marginLeft: 6, fontSize: 10, fontWeight: 800, textTransform: 'uppercase', color: l.tagColor, border: `1px solid ${l.tagColor}`, borderRadius: 5, padding: '1px 5px' }}>{l.tag}</span>
                      </td>
                      <td style={{ padding: '8px 10px', borderBottom: '1px solid var(--hp-border)', textAlign: 'right' }}>{num(l.vol, 0)}</td>
                      <td style={{ padding: '8px 10px', borderBottom: '1px solid var(--hp-border)', textAlign: 'right' }}>{offerWb.ok ? money2(l.rate) : '—'}</td>
                      <td style={{ padding: '8px 10px', borderBottom: '1px solid var(--hp-border)', textAlign: 'right' }}>{offerWb.ok ? money2(l.rate * 1.1) : '—'}</td>
                      <td style={{ padding: '8px 10px', borderBottom: '1px solid var(--hp-border)', textAlign: 'right', fontWeight: 700 }}>{offerWb.ok ? money(l.sub) : '—'}</td>
                      <td style={{ padding: '8px 10px', borderBottom: '1px solid var(--hp-border)', textAlign: 'right', fontWeight: 700 }}>{offerWb.ok ? money(l.sub * 1.1) : '—'}</td>
                    </tr>
                  ))}
                  <tr>
                    <td style={{ padding: '10px', borderTop: '2px solid var(--hp-border)', fontWeight: 800 }}>Parcel contract</td>
                    <td style={{ padding: '10px', borderTop: '2px solid var(--hp-border)', textAlign: 'right', fontWeight: 800 }}>{num(offerWb.nParcel, 0)}</td>
                    <td colSpan={2} style={{ borderTop: '2px solid var(--hp-border)' }}></td>
                    <td style={{ padding: '10px', borderTop: '2px solid var(--hp-border)', textAlign: 'right', fontWeight: 900, color: 'var(--hp-gold)' }}>{offerWb.ok ? money(offerWb.totalEx) : '—'}</td>
                    <td style={{ padding: '10px', borderTop: '2px solid var(--hp-border)', textAlign: 'right', fontWeight: 900, color: 'var(--hp-gold)' }}>{offerWb.ok ? money(offerWb.totalInc) : '—'}</td>
                  </tr>
                </tbody>
              </table>
            </div>

            <div style={{ marginTop: 14, fontSize: 12.5, color: 'var(--hp-dim)', lineHeight: 1.6 }}>
              {offerWb.ok
                ? <>The <b style={{ color: 'var(--hp-text)' }}>{money(offerWb.totalInc)} GST inc</b> contract is paid across <b style={{ color: 'var(--hp-text)' }}>{num(offerWb.nParcel, 0)}</b> parcels = <b style={{ color: 'var(--hp-gold)' }}>{money2(offerWb.solvedParcel)}</b>/parcel ex ({money2(offerWb.solvedParcel * 1.1)} inc), an implied <b style={{ color: offerWb.hourly >= pricing.awardHourly ? 'var(--hp-green)' : 'var(--hp-red)' }}>{money(offerWb.hourly)}/hr</b> — the entire above-award story rests on parcels alone. Separately, <b style={{ color: 'var(--hp-green)' }}>{num(offerWb.nEp, 0)} EP × {money2(offerWb.solvedEp)}</b> earns a <b style={{ color: 'var(--hp-green)' }}>+{money(offerWb.epBonusInc)} inc</b> bonus, paid on top and never counted in the $/hr above.</>
                : 'Enter the total contract value (affixed to parcels) — the parcel rate solves automatically; EP is a bonus on top.'}
            </div>

            {/* EP bonus potential — the upside if he chases more Express Post */}
            {offerWb.ok && (
              <div style={{ marginTop: 18, padding: '14px 16px', background: 'color-mix(in srgb, var(--hp-green) 8%, var(--hp-bg))', border: '1px solid var(--hp-border)', borderRadius: 10 }}>
                <div style={{ display: 'flex', alignItems: 'flex-end', justifyContent: 'space-between', gap: 12, flexWrap: 'wrap', marginBottom: 12 }}>
                  <div>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 14, fontWeight: 800, color: 'var(--hp-text)' }}><TrendingUp size={16} /> EP bonus potential</div>
                    <div style={{ fontSize: 12, color: 'var(--hp-dim)', marginTop: 2 }}>He averages {num(offerWb.curEpPerWeek, 1)} EP/week now. Model the upside if he chases more — every EP is {money2(offerWb.solvedEp * 1.1)} inc on top.</div>
                  </div>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                    <span style={{ fontSize: 12, color: 'var(--hp-dim)' }}>Target</span>
                    <button onClick={() => setOfferEpTarget(Math.max(0, +(offerWb.epTargetPerWeek - 1).toFixed(0)))} style={stepBtn}>−</button>
                    <input type="number" min="0" value={offerWb.epTargetPerWeek}
                      onChange={e => setOfferEpTarget(Math.max(0, parseInt(e.target.value, 10) || 0))}
                      style={{ ...inputStyle, width: 70, textAlign: 'center', fontWeight: 800 }} />
                    <span style={{ fontSize: 12, color: 'var(--hp-dim)' }}>EP/week</span>
                    <button onClick={() => setOfferEpTarget(+(offerWb.epTargetPerWeek + 1).toFixed(0))} style={stepBtn}>+</button>
                    {!offerWb.usingSuggestedTarget && <button onClick={() => setOfferEpTarget(null)} title="Reset to suggested" style={{ ...stepBtn, padding: '6px 8px' }}><RefreshCw size={12} /></button>}
                  </div>
                </div>
                <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap' }}>
                  <Stat label={`Potential EP / ${offerBasis === 'year' ? 'yr' : offerBasis === 'period' ? '4wk' : 'wk'}`} value={num(offerWb.epTargetBasis, 0)} sub={`${num(offerWb.epTargetPerWeek, 0)}/week × ${num(offerWb.days / 5, 0)} wks`} />
                  <Stat label="Potential EP bonus" value={`+${money(offerWb.epTargetBonusInc)}`} tone="var(--hp-green)" sub={`${money(offerWb.epTargetBonusEx)} ex · was +${money(offerWb.epBonusInc)}`} />
                  <Stat label="Extra vs current EP" value={`+${money(offerWb.epTargetBonusInc - offerWb.epBonusInc)}`} tone="var(--hp-green)" sub="pure bonus — never in his $/hr" />
                </div>
                <div style={{ marginTop: 12, display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap', fontSize: 12.5, color: 'var(--hp-dim)' }}>
                  <span style={{ fontWeight: 700, color: 'var(--hp-text)' }}>Bonus #2 — overflow assist:</span>
                  <input type="number" min="0" value={offerOverflowPerDay} onChange={e => setOfferOverflowPerDay(e.target.value)}
                    style={{ ...inputStyle, width: 70, textAlign: 'center', fontWeight: 800 }} />
                  <span>extra parcels in a day = <b style={{ color: 'var(--hp-green)' }}>+{money((parseFloat(offerOverflowPerDay) || 0) * offerWb.solvedParcel * 1.1)}</b> that day (per-day only — not every day).</span>
                </div>
              </div>
            )}

            {/* Above the award — the headline for the driver, hourly + annual */}
            {aboveAward && (
              <div style={{ marginTop: 20, paddingTop: 18, borderTop: '1px dashed var(--hp-border)' }}>
                <div style={{ display: 'flex', alignItems: 'flex-end', justifyContent: 'space-between', gap: 12, flexWrap: 'wrap', marginBottom: 12 }}>
                  <div>
                    <div style={{ fontSize: 14, fontWeight: 800, color: 'var(--hp-text)' }}>Above the minimum award</div>
                    <div style={{ fontSize: 12, color: 'var(--hp-dim)', marginTop: 2 }}>What {driverRec?.name || driver} sits above the Determination floor at this offer — the number to present to him.</div>
                  </div>
                  <button onClick={printOffer} title={`Print a one-page Contract Offer for ${driverRec?.name || driver}`}
                    style={{ ...stepBtn, background: 'var(--hp-gold)', color: 'var(--hp-black)', border: 'none', fontWeight: 800 }}><FileText size={14} /> Print offer for {driverRec?.name || driver}</button>
                </div>

                {/* What the DOCUMENT will say — stated where you press Print, not 400px up
                    the page next to the toggle. Losing payable time silently costs the offer
                    its depot allowance, its "How your day is paid" section and two of its
                    three pace tables, and nothing in the document announces the absence. */}
                {payable && (ptOn && payable.ok ? (
                  <div style={{ marginBottom: 12, padding: '9px 14px', borderRadius: 8, fontSize: 12.5, lineHeight: 1.6,
                    background: 'color-mix(in srgb, var(--hp-gold) 10%, transparent)', border: '1px solid var(--hp-gold)', color: 'var(--hp-text)' }}>
                    <b>Prints on payable time</b> — depot at {num(payable.indoorPace, 0)}/hr, road at {num(payable.outdoorPace)}/hr{payable.loadPct > 0 ? `, +${num(payable.loadPct)}% route loading` : ''}. The offer carries a <b>How your day is paid</b> section and <b>three</b> pace tables (road · depot · whole day).
                  </div>
                ) : (
                  <div style={{ marginBottom: 12, padding: '9px 14px', borderRadius: 8, fontSize: 12.5, lineHeight: 1.6, display: 'flex', gap: 10, alignItems: 'center', flexWrap: 'wrap',
                    background: 'color-mix(in srgb, var(--hp-red) 8%, transparent)', border: '1px solid var(--hp-red)', color: 'var(--hp-text)' }}>
                    <AlertTriangle size={16} style={{ flexShrink: 0, color: 'var(--hp-red)' }} />
                    <span style={{ flex: '1 1 320px' }}><b>Payable time is off</b> — this offer will print <b>one</b> whole-day pace table, with no depot allowance and no “How your day is paid” section.</span>
                    <button onClick={() => setPtOn(true)}
                      style={{ ...stepBtn, background: 'var(--hp-gold)', color: 'var(--hp-black)', border: 'none', fontWeight: 800 }}><Zap size={13} /> Switch it on</button>
                  </div>
                ))}

                <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap' }}>
                  <Stat label="His $/hr" value={`${money(aboveAward.hr)}/h`} tone="var(--hp-green)" sub="at this offer & his pace" />
                  <Stat label="Award floor" value={`${money(aboveAward.awardHr)}/h`} sub="Determination minimum" />
                  <Stat label="Above award / hr" value={`+${money(aboveAward.aboveHr)}/h`} tone={aboveAward.aboveHr >= 0 ? 'var(--hp-green)' : 'var(--hp-red)'} sub={`${aboveAward.aboveHr >= 0 ? '+' : '−'}${num(Math.abs(aboveAward.abovePct))}% vs floor`} />
                  <Stat label="Above award / yr" value={`+${money(aboveAward.aboveAnnual)}`} tone={aboveAward.aboveAnnual >= 0 ? 'var(--hp-green)' : 'var(--hp-red)'} sub={`over ${num(aboveAward.annHours, 0)}h/yr`} />
                  <Stat label="Annual value (inc GST)" value={money(aboveAward.annValueEx * 1.1)} tone="var(--hp-gold)" sub={`${money(aboveAward.annValueEx)} ex`} />
                </div>
                <div style={{ marginTop: 12, padding: '10px 14px', background: 'color-mix(in srgb, var(--hp-green) 8%, transparent)', border: '1px solid var(--hp-border)', borderRadius: 8, fontSize: 12.5, color: 'var(--hp-dim)', lineHeight: 1.6 }}>
                  This <b style={{ color: 'var(--hp-text)' }}>{money(aboveAward.hr)}/hr</b> is the parcel contract on its own — <b style={{ color: 'var(--hp-text)' }}>{num(aboveAward.abovePct)}% clear of the {money(aboveAward.awardHr)}/hr floor</b>. Express Post earns a <b style={{ color: 'var(--hp-green)' }}>separate bonus of +{money(offerWb.epBonusInc)} inc</b> on top (never counted in this $/hr).
                </div>
              </div>
            )}

            {/* Current vs proposed — his current deal (hourly or per-parcel) against this offer */}
            {aboveAward && (
              <div style={{ marginTop: 20, paddingTop: 18, borderTop: '1px dashed var(--hp-border)' }}>
                <div style={{ display: 'flex', alignItems: 'flex-end', justifyContent: 'space-between', gap: 12, flexWrap: 'wrap', marginBottom: 10 }}>
                  <div>
                    <div style={{ fontSize: 14, fontWeight: 800, color: 'var(--hp-text)' }}>Current vs proposed</div>
                    <div style={{ fontSize: 12, color: 'var(--hp-dim)', marginTop: 2 }}>His current deal vs this offer. For a driver being converted from hourly, use <b style={{ color: 'var(--hp-text)' }}>Hourly</b> — the RCTI per-parcel rate is a proxy.</div>
                  </div>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
                    {cmpBasis === 'hourly' && (
                      <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                        <span style={{ fontSize: 12, color: 'var(--hp-dim)' }}>Current</span>
                        <input type="number" min="0" step="0.5" value={cmpHourly} onChange={e => setCmpHourly(e.target.value)}
                          style={{ ...inputStyle, width: 90, fontWeight: 800 }} />
                        <span style={{ fontSize: 12, color: 'var(--hp-dim)' }}>/hr</span>
                        <div style={{ display: 'flex', gap: 3, background: 'var(--hp-bg)', border: '1px solid var(--hp-border)', borderRadius: 8, padding: 3 }}>
                          {[{ k: true, l: 'inc' }, { k: false, l: 'ex' }].map(o => (
                            <button key={o.l} onClick={() => setCmpHourlyInc(o.k)}
                              style={{ ...stepBtn, padding: '5px 8px', border: 'none', background: cmpHourlyInc === o.k ? 'var(--hp-gold)' : 'transparent', color: cmpHourlyInc === o.k ? 'var(--hp-black)' : 'var(--hp-dim)' }}>{o.l}</button>
                          ))}
                        </div>
                      </div>
                    )}
                    <div style={{ display: 'flex', gap: 4, background: 'var(--hp-bg)', border: '1px solid var(--hp-border)', borderRadius: 8, padding: 3 }}>
                      {[{ k: 'hourly', l: 'Hourly' }, { k: 'parcel', l: 'Per-parcel' }].map(o => (
                        <button key={o.k} onClick={() => setCmpBasis(o.k)} title={`Baseline his current deal as ${o.l.toLowerCase()}`}
                          style={{ ...stepBtn, border: 'none', background: cmpBasis === o.k ? 'var(--hp-gold)' : 'transparent', color: cmpBasis === o.k ? 'var(--hp-black)' : 'var(--hp-dim)' }}>{o.l}</button>
                      ))}
                    </div>
                  </div>
                </div>
                {currentCompare && currentCompare.has ? (() => {
                  const cc = currentCompare;
                  const rows = [
                    // Per-parcel row only makes sense when the current deal IS per-parcel;
                    // for an hourly baseline the honest comparison is $/hr + annual.
                    ...(cc.basis === 'parcel' ? [{ label: 'Parcel rate (ex GST)', cur: cc.rate, prop: offerWb.solvedParcel, fmt: (v) => money2(v) }] : []),
                    { label: 'Effective $/hr',        cur: cc.hourly, prop: aboveAward.hr,        fmt: (v) => `${money(v)}/h` },
                    { label: 'Annual value (ex GST)', cur: cc.annual, prop: aboveAward.annValueEx, fmt: (v) => money(v) },
                  ];
                  return (
                    <div style={{ overflowX: 'auto' }}>
                      <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13, minWidth: 480 }}>
                        <thead>
                          <tr style={{ textAlign: 'right', color: 'var(--hp-dim)', fontSize: 11, textTransform: 'uppercase', letterSpacing: '0.04em' }}>
                            {['', `Current (${cc.label})`, 'Proposed', 'Change'].map((h, i) => <th key={i} style={{ padding: '8px 10px', borderBottom: '1px solid var(--hp-border)', textAlign: i === 0 ? 'left' : 'right' }}>{h}</th>)}
                          </tr>
                        </thead>
                        <tbody>
                          {rows.map(r => {
                            const delta = offerWb.ok ? r.prop - r.cur : null;
                            const good = delta != null && delta >= 0;
                            return (
                              <tr key={r.label}>
                                <td style={{ padding: '8px 10px', borderBottom: '1px solid var(--hp-border)', fontWeight: 600 }}>{r.label}</td>
                                <td style={{ padding: '8px 10px', borderBottom: '1px solid var(--hp-border)', textAlign: 'right' }}>{r.fmt(r.cur)}</td>
                                <td style={{ padding: '8px 10px', borderBottom: '1px solid var(--hp-border)', textAlign: 'right', fontWeight: 800, color: offerWb.ok ? 'var(--hp-gold)' : 'var(--hp-dim)' }}>{offerWb.ok ? r.fmt(r.prop) : '—'}</td>
                                <td style={{ padding: '8px 10px', borderBottom: '1px solid var(--hp-border)', textAlign: 'right', fontWeight: 700, color: delta == null ? 'var(--hp-dim)' : good ? 'var(--hp-green)' : 'var(--hp-red)' }}>{delta == null ? '—' : `${delta >= 0 ? '+' : '−'}${r.fmt(Math.abs(delta)).replace('+', '')}`}</td>
                              </tr>
                            );
                          })}
                        </tbody>
                      </table>
                      {cc.basis === 'hourly' && <div style={{ marginTop: 6, fontSize: 11, color: 'var(--hp-dim)' }}>For reference, {cc.label} ≈ <b style={{ color: 'var(--hp-text)' }}>{money2(cc.rate)}/parcel</b> equivalent at his {num(analysis.medianPPH)} P/H pace — close to the RCTI proxy rate.</div>}
                    </div>
                  );
                })() : (
                  <div style={{ fontSize: 13, color: 'var(--hp-dim)' }}>
                    {cmpBasis === 'hourly'
                      ? 'Enter his current hourly rate above to compare.'
                      : <>No live per-parcel rate on file for {driver} — set it on the <b style={{ color: 'var(--hp-text)' }}>RCTI &amp; Xero</b> rate card, or switch to <b style={{ color: 'var(--hp-text)' }}>Hourly</b>.</>}
                  </div>
                )}
              </div>
            )}
          </Card>
          )}
        </>
      )}

      {showReport && reportData && (
        <DriverEarningsReport data={reportData} onClose={() => setShowReport(false)} />
      )}

      <style>{`.spin{animation:hpspin 1s linear infinite}@keyframes hpspin{to{transform:rotate(360deg)}}`}</style>
    </div>
  );
}
